import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import DOMPurify from 'dompurify';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, functions } from '../../utils/firebase';
import { httpsCallable } from 'firebase/functions';
import * as FS from '../../utils/firestore';
import RichText from '../RichText/RichText';
import { LogoSVG } from '../Icons';
import jsPDF from 'jspdf';
import { ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './PanelApp.css';

/* ══════════════════════════════════════════════════════════════════════════
   HELPERS & CONSTANTS
   ══════════════════════════════════════════════════════════════════════════ */
const FAMILIA_MATERIAS = ['Alimento','Cuidado personal','Cumplimiento','RdR','Proteccional','VIF','Salida País','Otro'];
const LABORAL_MOTIVOS = [
  'Me despidieron injustificadamente',
  'Me deben cotizaciones o sueldos',
  'Quiero autodespedirme',
  'Sufrí acoso laboral o vulneración de derechos',
  'Sufrí accidente laboral o enfermedad profesional',
  'Tengo problemas con finiquito',
];
const LABORAL_FLUJO = [
  'Admisión','Revisión de documentos','Comparendo','Redacción','Revisión',
  'Ingreso OJV','Preparación minuta','Audiencia Preparatoria',
  'Preparación Audiencia de Juicio','Audiencia de juicio','Pago',
];
const FAMILIA_FLUJO = [
  'Admisión','Documentos pendientes / en revisión','Redacción','Revisión',
  'Ingreso OJV / ratificar','Preparación minuta','Audiencia preparatoria',
  'Audiencia de juicio','Resuelto',
];
const CIVIL_FLUJO = [
  'Admisión','Documentos pendientes / en revisión','Redacción','Revisión',
  'Otro','Resuelto',
];
const INGRESO_WEB_ESTADOS = ['Contacto','Seguimiento','Viable','No viable','Rechazado por cliente'];
const REPORTES_PERIODOS = [
  {label:'Últ. 3 meses',months:3},
  {label:'Últ. 6 meses',months:6},
  {label:'Último año',months:12},
];
const MESES_NOMBRES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const TIPOS_AUDIENCIA = ['Audiencia','Comparendo','A.P.','C.A.P','A.J.','C.A.J.','Otro'];

const formatNotifTime = (ts: any): string => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} día${days > 1 ? 's' : ''}`;
};

const renderMenciones = (text: string) => {
  const parts = text.split(/(@\S+)/g);
  return parts.map((part, i) =>
    part.startsWith('@')
      ? <span key={i} className="mention-highlight">{part}</span>
      : <Fragment key={i}>{part}</Fragment>
  );
};

const extractPlazo = (txt: string): string | null => {
  const m = txt.match(/@plazo\s+([\d\-\/\.]+(?:\s+\d{1,2}:\d{2})?)/i);
  if (!m) return null;
  const raw = m[1].trim();
  const nums = raw.replace(/[^\d]/g, '');
  if (nums.length < 6) return null;
  const dd = nums.slice(0, 2).padStart(2, '0');
  const mm = nums.slice(2, 4).padStart(2, '0');
  const yyyy = nums.length >= 8 ? nums.slice(4, 8) : `20${nums.slice(4, 6)}`;
  const d = parseInt(dd), mo = parseInt(mm);
  if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
  const timeM = raw.match(/(\d{1,2}):(\d{2})\s*$/);
  if (timeM) return `${dd}-${mm}-${yyyy} ${timeM[1].padStart(2,'0')}:${timeM[2]}`;
  return `${dd}-${mm}-${yyyy}`;
};

const parsePlazoDate = (plazo: string): Date | null => {
  if (!plazo) return null;
  const m = plazo.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (!m) return null;
  return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]), parseInt(m[4] || '0'), parseInt(m[5] || '0'));
};

const formatRut = (raw: string): string => {
  let clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length < 2) return clean;
  const dv = clean.slice(-1);
  let body = clean.slice(0, -1);
  let formatted = '';
  while (body.length > 3) {
    formatted = '.' + body.slice(-3) + formatted;
    body = body.slice(0, -3);
  }
  formatted = body + formatted;
  return formatted + '-' + dv;
};

const validatePhone = (digits: string): boolean => {
  const clean = digits.replace(/\D/g, '');
  return clean.length === 8;
};

/* ══════════════════════════════════════════════════════════════════════════
   NOTAS INTERNAS (reusable)
   ══════════════════════════════════════════════════════════════════════════ */
const NotasInternas: React.FC<{casoId:string;field:string;value:string;edita:boolean;onRefresh:()=>void}> = ({casoId,field,value,edita,onRefresh}) => {
  const [val,setVal]=useState(value||'');
  useEffect(()=>setVal(value||''),[value]);
  const save=async()=>{await FS.updateCampo(casoId,field,val);onRefresh();};
  return(
    <div className="p-section" style={{marginTop:20,borderTop:'1px solid rgba(255,255,255,0.06)',paddingTop:16}}>
      <div className="p-section-title">📝 Notas internas</div>
      <textarea className="p-input" rows={3} value={val} onChange={e=>setVal(e.target.value)} onBlur={save} readOnly={!edita} placeholder="Notas internas del equipo para esta sección..." style={{resize:'vertical',fontSize:13}}/>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CASO ROW
   ══════════════════════════════════════════════════════════════════════════ */
const CasoRow = React.memo(({ caso: c, rol, onOpen, onDelete }: {
  caso: any;
  rol: string;
  onOpen: (id: string) => void;
  onDelete: (data: { id: string; nombre: string }) => void;
}) => (
  <div className="caso-row" onClick={() => onOpen(c.id)}>
    <div className="caso-row-left">
      <div className="caso-nombre">{c.nombreCliente || '—'}</div>
      <div className="caso-sub">{c.rut || ''} · {c.tema || '—'} · {c.abogada || 'Sin abogada asignada'}{c.rolCaso ? ` · ${c.rolCaso}` : ''}</div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span className="caso-badge" style={{ background: (FS.statusColors[c.estado] || '#555') + '22', color: FS.statusColors[c.estado] || '#888', border: `1px solid ${FS.statusColors[c.estado] || '#555'}44` }}>{c.estado || '—'}</span>
      <span className="caso-fecha">{FS.fmtDate(c.createdAt)}</span>
      {rol === 'admin' && <button className="p-btn-danger-sm" onClick={e => { e.stopPropagation(); onDelete({ id: c.id, nombre: c.nombreCliente || 'este caso' }); }} title="Eliminar caso">🗑</button>}
    </div>
  </div>
));

const ANUNCIO_CALENDAR_FECHA_LIMITE = '2026-07-10'; // viernes; el popup deja de mostrarse después de esta fecha

/* ══════════════════════════════════════════════════════════════════════════
   BANNER CUMPLEAÑOS VALE — solo se muestra el 20 de julio de 2026.
   TODO: eliminar este bloque (componente + estilos .cumple-*) después del 20-07-2026.
   ══════════════════════════════════════════════════════════════════════════ */
const CUMPLE_VALE_FECHA = '2026-07-20';
const CUMPLE_COLORES = ['#7B3FF2','#FF3B30','#FF8C1A','#FFCC00','#34C759','#2196F3','#12B5A5'];
const CumpleFlags: React.FC<{ texto: string }> = ({ texto }) => (
  <div className="cumple-garland">
    <span className="cumple-string" />
    {texto.split('').map((ch, i) => (
      <span
        key={i}
        className={`cumple-flag${ch === ' ' ? ' cumple-flag-gap' : ''}`}
        style={ch === ' ' ? undefined : { background: CUMPLE_COLORES[i % CUMPLE_COLORES.length] }}
      >
        {ch === ' ' ? '' : ch}
      </span>
    ))}
  </div>
);
const CumpleBanner: React.FC = () => {
  const hoy = new Date().toISOString().slice(0, 10);
  if (hoy !== CUMPLE_VALE_FECHA) return null;
  return (
    <div className="cumple-banner" role="img" aria-label="Feliz Cumpleaños Vale">
      <CumpleFlags texto="FELIZ CUMPLEAÑOS" />
      <div className="cumple-titulo">
        <span className="cumple-emoji">🎂</span>
        <span className="cumple-emoji">🎈</span>
        <h2>¡Feliz Cumpleaños, Vale!</h2>
        <span className="cumple-emoji">🎉</span>
        <span className="cumple-emoji">🎁</span>
      </div>
      <div className="cumple-sub">🥳 De todo el equipo de Souza &amp; Villa 🥂</div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   MAIN PANEL APP
   ══════════════════════════════════════════════════════════════════════════ */
const PanelApp: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [view, setView] = useState('casos');
  const [casos, setCasos] = useState<any[]>([]);
  const [currentCaso, setCurrentCaso] = useState<any>(null);
  const [tab, setTab] = useState('datos');
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{id:string;nombre:string}|null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notifs, setNotifs] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showAnuncioCalendar, setShowAnuncioCalendar] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u); const ud = await FS.getUserData(u.uid); setUserData(ud);
        try {
          const hoy = new Date().toISOString().slice(0,10);
          if (hoy <= ANUNCIO_CALENDAR_FECHA_LIMITE && localStorage.getItem('anuncioCalendar_v2_visto_' + u.uid) !== hoy) {
            setShowAnuncioCalendar(true);
          }
        } catch {}
      }
      else { setUser(null); setUserData(null); }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const refreshCasos = useCallback(async () => {
    if (!user || !userData) return;
    setCasos(await FS.loadCasos(userData.rol, user.uid));
  }, [user, userData]);

  useEffect(() => { if (user && userData) refreshCasos(); }, [user, userData, refreshCasos]);

  useEffect(() => {
    if (!user) return;
    const unsub = FS.subscribeNotificaciones(user.uid, setNotifs);
    try {
      const CLEAN_KEY = 'lastNotifClean_' + user.uid;
      const last = Number(localStorage.getItem(CLEAN_KEY) || 0);
      if (Date.now() - last > 3_600_000) {
        FS.limpiarNotificacionesViejas(user.uid).catch(() => {});
        localStorage.setItem(CLEAN_KEY, String(Date.now()));
      }
    } catch {}
    return () => unsub();
  }, [user]);

  // Resetea toda la navegación cuando cambia el usuario que está autenticado
  useEffect(() => {
    setView('casos');
    setCurrentCaso(null);
    setSearch('');
    setFiltroEstado('');
    setNotifOpen(false);
  }, [user?.uid]);

  const handleLogin = async () => {
    setLoginError('');
    try { await signInWithEmailAndPassword(auth, loginEmail, loginPass); }
    catch { setLoginError('Credenciales incorrectas. Intente nuevamente.'); }
  };
  const handleLogout = async () => { await signOut(auth); };

  const openCaso = useCallback(async (id: string) => { const c = await FS.getCaso(id); setCurrentCaso(c); setTab('datos'); setView('detalle'); }, []);
  const refreshCaso = async () => { if (!currentCaso) return; setCurrentCaso(await FS.getCaso(currentCaso.id)); };

  const filtered = useMemo(() => casos.filter(c =>
    (!search || (c.nombreCliente||'').toLowerCase().includes(search.toLowerCase()) || (c.rut||'').includes(search)) &&
    (!filtroEstado || c.estado === filtroEstado)
  ), [casos, search, filtroEstado]);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const edita = userData && ['admin','abogada','abogada1','procuradora'].includes(userData.rol);
  const rol = userData?.rol;

  if (loading) return <div className="panel-loading">Cargando...</div>;

  if (user && userData && userData.activo === false) return (
    <div className="panel-overlay" style={{display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,textAlign:'center',padding:32}}>
      <div style={{fontSize:'2.5rem'}}>🔒</div>
      <div style={{fontFamily:'Cinzel,serif',fontSize:'1.1rem',color:'var(--gold)',letterSpacing:'0.05em'}}>Cuenta desvinculada</div>
      <div style={{color:'var(--text-muted)',fontSize:'0.88rem',maxWidth:340}}>Tu cuenta ha sido desvinculada del estudio Souza &amp; Villa. Contacta a la administración si crees que esto es un error.</div>
      <button className="p-btn-secondary" onClick={()=>signOut(auth)} style={{marginTop:8}}>Cerrar sesión</button>
    </div>
  );

  if (!user) return (
    <div className="panel-overlay">
      <div className="login-box">
        <LogoSVG size={34} />
        <h2>Acceso interno</h2>
        <p>Souza &amp; Villa Abogadas — Panel de gestión</p>
        <div style={{width:'100%'}}>
          <div className="p-field"><div className="p-field-label">Correo electrónico</div>
            <input className="p-input" type="email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} placeholder="usuario@souzavilla.cl" onKeyPress={e=>e.key==='Enter'&&handleLogin()} /></div>
          <div className="p-field"><div className="p-field-label">Contraseña</div>
            <input className="p-input" type="password" value={loginPass} onChange={e=>setLoginPass(e.target.value)} placeholder="••••••••" onKeyPress={e=>e.key==='Enter'&&handleLogin()} /></div>
          <button className="p-btn-primary" onClick={handleLogin} style={{width:'100%',padding:'0.85rem'}}>Ingresar →</button>
          {loginError && <div className="login-error">{loginError}</div>}
        </div>
      </div>
    </div>
  );

  const navItems = [
    { id:'casos', icon:'⬛', label:'Casos', roles:['admin','abogada','abogada1','captadora','procuradora'] },
    { id:'nuevo', icon:'＋', label:'Nuevo caso', roles:['admin','abogada','abogada1','captadora','procuradora'] },
    { id:'notificaciones', icon:'🔔', label:'Notificaciones', roles:['admin','abogada','abogada1','captadora','procuradora'] },
    { id:'usuarios', icon:'👤', label:'Usuarios', roles:['admin','abogada'] },
    { id:'reportes', icon:'📊', label:'Reportes', roles:['admin','abogada'] },
    { id:'ingresoweb', icon:'🌐', label:'Ingreso Web', roles:['admin','abogada'] },
    { id:'flujocaja', icon:'💵', label:'Flujo de Caja', roles:['admin','abogada'] },
    { id:'analisis',  icon:'📈', label:'Análisis',     roles:['admin','abogada'] },
    { id:'captadoras',  icon:'🏆', label:'Captadoras',   roles:['admin','abogada'] },
    { id:'asistencia',  icon:'🕒', label:'Asistencia',   roles:['admin','abogada'] },
    { id:'calendario',  icon:'🗓️', label:'Calendario',   roles:['admin','abogada','procuradora'] },
  ].filter(i => i.roles.includes(rol));

  const allowedNavIds = navItems.map(i => i.id);
  const renderContent = () => {
    if (view === 'detalle' && currentCaso) return <CasoDetalle caso={currentCaso} edita={edita} rol={rol} tab={tab} setTab={setTab} onBack={()=>{setView('casos');refreshCasos();}} onRefresh={refreshCaso} currentUserId={user?.uid||''} currentUserName={userData?.nombre||user?.email||''} />;
    if (view === 'nuevo'         && allowedNavIds.includes('nuevo'))         return <NuevoCaso rol={rol} user={user} userData={userData} onBack={()=>{setView('casos');refreshCasos();}} />;
    if (view === 'notificaciones'&& allowedNavIds.includes('notificaciones')) return <NotificacionesView notifs={notifs} uid={user.uid} rol={rol} onAbrirCaso={async(casoId)=>{ const c=await FS.getCaso(casoId); setCurrentCaso(c); setTab('proceso'); setView('detalle'); }} />;
    if (view === 'usuarios'      && allowedNavIds.includes('usuarios'))      return <Usuarios rol={rol} />;
    if (view === 'reportes'      && allowedNavIds.includes('reportes'))      return <Reportes />;
    if (view === 'ingresoweb'    && allowedNavIds.includes('ingresoweb'))    return <IngresoWeb />;
    if (view === 'flujocaja'     && allowedNavIds.includes('flujocaja'))     return <FlujoCaja soloLectura={rol!=='admin'} />;
    if (view === 'analisis'      && allowedNavIds.includes('analisis'))      return <Analisis />;
    if (view === 'captadoras'    && allowedNavIds.includes('captadoras'))    return <Captadoras />;
    if (view === 'asistencia'    && allowedNavIds.includes('asistencia'))    return <Asistencia rol={rol} userId={user?.uid||''} userNombre={userData?.nombre||user?.email||''} />;
    if (view === 'calendario'    && allowedNavIds.includes('calendario'))    return <CalendarioView rol={rol} nombre={userData?.nombre||user?.email||''} uid={user?.uid||''} />;

    const hasDraft=(()=>{try{return !!localStorage.getItem('nuevoCaso_draft_'+user.uid);}catch{return false;}})();
    return (
      <div>
        <div className="casos-toolbar">
          <input className="p-input" placeholder="🔍  Buscar por nombre o RUT..." value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1,minWidth:180}} />
          <select className="p-select" value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)} style={{width:180}}>
            <option value="">Todos los estados</option>
            {Object.keys(FS.statusColors).map(s=><option key={s}>{s}</option>)}
          </select>
          {rol!=='captadora'&&hasDraft&&<button onClick={()=>setView('nuevo')} style={{padding:'8px 14px',background:'rgba(212,175,55,0.15)',border:'1px solid rgba(212,175,55,0.5)',color:'var(--gold)',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,whiteSpace:'nowrap'}}>📋 Recuperar borrador</button>}
          {rol!=='captadora'&&<button className="p-btn-primary" onClick={()=>setView('nuevo')} style={{padding:'8px 14px'}}>＋ Nuevo caso</button>}
        </div>
        {filtered.length===0 ? <div className="p-empty">No hay casos registrados aún.</div> :
          filtered.map(c=>(
            <CasoRow key={c.id} caso={c} rol={rol} onOpen={openCaso} onDelete={setConfirmDelete} />
          ))
        }
        {confirmDelete&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>!deleting&&setConfirmDelete(null)}>
            <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'28px 32px',maxWidth:420,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:32,marginBottom:10}}>🗑️</div>
              <div style={{fontSize:15,color:'var(--cream,#e8e0d0)',marginBottom:8,fontWeight:500}}>Eliminar caso</div>
              <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:20,lineHeight:1.6}}>¿Está seguro que desea eliminar el caso de <strong style={{color:'var(--cream,#e8e0d0)'}}>{confirmDelete.nombre}</strong>? Esta acción no se puede deshacer.</div>
              <div style={{display:'flex',gap:10,justifyContent:'center'}}>
                <button className="p-btn-secondary" onClick={()=>setConfirmDelete(null)} disabled={deleting}>Cancelar</button>
                <button className="p-btn-danger-sm" style={{padding:'8px 18px',fontSize:13,opacity:deleting?0.6:1,cursor:deleting?'not-allowed':'pointer'}} disabled={deleting} onClick={async()=>{
                  setDeleting(true);
                  await FS.deleteCaso(confirmDelete.id);
                  setConfirmDelete(null);setDeleting(false);
                  refreshCasos();
                }}>{deleting?'Eliminando…':'Eliminar'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="panel-fullscreen">
      {showAnuncioCalendar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>{setShowAnuncioCalendar(false);try{localStorage.setItem('anuncioCalendar_v2_visto_'+user.uid,new Date().toISOString().slice(0,10));}catch{}}}>
          <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(212,175,55,0.35)',borderRadius:10,padding:'28px 32px',maxWidth:440,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:32,marginBottom:10}}>📅</div>
            <div style={{fontSize:15,color:'var(--cream,#e8e0d0)',marginBottom:8,fontWeight:600}}>Nueva función: audiencias en Google Calendar</div>
            <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:20,lineHeight:1.6,textAlign:'justify'}}>
              Ahora deben agregar siempre los comparendos, audiencias u otras fechas clave en el caso del cliente respectivo, en la pestaña <strong style={{color:'var(--cream,#e8e0d0)'}}>Proceso</strong>, sección <strong style={{color:'var(--cream,#e8e0d0)'}}>Audiencias y fechas clave</strong>. Al agregarlas ahí, el evento se crea automáticamente en Google Calendar.
              <br/><br/>
              Además, se agregó una nueva sección <strong style={{color:'var(--cream,#e8e0d0)'}}>🗓️ Calendario</strong> en el menú lateral del panel para poder visualizar el calendario directamente ahí.
            </div>
            <button className="p-btn-primary" onClick={()=>{setShowAnuncioCalendar(false);try{localStorage.setItem('anuncioCalendar_v2_visto_'+user.uid,new Date().toISOString().slice(0,10));}catch{}}}>Entendido</button>
          </div>
        </div>
      )}
      <div className="panel-topbar">
        <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
          <button className="panel-hamburger" onClick={()=>setSidebarOpen(o=>!o)} aria-label="Menú">☰</button>
          <LogoSVG size={28} />
          <div className="panel-topbar-title">Souza &amp; Villa — Panel de Gestión</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
          <button className="notif-bell-btn" onClick={()=>setNotifOpen(o=>!o)} title="Notificaciones">
            🔔
            {notifs.filter(n=>!n.leida).length > 0 && (
              <span className="notif-badge">{notifs.filter(n=>!n.leida).length > 99 ? '99+' : notifs.filter(n=>!n.leida).length}</span>
            )}
          </button>
          <div className="panel-topbar-user" style={{textAlign:'right'}}>
            <strong>{userData?.nombre || user.email}</strong>
            <span>{userData?.rol || ''}</span>
          </div>
          <button className="p-btn-secondary" onClick={handleLogout} style={{padding:'5px 12px',fontSize:'0.68rem', color:'#3B82F6'}}>Cerrar sesión</button>
          <button className="panel-close-x" onClick={()=>window.close()} title="Cerrar (Esc)">✕</button>
        </div>
      </div>
      {notifOpen && (
        <>
          <div className="notif-overlay" onClick={()=>setNotifOpen(false)} />
          <div className="notif-dropdown">
            <div className="notif-dropdown-header">
              <span>Notificaciones</span>
              {notifs.some(n=>!n.leida) && (
                <button className="notif-mark-all" onClick={async()=>{ await FS.marcarTodasLeidas(user.uid); }}>Marcar todas como leídas</button>
              )}
            </div>
            <div className="notif-list">
              {notifs.length === 0 ? (
                <div className="notif-empty">Sin notificaciones nuevas</div>
              ) : notifs.map(n => (
                <div key={n.id} className={`notif-item${n.leida?'':' unread'}${n.realizado?' notif-item-done':''}`} onClick={async()=>{
                  await FS.marcarNotificacionLeida(n.id);
                  setNotifOpen(false);
                  const caso=await FS.getCaso(n.casoId); setCurrentCaso(caso); setTab('proceso'); setView('detalle');
                }}>
                  <div className="notif-item-from">@ {n.deNombre}{n.realizado?' ✓':''}</div>
                  <div className="notif-item-msg">"{n.mensaje}"</div>
                  <div className="notif-item-caso">{n.casoNombre} · {n.codigoCaso}</div>
                  <div className="notif-item-time">{formatNotifTime(n.createdAt)}</div>
                </div>
              ))}
            </div>
            <button
              onClick={()=>{setNotifOpen(false);setView('notificaciones');setSidebarOpen(false);}}
              style={{width:'100%',padding:'10px',background:'rgba(100,181,246,0.06)',border:'none',borderTop:'1px solid rgba(255,255,255,0.07)',color:'#64B5F6',fontSize:'0.75rem',cursor:'pointer',letterSpacing:'0.05em'}}
            >Ver todas las notificaciones →</button>
          </div>
        </>
      )}
      <div className="panel-body">
        {sidebarOpen && <div className="panel-nav-overlay" onClick={()=>setSidebarOpen(false)} />}
        <div className={`panel-nav${sidebarOpen?' open':''}`}>
          {navItems.map(i=>(
            <button key={i.id} className={`pnav-btn ${view===i.id||(view==='detalle'&&i.id==='casos')?'active':''}`} onClick={()=>{setView(i.id);setCurrentCaso(null);if(i.id==='casos')refreshCasos();setSidebarOpen(false);}}>
              <span style={{fontSize:14}}>{i.icon}</span>{i.label}
            </button>
          ))}
          {(rol==='admin'||rol==='abogada'||rol==='procuradora')&&(
            <button
              className="pnav-btn"
              style={{marginTop:'auto',borderTop:'1px solid rgba(212,175,55,0.15)',paddingTop:12,color:'var(--gold)',opacity:0.85}}
              onClick={()=>window.open(window.location.origin+'/asistente','_blank')}
            >
              <span style={{fontSize:14}}>✍️</span>Redactor IA
            </button>
          )}
        </div>
        <div className="panel-content"><CumpleBanner />{renderContent()}</div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CASO DETALLE
   ══════════════════════════════════════════════════════════════════════════ */
const CasoDetalle: React.FC<{caso:any;edita:boolean;rol:string;tab:string;setTab:(t:string)=>void;onBack:()=>void;onRefresh:()=>void;currentUserId:string;currentUserName:string}> = ({caso:c,edita,rol,tab,setTab,onBack,onRefresh,currentUserId,currentUserName}) => {
  const [abogadas,setAbogadas]=useState<any[]>([]);
  const [captadoras,setCaptadoras]=useState<any[]>([]);
  const [todosUsuarios,setTodosUsuarios]=useState<any[]>([]);
  useEffect(()=>{
    FS.loadAbogadas().then(setAbogadas).catch(()=>{});
    FS.loadCaptadoras().then(setCaptadoras).catch(()=>{});
    FS.loadUsuarios().then(setTodosUsuarios).catch(err=>console.error('[loadUsuarios]',err));
  },[]);

  const allUsers = useMemo(()=>{
    const seenIds = new Set<string>();
    const byName = new Map<string, any>();
    for (const u of todosUsuarios) {
      if (!u.id || seenIds.has(u.id)) continue;
      seenIds.add(u.id);
      const name = (u.nombre || '').trim().toLowerCase();
      if (!name) continue;
      const existing = byName.get(name);
      if (!existing) { byName.set(name, u); continue; }
      // Duplicado: preferir el UID actual, luego contacto@svabogadas.com
      if (u.id === currentUserId) byName.set(name, u);
      else if (u.email === 'contacto@svabogadas.com' && existing.id !== currentUserId) byName.set(name, u);
    }
    return Array.from(byName.values());
  },[todosUsuarios, currentUserId]);

  const [textModal,setTextModal]=useState<{open:boolean;field:string;label:string;value:string}>({open:false,field:'',label:'',value:''});
  const openTextModal=(field:string,label:string,value:string)=>setTextModal({open:true,field,label,value});
  const saveTextModal=async()=>{await handleCampo(textModal.field,textModal.value);setTextModal(m=>({...m,open:false}));onRefresh();};

  const handleCampo=async(field:string,value:any)=>{await FS.updateCampo(c.id,field,value);};

  const [evalVal,setEvalVal]=useState(c.evaluacion||c.resumen||'');
  const [notaVal,setNotaVal]=useState(c.notaInterna||'');
  const [evalInternaVal,setEvalInternaVal]=useState(c.evaluacionInterna||'');
  const [transformandoDetalle,setTransformandoDetalle]=useState(false);
  const [enviandoBienvenida,setEnviandoBienvenida]=useState(false);
  const enviarCorreoBienvenida=async()=>{
    if(!c.correo||enviandoBienvenida) return;
    setEnviandoBienvenida(true);
    try{
      await httpsCallable(functions,'bienvenidaCliente')({correo:c.correo,nombre:c.nombreCliente,codigoCaso:c.codigoCaso||c.id.slice(0,8).toUpperCase(),tema:c.tema});
      await FS.updateCampo(c.id,'bienvenidaEnviada',new Date().toISOString().slice(0,10));
      onRefresh();
    }catch(e){console.error('Error enviando correo:',e);}
    finally{setEnviandoBienvenida(false);}
  };
  const [enviandoNotifEstado,setEnviandoNotifEstado]=useState(false);
  const [notifEstadoEnviada,setNotifEstadoEnviada]=useState(false);
  const notificarEstado=async()=>{
    if(!c.correo||enviandoNotifEstado) return;
    setEnviandoNotifEstado(true);
    try{
      await httpsCallable(functions,'notificarCliente')({correo:c.correo,nombre:c.nombreCliente,codigoCaso:c.codigoCaso||c.id.slice(0,8).toUpperCase(),tipo:'estado',datos:{estado:c.estado||''}});
      setNotifEstadoEnviada(true);
      setTimeout(()=>setNotifEstadoEnviada(false),4000);
    }catch(e){console.error('Error notificando estado:',e);}
    finally{setEnviandoNotifEstado(false);}
  };
  useEffect(()=>{setEvalVal(c.evaluacion||c.resumen||'');},[c.evaluacion,c.resumen]);
  useEffect(()=>{setNotaVal(c.notaInterna||'');},[c.notaInterna]);
  useEffect(()=>{setEvalInternaVal(c.evaluacionInterna||'');},[c.evaluacionInterna]);

  const transformarConIADetalle=async()=>{
    const raw=evalInternaVal.replace(/<[^>]*>/g,'').trim();
    if(!raw) return;
    setTransformandoDetalle(true);
    try{
      const fn=httpsCallable(functions,'transformarEvaluacion');
      const res:any=await fn({texto:raw});
      const resultado=DOMPurify.sanitize((res.data.resultado||'').replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>'));
      setEvalVal(resultado);
      await handleCampo('evaluacion',resultado);
    }catch(e:any){console.error('Error IA:',e);}
    finally{setTransformandoDetalle(false);}
  };
  const handleEstado=async(estado:string)=>{
    await FS.updateEstado(c.id, estado, c.estado, !!c.fechaAceptado);
    onRefresh();
  };

  const [audF,setAudF]=useState('');const[audH,setAudH]=useState('');const[audL,setAudL]=useState('');const[audT,setAudT]=useState('');
  const [audTOtro,setAudTOtro]=useState('');
  const [savingAud,setSavingAud]=useState(false);
  const [syncingAud,setSyncingAud]=useState<number|null>(null);
  const addAud=async()=>{
    if(savingAud)return;
    setSavingAud(true);
    const tipoFinal=audT==='Otro'?audTOtro.trim():audT;
    const nuevaAud:any={tipo:tipoFinal,fecha:audF,hora:audH,lugar:audL};
    let calendarOk=false;
    try{
      const res:any=await httpsCallable(functions,'crearEventoAudienciaCalendar')({casoId:c.id,audiencia:nuevaAud});
      if(res?.data?.eventId){nuevaAud.gcalEventId=res.data.eventId;calendarOk=true;}
    }catch(e){console.error('Error creando evento en Google Calendar:',e);}
    await FS.addAudiencia(c.id,nuevaAud);
    setAudF('');setAudH('');setAudL('');setAudT('');setAudTOtro('');onRefresh();
    setSavingAud(false);
    if(!calendarOk) alert('La audiencia se guardó, pero no se pudo crear el evento en Google Calendar. Aparecerá marcada como "no sincronizada": usa el botón 🔄 para reintentar.');
  };
  // Reintenta crear el evento en Google Calendar para una audiencia que quedó sin gcalEventId
  const syncAud=async(i:number)=>{
    if(syncingAud!==null)return;
    const aud=(c.audiencias||[])[i];
    if(!aud||aud.gcalEventId)return;
    setSyncingAud(i);
    try{
      const res:any=await httpsCallable(functions,'crearEventoAudienciaCalendar')({casoId:c.id,audiencia:{tipo:aud.tipo,fecha:aud.fecha,hora:aud.hora,lugar:aud.lugar}});
      if(res?.data?.eventId){await FS.updateAudiencia(c.id,i,{gcalEventId:res.data.eventId});onRefresh();}
      else alert('No se pudo crear el evento en Google Calendar. Inténtalo de nuevo.');
    }catch(e){console.error('Error sincronizando audiencia con Google Calendar:',e);alert('No se pudo crear el evento en Google Calendar. Inténtalo de nuevo.');}
    finally{setSyncingAud(null);}
  };
  const delAud=async(i:number)=>{
    const aud=(c.audiencias||[])[i];
    if(aud?.gcalEventId){
      try{await httpsCallable(functions,'eliminarEventoAudienciaCalendar')({eventId:aud.gcalEventId});}
      catch(e){console.error('Error eliminando evento en Google Calendar:',e);}
    }
    await FS.removeAudiencia(c.id,i);onRefresh();
  };

  const [confirmDel,setConfirmDel]=useState<{msg:string;onConfirm:()=>void}|null>(null);

  const [checkTxt,setCheckTxt]=useState('');
  const [mentionSearch,setMentionSearch]=useState<string|null>(null);
  const [savingCheck,setSavingCheck]=useState(false);
  const checkInputRef=useRef<HTMLInputElement>(null);

  const mentionLabel=(u:any):string=>u.nombre||(u.email?u.email.split('@')[0]:'');

  const PLAZO_ITEM={id:'__plazo__',nombre:'plazo',rol:'📅 fecha límite',isSpecial:true};
  const TODOS_ITEM={id:'__todos__',nombre:'todos',rol:'📢 notificar a todo el equipo',isSpecial:true};
  const ROLES_EQUIPO=['admin','abogada','abogada1','procuradora'];

  const filteredMentions=useMemo(()=>{
    if(mentionSearch===null)return[];
    const q=mentionSearch.toLowerCase();
    const items:any[]=[];
    if('plazo'.startsWith(q)||q==='') items.push(PLAZO_ITEM);
    if('todos'.startsWith(q)||q==='') items.push(TODOS_ITEM);
    const users=allUsers
      .filter(u=>{
        const lbl=mentionLabel(u).toLowerCase();
        return lbl.includes(q)||(u.email||'').toLowerCase().includes(q);
      })
      .slice(0,5);
    return [...items,...users];
  },[mentionSearch,allUsers]);

  const handleCheckTxtChange=(e:React.ChangeEvent<HTMLInputElement>)=>{
    // Auto-formato: si hay 5+ dígitos seguidos después de @plazo, inserta guiones
    let val=e.target.value.replace(/@plazo\s+([\d]{5,})/gi,(_,nums)=>{
      const d=nums.slice(0,2);
      const mo=nums.length>=4?'-'+nums.slice(2,4):'';
      const y=nums.length>=5?'-'+nums.slice(4,8):'';
      return `@plazo ${d}${mo}${y}`;
    });
    setCheckTxt(val);
    const cursor=e.target.selectionStart??val.length;
    const before=val.slice(0,cursor);
    const m=before.match(/@(\S*)$/);
    setMentionSearch(m?m[1]:null);
  };

  const insertMention=(u:any)=>{
    const cursor=checkInputRef.current?.selectionStart??checkTxt.length;
    const before=checkTxt.slice(0,cursor);
    const after=checkTxt.slice(cursor);
    const m=before.match(/@(\S*)$/);
    if(m){
      const lbl=u.isSpecial?u.nombre:mentionLabel(u).split(/\s+/)[0];
      const newBefore=before.slice(0,before.length-m[0].length)+`@${lbl} `;
      setCheckTxt(newBefore+after);
    }
    setMentionSearch(null);
    setTimeout(()=>checkInputRef.current?.focus(),0);
  };

  const notifyMentions=async(txt:string)=>{
    const plazo=extractPlazo(txt)||undefined;
    const tokens=new Set((txt.match(/@(\S+)/g)||[]).map(t=>t.slice(1).toLowerCase()));
    if(tokens.size===0)return;
    const notifData=(uid:string)=>({
      para:uid,de:currentUserId,deNombre:currentUserName,
      casoId:c.id,casoNombre:c.nombreCliente||'—',
      codigoCaso:c.codigoCaso||c.id.slice(0,8).toUpperCase(),
      mensaje:txt,plazo,
    });
    const mentionedUids=new Set<string>();
    if(tokens.has('todos')){
      // Difusión a todo el equipo: recorremos la lista COMPLETA de usuarios
      // (todosUsuarios), no la deduplicada por nombre (allUsers), porque esa
      // deduplicación puede dejar fuera o desviar a la procuradora / abogadas
      // cuando existen cuentas duplicadas (p. ej. contacto@svabogadas.com).
      // Se notifica a cada rol del equipo, omitiendo al emisor y a las cuentas
      // inactivas, evitando duplicados por UID.
      for(const u of todosUsuarios){
        if(!u.id||u.id===currentUserId||u.activo===false||mentionedUids.has(u.id))continue;
        if(!ROLES_EQUIPO.includes(u.rol))continue;
        mentionedUids.add(u.id);
        await FS.crearNotificacion(notifData(u.id));
      }
    }
    for(const u of allUsers){
      const lbl=mentionLabel(u).split(/\s+/)[0];
      if(!lbl||!tokens.has(lbl.toLowerCase())||mentionedUids.has(u.id))continue;
      mentionedUids.add(u.id);
      await FS.crearNotificacion(notifData(u.id));
    }
    if(plazo&&mentionedUids.size===0){
      await FS.crearNotificacion(notifData(currentUserId));
    }
  };

  const [checkSinPlazo,setCheckSinPlazo]=useState<string|null>(null);

  const addCheck=async(force=false)=>{
    const txt=checkTxt.trim();
    if(!txt||savingCheck)return;
    const hasMention=/@\S+/.test(txt);
    const plazo=extractPlazo(txt);
    const hasPlazo=!!plazo;
    if(hasMention&&!hasPlazo&&!force){setCheckSinPlazo(txt);return;}
    setSavingCheck(true);
    setCheckTxt('');setMentionSearch(null);
    try{
      let gtasksIds:any=undefined;
      try{
        const res:any=await httpsCallable(functions,'crearTaskChecklist')({casoId:c.id,texto:txt,plazo:plazo||undefined});
        if(res?.data?.ids) gtasksIds=res.data.ids;
      }catch(e){console.error('Error creando tarea en Google Tasks:',e);}
      await FS.addCheckItem(c.id,txt,gtasksIds);
      await notifyMentions(txt);
    }catch(e:any){
      console.error('[addCheck] Error:', e?.code, e?.message);
    }finally{
      setSavingCheck(false);
      onRefresh();
    }
  };
  const toggleCheck=async(i:number,done:boolean)=>{
    await FS.toggleCheckItem(c.id,i,done);
    const item=c.checklist?.[i];
    if(item?.texto) await FS.actualizarRealizadaPorTexto(c.id,item.texto,done,currentUserId);
    if(item?.gtasksIds){
      try{await httpsCallable(functions,'actualizarTaskChecklist')({ids:item.gtasksIds,done});}
      catch(e){console.error('Error actualizando tarea en Google Tasks:',e);}
    }
    onRefresh();
  };
  const delCheck=async(i:number)=>{
    const item=c.checklist?.[i];
    if(item?.gtasksIds){
      try{await httpsCallable(functions,'eliminarTaskChecklist')({ids:item.gtasksIds});}
      catch(e){console.error('Error eliminando tarea en Google Tasks:',e);}
    }
    await FS.removeCheckItem(c.id,i);onRefresh();
  };
  const [editCheckIdx,setEditCheckIdx]=useState<number|null>(null);
  const [editCheckTxt,setEditCheckTxt]=useState('');
  const [editCheckOriginal,setEditCheckOriginal]=useState('');
  const startEditCheck=(i:number,texto:string)=>{setEditCheckIdx(i);setEditCheckTxt(texto);setEditCheckOriginal(texto);};
  const saveEditCheck=async()=>{
    if(editCheckIdx===null)return;
    const txt=editCheckTxt.trim();
    if(txt&&txt!==editCheckOriginal){
      await FS.updateCheckItem(c.id,editCheckIdx,txt);
      await FS.actualizarNotificacionesMensaje(c.id,editCheckOriginal,txt,extractPlazo(txt)||undefined);
    }
    setEditCheckIdx(null);setEditCheckTxt('');setEditCheckOriginal('');onRefresh();
  };

  const [subCheckInputs,setSubCheckInputs]=useState<{[k:string]:string}>({});
  const updateSubChecklist=async(step:string,flowType:string,sub:any[])=>{
    const currentFlow=flowFor(flowType);
    const updated={...currentFlow,[step]:{...(currentFlow[step]||{}),subChecklist:sub}};
    await FS.updateCampo(c.id,flowFieldFor(flowType),updated);onRefresh();
  };
  const addSubCheck=async(step:string,flowType:string,txt:string)=>{
    const currentFlow=flowFor(flowType);
    const sub=[...((currentFlow[step]||{}).subChecklist||[]),{texto:txt,done:false}];
    await updateSubChecklist(step,flowType,sub);
  };
  const toggleSubCheck=async(step:string,flowType:string,i:number,done:boolean)=>{
    const currentFlow=flowFor(flowType);
    const sub=[...((currentFlow[step]||{}).subChecklist||[])];
    sub[i]={...sub[i],done};
    await updateSubChecklist(step,flowType,sub);
  };
  const delSubCheck=async(step:string,flowType:string,i:number)=>{
    const currentFlow=flowFor(flowType);
    const sub=((currentFlow[step]||{}).subChecklist||[]).filter((_:any,idx:number)=>idx!==i);
    await updateSubChecklist(step,flowType,sub);
  };

  const laborFlow = c.laborFlow || {};
  const familiaFlow = c.familiaFlow || {};
  const civilFlow = c.civilFlow || {};
  const laborSteps: string[] = c.laborFlowSteps || LABORAL_FLUJO;
  const familiaSteps: string[] = c.familiaFlowSteps || FAMILIA_FLUJO;
  const civilSteps: string[] = c.civilFlowSteps || CIVIL_FLUJO;
  const flowFieldFor=(ft:string)=>ft==='familia'?'familiaFlow':ft==='civil'?'civilFlow':'laborFlow';
  const stepsFieldFor=(ft:string)=>ft==='familia'?'familiaFlowSteps':ft==='civil'?'civilFlowSteps':'laborFlowSteps';
  const flowFor=(ft:string):{[k:string]:any}=>ft==='familia'?familiaFlow:ft==='civil'?civilFlow:laborFlow;
  const stepsFor=(ft:string):string[]=>ft==='familia'?familiaSteps:ft==='civil'?civilSteps:laborSteps;

  const toggleFlowStep=async(step:string,done:boolean,flowType:string='labor')=>{
    const currentFlow=flowFor(flowType);
    const updated={...currentFlow,[step]:{done,instrucciones:currentFlow[step]?.instrucciones||''}};
    await FS.updateCampo(c.id,flowFieldFor(flowType),updated);
    // La etapa "Resuelto" (Familia/Civil) se sincroniza con el estado del caso.
    if((flowType==='familia'||flowType==='civil')&&step==='Resuelto'){
      const nuevoEstado=done?'Resuelto / Cerrado':'En tramitación';
      if((c.estado||'')!==nuevoEstado)await FS.updateEstado(c.id,nuevoEstado,c.estado,!!c.fechaAceptado);
    }
    onRefresh();
  };
  const updateFlowInstrucciones=async(step:string,txt:string,flowType:string='labor')=>{
    const currentFlow=flowFor(flowType);
    const updated={...currentFlow,[step]:{done:currentFlow[step]?.done||false,instrucciones:txt}};
    await FS.updateCampo(c.id,flowFieldFor(flowType),updated);
  };

  const [editStepIdx,setEditStepIdx]=useState<{flowType:string;idx:number}|null>(null);
  const [editStepTxt,setEditStepTxt]=useState('');
  const startEditStep=(flowType:string,idx:number,name:string)=>{setEditStepIdx({flowType,idx});setEditStepTxt(name);};
  const saveEditStep=async()=>{
    if(!editStepIdx)return;
    const{flowType,idx}=editStepIdx;
    const newName=editStepTxt.trim();
    if(!newName){setEditStepIdx(null);return;}
    const stepsField=stepsFieldFor(flowType);
    const flowField=flowFieldFor(flowType);
    const currentSteps=stepsFor(flowType);
    const currentFlow=flowFor(flowType);
    const oldName=currentSteps[idx];
    if(oldName===newName){setEditStepIdx(null);return;}
    const newSteps=currentSteps.map((s,i)=>i===idx?newName:s);
    const newFlow:{[k:string]:any}={};
    Object.entries(currentFlow).forEach(([k,v])=>newFlow[k===oldName?newName:k]=v);
    await Promise.all([FS.updateCampo(c.id,stepsField,newSteps),FS.updateCampo(c.id,flowField,newFlow)]);
    setEditStepIdx(null);onRefresh();
  };
  const deleteFlowStep=async(flowType:string,idx:number)=>{
    const stepsField=stepsFieldFor(flowType);
    const flowField=flowFieldFor(flowType);
    const currentSteps=stepsFor(flowType);
    const currentFlow=flowFor(flowType);
    const stepName=currentSteps[idx];
    const newSteps=currentSteps.filter((_,i)=>i!==idx);
    const newFlow={...currentFlow};
    delete newFlow[stepName];
    await Promise.all([FS.updateCampo(c.id,stepsField,newSteps),FS.updateCampo(c.id,flowField,newFlow)]);
    onRefresh();
  };

  const [newStepInputs,setNewStepInputs]=useState<{[k:string]:string}>({});
  const addFlowStep=async(flowType:string)=>{
    const key=`newStep_${flowType}`;
    const name=(newStepInputs[key]||'').trim();
    if(!name)return;
    const stepsField=stepsFieldFor(flowType);
    const currentSteps=stepsFor(flowType);
    const newSteps=[...currentSteps,name];
    await FS.updateCampo(c.id,stepsField,newSteps);
    setNewStepInputs(p=>({...p,[key]:''}));
    onRefresh();
  };

  const [confirmDocDel,setConfirmDocDel]=useState<{idx:number;nombre:string}|null>(null);
  const delDoc=async(i:number)=>{await FS.removeDocumento(c.id,i);setConfirmDocDel(null);onRefresh();};

  const [editDocIdx,setEditDocIdx]=useState<number|null>(null);
  const [editDocNombre,setEditDocNombre]=useState('');
  const startRename=(i:number,nombre:string)=>{setEditDocIdx(i);setEditDocNombre(nombre);};
  const cancelRename=()=>{setEditDocIdx(null);setEditDocNombre('');};
  const confirmRename=async(i:number)=>{
    const nombre=editDocNombre.trim();
    if(!nombre){cancelRename();return;}
    await FS.renameDocumento(c.id,i,nombre);
    cancelRename();onRefresh();
  };

  const fileInputRef=useRef<HTMLInputElement>(null);
  const [uploadingDrive,setUploadingDrive]=useState(false);
  const handleFileUpload=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=e.target.files;if(!files||files.length===0)return;
    setUploadingDrive(true);
    const fallidos:string[]=[];
    try{
      const token=await auth.currentUser?.getIdToken();
      await Promise.all(Array.from(files).map(async(file)=>{
        try{
          const base64=await new Promise<string>((res)=>{const r=new FileReader();r.onload=()=>res((r.result as string).split(',')[1]);r.readAsDataURL(file);});
          const resp=await fetch('https://us-central1-souza-villa.cloudfunctions.net/uploadToDrive',{method:'POST',headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},body:JSON.stringify({fileBase64:base64,fileName:file.name,mimeType:file.type||'application/octet-stream',abogada:c.abogada||'Sin Abogada',cliente:c.nombreCliente||'Sin Cliente',codigoCaso:c.codigoCaso||c.id})});
          const result=await resp.json();
          if(!resp.ok) throw new Error(result.error||`Error ${resp.status}`);
          // Solo registramos el documento si de verdad se subió a Drive; así no
          // quedan "documentos fantasma" sin enlace ni archivo real.
          await FS.addDocumento(c.id,{nombre:file.name,tipo:'Archivo adjunto',link:result.driveUrl||'',driveId:result.driveId||'',size:file.size});
        }catch(err){
          console.error('Error subiendo a Drive:',err);
          fallidos.push(file.name);
        }
      }));
      onRefresh();
    }finally{
      setUploadingDrive(false);if(fileInputRef.current)fileInputRef.current.value='';
    }
    if(fallidos.length) alert(`No se pudo subir a Drive ${fallidos.length===1?'el archivo':`${fallidos.length} archivos`}:\n\n${fallidos.join('\n')}\n\nNo se agregaron a la lista del caso. Revisa tu conexión o permisos e intenta de nuevo.`);
  };

  const cameraInputRef=useRef<HTMLInputElement>(null);
  const [capturedPhotos,setCapturedPhotos]=useState<{name:string;dataUrl:string;w:number;h:number}[]>([]);
  const [pdfNombre,setPdfNombre]=useState('');
  const handleCameraCapture=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=e.target.files;if(!files||files.length===0)return;
    const MAX_PX=1600;
    const compressPhoto=(file:File):Promise<{name:string;dataUrl:string;w:number;h:number}>=>(
      new Promise((resolve,reject)=>{
        const objUrl=URL.createObjectURL(file);
        const img=new Image();
        img.onerror=()=>{URL.revokeObjectURL(objUrl);reject(new Error('No se pudo leer la imagen'));};
        img.onload=()=>{
          URL.revokeObjectURL(objUrl);
          try{
            let w=img.naturalWidth,h=img.naturalHeight;
            if(Math.max(w,h)>MAX_PX){
              if(w>=h){h=Math.round(h*MAX_PX/w);w=MAX_PX;}
              else{w=Math.round(w*MAX_PX/h);h=MAX_PX;}
            }
            const canvas=document.createElement('canvas');
            canvas.width=w;canvas.height=h;
            const ctx=canvas.getContext('2d');
            if(!ctx){reject(new Error('Canvas no disponible'));return;}
            ctx.drawImage(img,0,0,w,h);
            resolve({name:file.name,dataUrl:canvas.toDataURL('image/jpeg',0.72),w,h});
          }catch(err){reject(err);}
        };
        img.src=objUrl;
      })
    );
    try{
      const compressed=await Promise.all(Array.from(files).map(compressPhoto));
      setCapturedPhotos(prev=>[...prev,...compressed]);
    }catch(err:any){
      console.error('Error procesando foto:',err);
      alert('No se pudo procesar la foto. Intentalo de nuevo.');
    }
    if(cameraInputRef.current)cameraInputRef.current.value='';
  };
  const removePhoto=(idx:number)=>setCapturedPhotos(prev=>prev.filter((_,i)=>i!==idx));
  const compileToPdf=async()=>{
    if(capturedPhotos.length===0)return;
    setUploadingDrive(true);
    try{
      const firstIsPortrait=capturedPhotos[0].h>=capturedPhotos[0].w;
      const pdf=new jsPDF({orientation:firstIsPortrait?'portrait':'landscape',unit:'mm',format:'a4'});
      for(let i=0;i<capturedPhotos.length;i++){
        if(i>0) pdf.addPage('a4',capturedPhotos[i].h>=capturedPhotos[i].w?'portrait':'landscape');
        const pageW=pdf.internal.pageSize.getWidth();
        const pageH=pdf.internal.pageSize.getHeight();
        const{dataUrl:url,w,h}=capturedPhotos[i];
        const ratio=Math.min((pageW-10)/w,(pageH-10)/h);
        const fw=w*ratio;const fh=h*ratio;
        pdf.addImage(url,'JPEG',(pageW-fw)/2,(pageH-fh)/2,fw,fh);
      }
      const pdfBlob=pdf.output('blob');
      const base=pdfNombre.trim()||`Fotos_${c.codigoCaso||c.id.slice(0,8)}_${new Date().toISOString().slice(0,10)}`;
      const fileName=base.toLowerCase().endsWith('.pdf')?base:`${base}.pdf`;
      const token=await auth.currentUser?.getIdToken();
      const base64=await new Promise<string>(res=>{const r=new FileReader();r.onload=()=>res((r.result as string).split(',')[1]);r.readAsDataURL(pdfBlob);});
      const resp=await fetch('https://us-central1-souza-villa.cloudfunctions.net/uploadToDrive',{method:'POST',headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},body:JSON.stringify({fileBase64:base64,fileName,mimeType:'application/pdf',abogada:c.abogada||'Sin Abogada',cliente:c.nombreCliente||'Sin Cliente',codigoCaso:c.codigoCaso||c.id})});
      const result=await resp.json();
      if(!resp.ok) throw new Error(result.error||`Error ${resp.status}`);
      await FS.addDocumento(c.id,{nombre:fileName,tipo:'Fotos compiladas',link:result.driveUrl||'',driveId:result.driveId||'',size:pdfBlob.size});
      setCapturedPhotos([]);setPdfNombre('');onRefresh();
    }catch(err:any){
      console.error('Error compilando fotos a PDF:',err);
      alert(`Error al compilar el PDF: ${err?.message||'Intenta de nuevo.'}`);
    }finally{
      setUploadingDrive(false);
    }
  };

  const [pConc,setPConc]=useState('');const[pMon,setPMon]=useState('');const[pFec,setPFec]=useState('');const[pEst,setPEst]=useState('Pendiente');const[pNot,setPNot]=useState('');
  const [pForma,setPForma]=useState('Transferencia');const[pVenc,setPVenc]=useState('');
  const [autoCuotas,setAutoCuotas]=useState('');const[autoMonto,setAutoMonto]=useState('');const[autoVencInicio,setAutoVencInicio]=useState('');
  const [confirmGenerar,setConfirmGenerar]=useState(false);
  const addPago=async()=>{await FS.addPago(c.id,{concepto:pConc,monto:Number(pMon),fecha:pFec,estado:pEst,nota:pNot,formaPago:pForma,vencimiento:pVenc});setPConc('');setPMon('');setPFec('');setPNot('');setPVenc('');onRefresh();};
  const [confirmPagoDel,setConfirmPagoDel]=useState<{idx:number;nombre:string}|null>(null);
  const delPago=async(i:number)=>{await FS.removePago(c.id,i);setConfirmPagoDel(null);onRefresh();};
  const ejecutarGenerarCuotas=async()=>{
    const n=parseInt(autoCuotas);const monto=Number(autoMonto);
    if(!n||n<1||!monto)return;
    const startDate=autoVencInicio?new Date(autoVencInicio+'T00:00:00'):new Date();
    const startDay=startDate.getDate();
    const startMonth=startDate.getMonth();
    const startYear=startDate.getFullYear();
    const nuevasCuotas=Array.from({length:n},(_,i)=>{
      const totalMonth=startMonth+i;
      const y=startYear+Math.floor(totalMonth/12);
      const m=totalMonth%12;
      const lastDayOfMonth=new Date(y,m+1,0).getDate();
      const d=Math.min(startDay,lastDayOfMonth);
      const vencStr=`${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      return {concepto:`Cuota ${i+1}`,monto,fecha:'',estado:'Pendiente',nota:'',formaPago:'',vencimiento:vencStr};
    });
    setAutoCuotas('');setAutoMonto('');setAutoVencInicio('');setConfirmGenerar(false);
    FS.updateCampo(c.id,'pagos',[...(c.pagos||[]),...nuevasCuotas]).then(()=>onRefresh());
  };
  const generarCuotas=()=>{
    if((c.pagos||[]).length>0){setConfirmGenerar(true);}
    else{ejecutarGenerarCuotas();}
  };

  const [confirmBorrarCuotas,setConfirmBorrarCuotas]=useState(false);
  const borrarTodasCuotas=()=>{
    FS.updateCampo(c.id,'pagos',[]).then(()=>onRefresh());
    setConfirmBorrarCuotas(false);
  };

  const changePagoEstado=async(idx:number,nuevoEstado:string)=>{
    const updated=[...(c.pagos||[])];
    updated[idx]={...updated[idx],estado:nuevoEstado};
    if(nuevoEstado==='Pagado' && !updated[idx].fechaPago) updated[idx].fechaPago=new Date().toISOString().slice(0,10);
    if(nuevoEstado!=='Pagado') updated[idx].fechaPago='';
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };
  const changePagoFechaPago=async(idx:number,fecha:string)=>{
    const updated=[...(c.pagos||[])];updated[idx]={...updated[idx],fechaPago:fecha};
    await FS.updateCampo(c.id,'pagos',updated);onRefresh();
  };

  const pagos=c.pagos||[];
  const totalP=pagos.reduce((s:number,p:any)=>s+(Number(p.monto)||0),0);
  const pagado=pagos.filter((p:any)=>p.estado==='Pagado').reduce((s:number,p:any)=>s+(Number(p.monto)||0),0);
  const saldo=totalP-pagado;

  const handleAbogada=async(e:React.ChangeEvent<HTMLSelectElement>)=>{
    const uid=e.target.value;const nombre=e.target.options[e.target.selectedIndex]?.text||'';
    await FS.cambiarAbogada(c.id,uid,nombre);onRefresh();
  };

  const handleCaptadora=async(e:React.ChangeEvent<HTMLSelectElement>)=>{
    const uid=e.target.value;const nombre=e.target.options[e.target.selectedIndex]?.text||'';
    await FS.cambiarCaptadora(c.id,uid,nombre);onRefresh();
  };

  const PField=({label,val,field}:{label:string;val:string;field?:string})=>edita&&field?(
    <div className="p-field"><div className="p-field-label">{label}</div><input className="p-input" defaultValue={val||''} onBlur={e=>handleCampo(field,e.target.value)}/></div>
  ):(
    <div className="p-field"><div className="p-field-label">{label}</div><div className="p-field-val">{val||'—'}</div></div>
  );

  const isLaboral = (c.tema||'').toLowerCase().includes('laboral');

  return (<>
    <button className="p-btn-back" onClick={onBack}>← Volver a casos</button>
    <div className="caso-header-bar">
      <div>
        <div className="caso-titulo">{c.nombreCliente||'Cliente'}</div>
        <div style={{display:'flex',alignItems:'center',gap:10,marginTop:6,flexWrap:'wrap'}}>
          {(c.codigoCaso||c.id)&&<span style={{fontSize:'0.78rem',fontWeight:700,letterSpacing:'0.15em',padding:'4px 12px',borderRadius:5,background:'rgba(0,188,212,0.15)',color:'#00E5FF',border:'1px solid rgba(0,229,255,0.4)',fontFamily:'monospace'}}>{c.codigoCaso||c.id.slice(0,8).toUpperCase()}</span>}
          <span className="caso-sub">{c.rut||''}</span>
          <span className="caso-sub" style={{display:'inline-flex',alignItems:'center',gap:5}} title="Fecha en que se atendió al cliente por primera vez">
            <span style={{opacity:0.65}}>🗓️ Atendido:</span>
            <strong style={{fontWeight:600}}>{FS.fmtDate(c.createdAt)}</strong>
          </span>
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8}}>
        {edita?<select className="p-select estado-select" value={c.estado||''} onChange={e=>handleEstado(e.target.value)} style={{borderColor:FS.statusColors[c.estado]||'#888'}}>
          {Object.keys(FS.statusColors).map(s=><option key={s}>{s}</option>)}
        </select>:<span className="caso-badge" style={{background:(FS.statusColors[c.estado]||'#555')+'22',color:FS.statusColors[c.estado]||'#888'}}>{c.estado||'—'}</span>}
        {c.correo&&(edita||(rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora'))&&(
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
            <button onClick={notificarEstado} disabled={enviandoNotifEstado||!c.estado}
              style={{background:'none',border:'1px solid rgba(100,181,246,0.35)',borderRadius:6,cursor:'pointer',padding:'4px 12px',fontSize:'0.7rem',color:'#64B5F6',letterSpacing:'0.08em',transition:'all 0.2s',whiteSpace:'nowrap',opacity:(enviandoNotifEstado||!c.estado)?0.5:1}}>
              {enviandoNotifEstado?'Enviando…':'📨 Notificar estado al cliente'}
            </button>
            {notifEstadoEnviada&&<span style={{fontSize:'0.65rem',color:'#6aad6a'}}>✓ Notificación enviada</span>}
            <button onClick={enviarCorreoBienvenida} disabled={enviandoBienvenida}
              style={{background:'none',border:'1px solid rgba(184,150,90,0.35)',borderRadius:6,cursor:'pointer',padding:'4px 12px',fontSize:'0.7rem',color:'var(--gold)',letterSpacing:'0.08em',transition:'all 0.2s',whiteSpace:'nowrap',opacity:enviandoBienvenida?0.6:1}}>
              {enviandoBienvenida?'Enviando…':'📧 Enviar correo de bienvenida'}
            </button>
            {c.bienvenidaEnviada&&(
              <span style={{fontSize:'0.65rem',color:'#6aad6a',letterSpacing:'0.05em'}}>
                ✓ Bienvenida enviada el {c.bienvenidaEnviada.split('-').reverse().join('-')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
    <div className="caso-tabs">
      {['datos','proceso','documentos','pagos'].map(t=>(
        <button key={t} className={`ctab ${tab===t?'active':''}`} onClick={()=>setTab(t)}>
          {t==='datos'?'📋 Datos':t==='proceso'?'⚖️ Proceso':t==='documentos'?'📁 Documentos':'💰 Pagos'}
        </button>
      ))}
    </div>

    {tab==='datos'&&(
      <div className="p-grid3-datos">
        <div className="p-section">
          <div className="p-section-title">Datos personales</div>
          <PField label="Nombre completo" val={c.nombreCliente} field="nombreCliente"/>
          {edita?<div className="p-field"><div className="p-field-label">RUT</div><input className="p-input" defaultValue={c.rut||''} onChange={e=>{const cl=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);e.target.value=cl.length>=2?formatRut(cl):cl;}} onBlur={e=>{const v=formatRut(e.target.value);e.target.value=v;handleCampo('rut',v);}}/></div>:<div className="p-field"><div className="p-field-label">RUT</div><div className="p-field-val">{c.rut||'—'}</div></div>}
          <PField label="Estado civil" val={c.estadoCivil} field="estadoCivil"/>
          <PField label="Nacionalidad" val={c.nacionalidad} field="nacionalidad"/>
          <PField label="Ocupación" val={c.ocupacion} field="ocupacion"/>
          {isLaboral && <>
            <div className="p-sub-title" style={{marginTop:14}}>Datos laborales</div>
            <PField label="Empleador" val={c.empleador} field="empleador"/>
            {edita?<div className="p-field"><div className="p-field-label">RUT Empleador</div><input className="p-input" defaultValue={c.rutEmpleador||''} onChange={e=>{const cl=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);e.target.value=cl.length>=2?formatRut(cl):cl;}} onBlur={e=>{const v=formatRut(e.target.value);e.target.value=v;handleCampo('rutEmpleador',v);}}/></div>:<div className="p-field"><div className="p-field-label">RUT Empleador</div><div className="p-field-val">{c.rutEmpleador||'—'}</div></div>}
            <PField label="Representante legal" val={c.representanteLegal} field="representanteLegal"/>
            <PField label="Domicilio empleador" val={c.domicilioEmpleador} field="domicilioEmpleador"/>
            {edita?(
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><input className="p-input" type="date" defaultValue={c.fechaDespido||''} onBlur={e=>handleCampo('fechaDespido',e.target.value)}/></div>
            ):(
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><div className="p-field-val">{c.fechaDespido?c.fechaDespido.split('-').reverse().join('-'):'—'}</div></div>
            )}
            {(c.laboralMotivos||[]).length>0 && (
              <div className="p-field">
                <div className="p-field-label">Motivo principal consulta</div>
                <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:4}}>
                  {(c.laboralMotivos||[]).map((m:string)=>(
                    <div key={m} style={{fontSize:13,color:'var(--white)',padding:'4px 10px',background:'rgba(255,255,255,0.04)',borderRadius:5,borderLeft:'3px solid var(--gold)'}}>
                      {m}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>}
        </div>
        <div className="p-section">
          <div className="p-section-title">Contacto</div>
          <PField label="Teléfono / WhatsApp" val={c.telefono} field="telefono"/>
          <PField label="Correo electrónico" val={c.correo} field="correo"/>
          <PField label="Dirección" val={c.direccion} field="direccion"/>
          <div className="p-section" style={{marginTop:'1.2rem'}}>
            <div className="p-section-title">Caso</div>
            <PField label="Tema" val={c.tema}/>
            {(rol==='admin'||rol==='abogada')?(
              <div className="p-field">
                <div className="p-field-label">Captadora</div>
                <select className="p-select" value={c.captadoraId||''} onChange={handleCaptadora}>
                  <option value="">Sin captadora</option>
                  <option value="WEB">WEB</option>
                  {captadoras.map((a:any)=><option key={a.id} value={a.id}>{a.nombre||a.email}</option>)}
                </select>
              </div>
            ):(
              <PField label="Captadora" val={c.captadora}/>
            )}
            <div className="p-field">
              <div className="p-field-label">Abogada asignada</div>
              {edita?<select className="p-select" value={c.abogadaId||''} onChange={handleAbogada}>
                <option value="">Sin asignar</option>
                {abogadas.map((a:any)=><option key={a.id} value={a.id}>{(a as any).nombre||(a as any).email}</option>)}
              </select>:<div className="p-field-val">{c.abogada||'—'}</div>}
            </div>
            <PField label="Procedimiento" val={c.procedimiento} field="procedimiento"/>
            <PField label="Rol" val={c.rolCaso} field="rolCaso"/>
            <div className="p-field">
              <div className="p-field-label">Contraparte</div>
              {edita ? (
                <textarea className="p-input" rows={4} defaultValue={c.contraparte||''} onBlur={e=>handleCampo('contraparte',e.target.value)} style={{resize:'vertical',whiteSpace:'pre-wrap'}}/>
              ) : (
                <div className="p-field-val" style={{whiteSpace:'pre-wrap'}}>{c.contraparte||'—'}</div>
              )}
            </div>
            {(c.tema||'').includes('Familia') && (c.familiaMaterias||[]).length>0 && (
              <div className="p-field">
                <div className="p-field-label">Materia</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:4}}>
                  {(c.familiaMaterias||[]).map((m:string)=>(
                    <span key={m} style={{fontSize:12,color:'var(--white)',padding:'3px 10px',background:'rgba(255,255,255,0.06)',borderRadius:12,border:'1px solid rgba(255,255,255,0.1)'}}>
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-section">
          <div className="p-section-title">Evaluación</div>

          {(rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora')&&(
            <div className="p-field">
              <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>Evaluación interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Borrador rápido · Solo equipo</span></span>
                {edita&&<button onClick={()=>openTextModal('evaluacionInterna','Evaluación interna (solo equipo)',evalInternaVal)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12}}>⛶ Expandir</button>}
              </div>
              <RichText value={evalInternaVal} onChange={setEvalInternaVal} onBlur={v=>edita&&handleCampo('evaluacionInterna',v)} readOnly={!edita} placeholder="Borrador rápido de evaluación — Solo visible para el equipo" minHeight={120}/>
              {edita&&<button type="button" onClick={transformarConIADetalle} disabled={transformandoDetalle||!evalInternaVal.replace(/<[^>]*>/g,'').trim()} style={{marginTop:8,padding:'8px 0',background:transformandoDetalle?'rgba(255,255,255,0.04)':'rgba(212,175,55,0.12)',border:'1px solid rgba(212,175,55,0.4)',color:'var(--gold)',borderRadius:6,cursor:transformandoDetalle?'wait':'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:6,width:'100%',opacity:transformandoDetalle||!evalInternaVal.replace(/<[^>]*>/g,'').trim()?0.5:1,transition:'all 0.2s'}}>
                {transformandoDetalle?'⏳ Procesando con IA…':'✨ Transformar con IA → Evaluación para cliente'}
              </button>}
            </div>
          )}

          <div className="p-field">
            <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>Evaluación (visible para cliente)</span>
              {edita&&<button onClick={()=>openTextModal('evaluacion','Evaluación (visible para cliente)',evalVal)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12}}>⛶ Expandir</button>}
            </div>
            <RichText value={evalVal} onChange={setEvalVal} onBlur={v=>edita&&handleCampo('evaluacion',v)} readOnly={!edita} placeholder="Evaluación profesional del caso (visible para el cliente)" minHeight={120}/>
          </div>

          <div className="p-field">
            <div className="p-field-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>Nota interna (solo equipo)</span>
              {edita&&<button onClick={()=>openTextModal('notaInterna','Nota interna (solo equipo)',notaVal)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12}}>⛶ Expandir</button>}
            </div>
            <RichText value={notaVal} onChange={setNotaVal} onBlur={v=>edita&&handleCampo('notaInterna',v)} readOnly={!edita} placeholder="Notas internas del equipo" minHeight={100}/>
          </div>

          <div className="p-field"><div className="p-field-label">Recordatorio para cliente</div>
            <textarea className="p-input" rows={3} defaultValue={c.recordatorio||''} onBlur={e=>edita&&handleCampo('recordatorio',e.target.value)} readOnly={!edita} style={{resize:'vertical'}}/></div>
        </div>

        {textModal.open&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setTextModal(m=>({...m,open:false}))}}>
            <div style={{background:'#1a2540',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,width:'100%',maxWidth:760,maxHeight:'calc(100vh - 40px)',display:'flex',flexDirection:'column',gap:0,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                <span style={{fontWeight:600,color:'var(--white)',fontSize:15}}>{textModal.label}</span>
                <button onClick={()=>setTextModal(m=>({...m,open:false}))} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
              </div>
              <div style={{padding:'8px 20px 0',flex:1,overflowY:'auto'}}>
                <RichText value={textModal.value} onChange={v=>setTextModal(m=>({...m,value:v}))} readOnly={!edita} placeholder={edita?'Escriba aquí…':'(sin contenido)'} minHeight={350}/>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:10,padding:'12px 20px',borderTop:'1px solid rgba(255,255,255,0.08)',flexShrink:0}}>
                <button onClick={()=>setTextModal(m=>({...m,open:false}))} className="p-btn-secondary">Cancelar</button>
                {edita&&<button onClick={saveTextModal} className="p-btn-primary">Guardar</button>}
              </div>
            </div>
          </div>
        )}
        <div style={{gridColumn:'1/-1'}}><NotasInternas casoId={c.id} field="notasInternasDatos" value={c.notasInternasDatos} edita={edita} onRefresh={onRefresh}/></div>
      </div>
    )}

    {tab==='proceso'&&(<>
      {isLaboral && (
        <div className="p-section">
          <div className="p-section-title">⚙️ Procedimiento</div>
          {(()=>{
            const done=laborSteps.filter(s=>(laborFlow[s]||{}).done).length;
            const total=laborSteps.length;
            const next=laborSteps.find(s=>!(laborFlow[s]||{}).done);
            const pct=total>0?Math.round(done/total*100):0;
            return(
              <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>{done===total&&total>0?'Procedimiento completado':`Etapa ${done+1} de ${total}`}</span>
                  <span style={{fontSize:12,color:done===total&&total>0?'#4CAF50':'var(--text-muted)'}}>{done===total&&total>0?'✅ Completado':`Faltan ${total-done} etapas`}</span>
                </div>
                <div style={{background:'rgba(255,255,255,0.06)',borderRadius:3,height:5,marginBottom:next?8:0,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:3,transition:'width 0.4s'}}/>
                </div>
                {next&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Próxima etapa: <span style={{color:'var(--gold)',fontWeight:500}}>{next}</span></div>}
              </div>
            );
          })()}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {laborSteps.map((step,i)=>{
              const sd=laborFlow[step]||{done:false,instrucciones:''};
              const isNext=!sd.done&&laborSteps.slice(0,i).every(s=>(laborFlow[s]||{}).done);
              const isEditing=editStepIdx?.flowType==='labor'&&editStepIdx?.idx===i;
              return(
                <div key={i} style={{background:isNext?'rgba(184,150,90,0.05)':'rgba(255,255,255,0.02)',borderRadius:8,padding:'10px 14px',borderLeft:sd.done?'3px solid var(--gold)':isNext?'3px solid rgba(184,150,90,0.5)':'3px solid rgba(255,255,255,0.08)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                      <input type="checkbox" checked={sd.done} onChange={e=>edita&&toggleFlowStep(step,e.target.checked,'labor')} disabled={!edita} style={{width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,minWidth:22,flexShrink:0}}>{i+1}.</span>
                      {isEditing?(
                        <input autoFocus className="p-input" value={editStepTxt} onChange={e=>setEditStepTxt(e.target.value)} onBlur={saveEditStep} onKeyDown={e=>{if(e.key==='Enter')saveEditStep();if(e.key==='Escape')setEditStepIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
                      ):(
                        <span style={{textDecoration:sd.done?'line-through':'none',color:sd.done?'var(--text-muted)':isNext?'var(--gold)':'var(--white)',fontWeight:isNext?600:500,fontSize:14}}>{step}</span>
                      )}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      {sd.done&&!isEditing&&<span className="pago-badge ok" style={{fontSize:10}}>✓</span>}
                      {!sd.done&&isNext&&!isEditing&&<span style={{fontSize:10,color:'var(--gold)',border:'1px solid rgba(184,150,90,0.4)',padding:'2px 6px',borderRadius:3}}>Actual</span>}
                      {edita&&!isEditing&&<button onClick={()=>startEditStep('labor',i,step)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Renombrar etapa">✏️</button>}
                      {edita&&!isEditing&&<button onClick={()=>setConfirmDel({msg:`la etapa "${step}"`,onConfirm:()=>deleteFlowStep('labor',i)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.6)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Eliminar etapa">✕</button>}
                    </div>
                  </div>
                  {!isEditing&&(
                    <div style={{marginTop:8,marginLeft:36}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,marginBottom:5,letterSpacing:'0.05em',textTransform:'uppercase'}}>Notas internas</div>
                      {edita&&<div style={{display:'flex',gap:6,marginBottom:6}}>
                        <input className="p-input" placeholder="Agregar nota..." value={subCheckInputs[`labor_${i}`]||''} onChange={e=>setSubCheckInputs(p=>({...p,[`labor_${i}`]:e.target.value}))} onKeyDown={e=>{const t=(subCheckInputs[`labor_${i}`]||'').trim();if(e.key==='Enter'&&t){addSubCheck(step,'labor',t);setSubCheckInputs(p=>({...p,[`labor_${i}`]:''}));}}} style={{flex:1,fontSize:12,padding:'3px 8px'}}/>
                        <button className="p-btn-primary" onClick={()=>{const t=(subCheckInputs[`labor_${i}`]||'').trim();if(t){addSubCheck(step,'labor',t);setSubCheckInputs(p=>({...p,[`labor_${i}`]:''}));}}} style={{fontSize:12,padding:'3px 10px'}}>＋</button>
                      </div>}
                      {(sd.subChecklist||[]).length===0&&<div style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic',opacity:0.6}}>Sin notas.</div>}
                      {(sd.subChecklist||[]).map((sc:any,si:number)=>(
                        <div key={si} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                          <input type="checkbox" checked={sc.done} onChange={e=>toggleSubCheck(step,'labor',si,e.target.checked)} style={{width:13,height:13,flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12,textDecoration:sc.done?'line-through':'none',color:sc.done?'var(--text-muted)':'var(--white)',opacity:0.85}}>{sc.texto}</span>
                          {edita&&<button onClick={()=>setConfirmDel({msg:`la nota "${sc.texto}"`,onConfirm:()=>delSubCheck(step,'labor',si)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.5)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {edita&&<div style={{display:'flex',gap:8,marginTop:10}}>
            <input className="p-input" placeholder="Nueva etapa..." value={newStepInputs['newStep_labor']||''} onChange={e=>setNewStepInputs(p=>({...p,newStep_labor:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addFlowStep('labor')} style={{flex:1,fontSize:13}}/>
            <button className="p-btn-primary" onClick={()=>addFlowStep('labor')} style={{whiteSpace:'nowrap'}}>＋ Agregar etapa</button>
          </div>}
        </div>
      )}

      {(c.tema||'').includes('Familia') && (
        <div className="p-section">
          <div className="p-section-title">⚙️ Procedimiento</div>
          {(()=>{
            const done=familiaSteps.filter(s=>(familiaFlow[s]||{}).done).length;
            const total=familiaSteps.length;
            const next=familiaSteps.find(s=>!(familiaFlow[s]||{}).done);
            const pct=total>0?Math.round(done/total*100):0;
            return(
              <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>{done===total&&total>0?'Procedimiento completado':`Etapa ${done+1} de ${total}`}</span>
                  <span style={{fontSize:12,color:done===total&&total>0?'#4CAF50':'var(--text-muted)'}}>{done===total&&total>0?'✅ Completado':`Faltan ${total-done} etapas`}</span>
                </div>
                <div style={{background:'rgba(255,255,255,0.06)',borderRadius:3,height:5,marginBottom:next?8:0,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:3,transition:'width 0.4s'}}/>
                </div>
                {next&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Próxima etapa: <span style={{color:'var(--gold)',fontWeight:500}}>{next}</span></div>}
              </div>
            );
          })()}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {familiaSteps.map((step,i)=>{
              const sd=familiaFlow[step]||{done:false,instrucciones:''};
              const isNext=!sd.done&&familiaSteps.slice(0,i).every(s=>(familiaFlow[s]||{}).done);
              const isEditing=editStepIdx?.flowType==='familia'&&editStepIdx?.idx===i;
              return(
                <div key={i} style={{background:isNext?'rgba(184,150,90,0.05)':'rgba(255,255,255,0.02)',borderRadius:8,padding:'10px 14px',borderLeft:sd.done?'3px solid var(--gold)':isNext?'3px solid rgba(184,150,90,0.5)':'3px solid rgba(255,255,255,0.08)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                      <input type="checkbox" checked={sd.done} onChange={e=>edita&&toggleFlowStep(step,e.target.checked,'familia')} disabled={!edita} style={{width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,minWidth:22,flexShrink:0}}>{i+1}.</span>
                      {isEditing?(
                        <input autoFocus className="p-input" value={editStepTxt} onChange={e=>setEditStepTxt(e.target.value)} onBlur={saveEditStep} onKeyDown={e=>{if(e.key==='Enter')saveEditStep();if(e.key==='Escape')setEditStepIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
                      ):(
                        <span style={{textDecoration:sd.done?'line-through':'none',color:sd.done?'var(--text-muted)':isNext?'var(--gold)':'var(--white)',fontWeight:isNext?600:500,fontSize:14}}>{step}</span>
                      )}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      {sd.done&&!isEditing&&<span className="pago-badge ok" style={{fontSize:10}}>✓</span>}
                      {!sd.done&&isNext&&!isEditing&&<span style={{fontSize:10,color:'var(--gold)',border:'1px solid rgba(184,150,90,0.4)',padding:'2px 6px',borderRadius:3}}>Actual</span>}
                      {edita&&!isEditing&&<button onClick={()=>startEditStep('familia',i,step)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Renombrar etapa">✏️</button>}
                      {edita&&!isEditing&&<button onClick={()=>setConfirmDel({msg:`la etapa "${step}"`,onConfirm:()=>deleteFlowStep('familia',i)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.6)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Eliminar etapa">✕</button>}
                    </div>
                  </div>
                  {!isEditing&&(
                    <div style={{marginTop:8,marginLeft:36}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,marginBottom:5,letterSpacing:'0.05em',textTransform:'uppercase'}}>Notas internas</div>
                      {edita&&<div style={{display:'flex',gap:6,marginBottom:6}}>
                        <input className="p-input" placeholder="Agregar nota..." value={subCheckInputs[`familia_${i}`]||''} onChange={e=>setSubCheckInputs(p=>({...p,[`familia_${i}`]:e.target.value}))} onKeyDown={e=>{const t=(subCheckInputs[`familia_${i}`]||'').trim();if(e.key==='Enter'&&t){addSubCheck(step,'familia',t);setSubCheckInputs(p=>({...p,[`familia_${i}`]:''}));}}} style={{flex:1,fontSize:12,padding:'3px 8px'}}/>
                        <button className="p-btn-primary" onClick={()=>{const t=(subCheckInputs[`familia_${i}`]||'').trim();if(t){addSubCheck(step,'familia',t);setSubCheckInputs(p=>({...p,[`familia_${i}`]:''}));}}} style={{fontSize:12,padding:'3px 10px'}}>＋</button>
                      </div>}
                      {(sd.subChecklist||[]).length===0&&<div style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic',opacity:0.6}}>Sin notas.</div>}
                      {(sd.subChecklist||[]).map((sc:any,si:number)=>(
                        <div key={si} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                          <input type="checkbox" checked={sc.done} onChange={e=>toggleSubCheck(step,'familia',si,e.target.checked)} style={{width:13,height:13,flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12,textDecoration:sc.done?'line-through':'none',color:sc.done?'var(--text-muted)':'var(--white)',opacity:0.85}}>{sc.texto}</span>
                          {edita&&<button onClick={()=>setConfirmDel({msg:`la nota "${sc.texto}"`,onConfirm:()=>delSubCheck(step,'familia',si)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.5)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {edita&&<div style={{display:'flex',gap:8,marginTop:10}}>
            <input className="p-input" placeholder="Nueva etapa..." value={newStepInputs['newStep_familia']||''} onChange={e=>setNewStepInputs(p=>({...p,newStep_familia:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addFlowStep('familia')} style={{flex:1,fontSize:13}}/>
            <button className="p-btn-primary" onClick={()=>addFlowStep('familia')} style={{whiteSpace:'nowrap'}}>＋ Agregar etapa</button>
          </div>}
        </div>
      )}

      {(c.tema||'').includes('Civil') && (
        <div className="p-section">
          <div className="p-section-title">⚙️ Procedimiento</div>
          {(()=>{
            const done=civilSteps.filter(s=>(civilFlow[s]||{}).done).length;
            const total=civilSteps.length;
            const next=civilSteps.find(s=>!(civilFlow[s]||{}).done);
            const pct=total>0?Math.round(done/total*100):0;
            return(
              <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,border:'1px solid rgba(255,255,255,0.06)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>{done===total&&total>0?'Procedimiento completado':`Etapa ${done+1} de ${total}`}</span>
                  <span style={{fontSize:12,color:done===total&&total>0?'#4CAF50':'var(--text-muted)'}}>{done===total&&total>0?'✅ Completado':`Faltan ${total-done} etapas`}</span>
                </div>
                <div style={{background:'rgba(255,255,255,0.06)',borderRadius:3,height:5,marginBottom:next?8:0,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:'var(--gold)',borderRadius:3,transition:'width 0.4s'}}/>
                </div>
                {next&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Próxima etapa: <span style={{color:'var(--gold)',fontWeight:500}}>{next}</span></div>}
              </div>
            );
          })()}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {civilSteps.map((step,i)=>{
              const sd=civilFlow[step]||{done:false,instrucciones:''};
              const isNext=!sd.done&&civilSteps.slice(0,i).every(s=>(civilFlow[s]||{}).done);
              const isEditing=editStepIdx?.flowType==='civil'&&editStepIdx?.idx===i;
              return(
                <div key={i} style={{background:isNext?'rgba(184,150,90,0.05)':'rgba(255,255,255,0.02)',borderRadius:8,padding:'10px 14px',borderLeft:sd.done?'3px solid var(--gold)':isNext?'3px solid rgba(184,150,90,0.5)':'3px solid rgba(255,255,255,0.08)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                      <input type="checkbox" checked={sd.done} onChange={e=>edita&&toggleFlowStep(step,e.target.checked,'civil')} disabled={!edita} style={{width:16,height:16,flexShrink:0}}/>
                      <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,minWidth:22,flexShrink:0}}>{i+1}.</span>
                      {isEditing?(
                        <input autoFocus className="p-input" value={editStepTxt} onChange={e=>setEditStepTxt(e.target.value)} onBlur={saveEditStep} onKeyDown={e=>{if(e.key==='Enter')saveEditStep();if(e.key==='Escape')setEditStepIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
                      ):(
                        <span style={{textDecoration:sd.done?'line-through':'none',color:sd.done?'var(--text-muted)':isNext?'var(--gold)':'var(--white)',fontWeight:isNext?600:500,fontSize:14}}>{step}</span>
                      )}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      {sd.done&&!isEditing&&<span className="pago-badge ok" style={{fontSize:10}}>✓</span>}
                      {!sd.done&&isNext&&!isEditing&&<span style={{fontSize:10,color:'var(--gold)',border:'1px solid rgba(184,150,90,0.4)',padding:'2px 6px',borderRadius:3}}>Actual</span>}
                      {edita&&!isEditing&&<button onClick={()=>startEditStep('civil',i,step)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Renombrar etapa">✏️</button>}
                      {edita&&!isEditing&&<button onClick={()=>setConfirmDel({msg:`la etapa "${step}"`,onConfirm:()=>deleteFlowStep('civil',i)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.6)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Eliminar etapa">✕</button>}
                    </div>
                  </div>
                  {!isEditing&&(
                    <div style={{marginTop:8,marginLeft:36}}>
                      <div style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,marginBottom:5,letterSpacing:'0.05em',textTransform:'uppercase'}}>Notas internas</div>
                      {edita&&<div style={{display:'flex',gap:6,marginBottom:6}}>
                        <input className="p-input" placeholder="Agregar nota..." value={subCheckInputs[`civil_${i}`]||''} onChange={e=>setSubCheckInputs(p=>({...p,[`civil_${i}`]:e.target.value}))} onKeyDown={e=>{const t=(subCheckInputs[`civil_${i}`]||'').trim();if(e.key==='Enter'&&t){addSubCheck(step,'civil',t);setSubCheckInputs(p=>({...p,[`civil_${i}`]:''}));}}} style={{flex:1,fontSize:12,padding:'3px 8px'}}/>
                        <button className="p-btn-primary" onClick={()=>{const t=(subCheckInputs[`civil_${i}`]||'').trim();if(t){addSubCheck(step,'civil',t);setSubCheckInputs(p=>({...p,[`civil_${i}`]:''}));}}} style={{fontSize:12,padding:'3px 10px'}}>＋</button>
                      </div>}
                      {(sd.subChecklist||[]).length===0&&<div style={{fontSize:11,color:'var(--text-muted)',fontStyle:'italic',opacity:0.6}}>Sin notas.</div>}
                      {(sd.subChecklist||[]).map((sc:any,si:number)=>(
                        <div key={si} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                          <input type="checkbox" checked={sc.done} onChange={e=>toggleSubCheck(step,'civil',si,e.target.checked)} style={{width:13,height:13,flexShrink:0}}/>
                          <span style={{flex:1,fontSize:12,textDecoration:sc.done?'line-through':'none',color:sc.done?'var(--text-muted)':'var(--white)',opacity:0.85}}>{sc.texto}</span>
                          {edita&&<button onClick={()=>setConfirmDel({msg:`la nota "${sc.texto}"`,onConfirm:()=>delSubCheck(step,'civil',si)})} style={{background:'none',border:'none',color:'rgba(255,100,100,0.5)',cursor:'pointer',fontSize:11,padding:'0 2px',lineHeight:1}}>✕</button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {edita&&<div style={{display:'flex',gap:8,marginTop:10}}>
            <input className="p-input" placeholder="Nueva etapa..." value={newStepInputs['newStep_civil']||''} onChange={e=>setNewStepInputs(p=>({...p,newStep_civil:e.target.value}))} onKeyDown={e=>e.key==='Enter'&&addFlowStep('civil')} style={{flex:1,fontSize:13}}/>
            <button className="p-btn-primary" onClick={()=>addFlowStep('civil')} style={{whiteSpace:'nowrap'}}>＋ Agregar etapa</button>
          </div>}
        </div>
      )}

      <div className="p-section" style={{marginTop:(isLaboral||(c.tema||'').includes('Familia')||(c.tema||'').includes('Civil'))?20:0}}>
        <div className="p-section-title">Audiencias y fechas clave</div>
        {edita&&<div className="p-grid3" style={{marginBottom:12}}>
          <input className="p-input" type="date" value={audF} onChange={e=>setAudF(e.target.value)}/>
          <input className="p-input" type="time" value={audH} onChange={e=>setAudH(e.target.value)}/>
          <input className="p-input" placeholder="Lugar" value={audL} onChange={e=>setAudL(e.target.value)}/>
          <select className="p-select" value={audT} onChange={e=>{setAudT(e.target.value);if(e.target.value!=='Otro')setAudTOtro('');}} style={{gridColumn:audT==='Otro'?'1/2':'1/3'}}>
            <option value="">Selecciona tipo...</option>
            {TIPOS_AUDIENCIA.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {audT==='Otro'&&<input className="p-input" placeholder="Especifica el tipo" value={audTOtro} onChange={e=>setAudTOtro(e.target.value)} style={{gridColumn:'2/3'}}/>}
          <button className="p-btn-primary" onClick={addAud} disabled={savingAud} style={{cursor:savingAud?'not-allowed':'pointer',opacity:savingAud?0.6:1}}>{savingAud?'Agregando…':'＋ Agregar'}</button>
        </div>}
        {(c.audiencias||[]).length?(c.audiencias||[]).map((a:any,i:number)=>(
          <div className="aud-row" key={i}><div className="aud-dot"/><div className="aud-body"><div className="aud-tipo">{a.tipo||'Audiencia'}{!a.gcalEventId&&<span title="No sincronizada con Google Calendar" style={{marginLeft:6,fontSize:12,color:'#c9a227'}}>⚠️ sin calendario</span>}</div><div className="aud-info">{FS.fmtDate(a.fecha)||''} {a.hora?'· '+a.hora:''} {a.lugar?'· '+a.lugar:''}</div></div>
          {edita&&!a.gcalEventId&&<button className="p-btn-secondary-sm" title="Crear evento en Google Calendar" onClick={()=>syncAud(i)} disabled={syncingAud!==null} style={{cursor:syncingAud!==null?'not-allowed':'pointer',opacity:syncingAud!==null&&syncingAud!==i?0.6:1}}>{syncingAud===i?'…':'🔄'}</button>}
          {edita&&<button className="p-btn-danger-sm" onClick={()=>setConfirmDel({msg:`la audiencia "${a.tipo||'Audiencia'}" (${FS.fmtDate(a.fecha)||''})`,onConfirm:()=>delAud(i)})}>✕</button>}</div>
        )):<div className="p-empty">No hay audiencias registradas.</div>}
      </div>
      <div className="p-section" style={{marginTop:20}}>
        <div className="p-section-title">Checklist de acciones pendientes</div>
        {edita&&(
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <div className="mention-wrapper">
              <input
                ref={checkInputRef}
                className="p-input"
                placeholder="Acción pendiente... @Nombre o @todos para notificar · @plazo 28-05-2026 para fecha límite"
                value={checkTxt}
                onChange={handleCheckTxtChange}
                onKeyDown={e=>{
                  if(e.key==='Enter'&&mentionSearch===null)addCheck(false);
                  if(e.key==='Escape')setMentionSearch(null);
                }}
              />
              {filteredMentions.length>0&&(
                <div className="mention-dropdown">
                  {filteredMentions.map(u=>(
                    <div key={u.id} className="mention-item" onMouseDown={e=>{e.preventDefault();insertMention(u);}}>
                      <div className="mention-item-avatar">{(mentionLabel(u)||'?').slice(0,2).toUpperCase()}</div>
                      <span className="mention-item-name">{mentionLabel(u)}</span>
                      <span className="mention-item-rol">{u.rol}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="p-btn-primary" onClick={()=>addCheck(false)} disabled={savingCheck}>{savingCheck?'…':'＋'}</button>
          </div>
        )}
        {(c.checklist||[]).length?(c.checklist||[]).map((ch:any,i:number)=>(
          <div className="check-row" key={i}>
            <input type="checkbox" checked={ch.done} onChange={e=>edita&&toggleCheck(i,e.target.checked)} disabled={!edita}/>
            {edita&&editCheckIdx===i?(
              <input autoFocus className="p-input" value={editCheckTxt} onChange={e=>setEditCheckTxt(e.target.value)} onBlur={saveEditCheck} onKeyDown={e=>{if(e.key==='Enter')saveEditCheck();if(e.key==='Escape')setEditCheckIdx(null);}} style={{flex:1,fontSize:14,padding:'2px 6px'}}/>
            ):(
              <span style={{flex:1,textDecoration:ch.done?'line-through':'none',color:ch.done?'var(--text-muted)':'var(--white)'}}>{renderMenciones(ch.texto)}</span>
            )}
            {edita&&editCheckIdx!==i&&<button onClick={()=>startEditCheck(i,ch.texto)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:13,padding:'0 3px',lineHeight:1}} title="Editar">✏️</button>}
            {edita&&editCheckIdx!==i&&<button className="p-btn-danger-sm" onClick={()=>setConfirmDel({msg:`"${ch.texto}"`,onConfirm:()=>delCheck(i)})}>✕</button>}
          </div>
        )):<div className="p-empty">Sin acciones pendientes.</div>}
      </div>
      <NotasInternas casoId={c.id} field="notasInternasProceso" value={c.notasInternasProceso} edita={edita} onRefresh={onRefresh}/>
    </>)}

    {tab==='documentos'&&(
      <div className="p-section">
        <div className="p-section-title">Documentos del caso</div>
        <div className="p-info-box">💡 Adjunte archivos o tome fotos con su celular.</div>
        {edita&&<>
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <button className="p-btn-secondary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&fileInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>{uploadingDrive?'⏳ Subiendo a Drive…':'📎 Adjuntar archivos'}</button>
            <input ref={fileInputRef} type="file" multiple accept="*/*" onChange={handleFileUpload} style={{display:'none'}}/>
            <button className="p-btn-secondary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&cameraInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>📷 Tomar fotos</button>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handleCameraCapture} style={{display:'none'}}/>
            {capturedPhotos.length>0 && <button className="p-btn-primary" disabled={uploadingDrive} onClick={()=>!uploadingDrive&&compileToPdf()} style={{display:'flex',alignItems:'center',gap:6,opacity:uploadingDrive?0.6:1,cursor:uploadingDrive?'not-allowed':'pointer'}}>{uploadingDrive?'⏳ Subiendo a Drive…':`📄 Compilar ${capturedPhotos.length} foto${capturedPhotos.length>1?'s':''} a PDF`}</button>}
          </div>
          {capturedPhotos.length>0 && (
            <div style={{marginBottom:16}}>
              <div className="p-field-label" style={{marginBottom:6}}>Nombre del PDF</div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
                <input
                  className="p-input"
                  style={{flex:1,fontSize:13}}
                  value={pdfNombre}
                  onChange={e=>setPdfNombre(e.target.value)}
                  placeholder={`Fotos_${c.codigoCaso||c.id.slice(0,8)}_${new Date().toISOString().slice(0,10)}`}
                  maxLength={100}
                />
                <span style={{color:'var(--text-muted)',fontSize:12,whiteSpace:'nowrap'}}>.pdf</span>
              </div>
              <div className="p-field-label" style={{marginBottom:8}}>Fotos capturadas ({capturedPhotos.length})</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {capturedPhotos.map((p,i)=>(
                  <div key={i} style={{position:'relative',width:80,height:80,borderRadius:6,overflow:'hidden',border:'1px solid rgba(255,255,255,0.1)'}}>
                    <img src={p.dataUrl} alt={p.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    <button onClick={()=>removePhoto(i)} style={{position:'absolute',top:2,right:2,background:'rgba(0,0,0,0.7)',color:'#fff',border:'none',borderRadius:'50%',width:18,height:18,cursor:'pointer',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>}
        {(c.documentos||[]).length?<table className="p-table"><thead><tr><th>Archivo</th><th>Fecha</th><th>Enlace</th>{edita&&<th></th>}</tr></thead><tbody>
          {(c.documentos||[]).map((d:any,i:number)=>(
            <tr key={i}>
              <td>
                {edita&&editDocIdx===i?(
                  <div style={{display:'flex',alignItems:'center',gap:4}}>
                    <input
                      className="p-input"
                      style={{fontSize:12,padding:'3px 6px',flex:1,minWidth:0}}
                      value={editDocNombre}
                      onChange={e=>setEditDocNombre(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter')confirmRename(i);if(e.key==='Escape')cancelRename();}}
                      autoFocus
                    />
                    <button onClick={()=>confirmRename(i)} style={{background:'rgba(129,199,132,0.15)',border:'1px solid rgba(129,199,132,0.3)',color:'#81C784',borderRadius:4,padding:'2px 7px',cursor:'pointer',fontSize:12}}>✓</button>
                    <button onClick={cancelRename} style={{background:'none',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:4,padding:'2px 7px',cursor:'pointer',fontSize:12}}>✕</button>
                  </div>
                ):(
                  <span>{d.nombre||'—'}</span>
                )}
              </td>
              <td>{d.fecha||'—'}</td>
              <td>{d.link?<a href={d.link} target="_blank" rel="noopener noreferrer" className="p-link">Abrir ↗</a>:'—'}</td>
              {edita&&(
                <td style={{display:'flex',gap:4}}>
                  {editDocIdx!==i&&<button onClick={()=>startRename(i,d.nombre||'')} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:4,padding:'2px 7px',cursor:'pointer',fontSize:12}} title="Renombrar">✏️</button>}
                  <button className="p-btn-danger-sm" onClick={()=>setConfirmDocDel({idx:i,nombre:d.nombre||'este documento'})}>✕</button>
                </td>
              )}
            </tr>
          ))}
        </tbody></table>:<div className="p-empty">No hay documentos registrados.</div>}
        <NotasInternas casoId={c.id} field="notasInternasDocumentos" value={c.notasInternasDocumentos} edita={edita} onRefresh={onRefresh}/>
      </div>
    )}

    {confirmDocDel&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmDocDel(null)}>
        <div style={{background:'var(--dark-2,#1a2235)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:'28px 32px',maxWidth:380,width:'90vw',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:'1.6rem',marginBottom:10}}>🗑️</div>
          <div style={{fontSize:15,color:'var(--cream,#e8e0d0)',marginBottom:8,fontWeight:500}}>Eliminar documento</div>
          <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:22,lineHeight:1.6}}>¿Seguro que desea eliminar <strong style={{color:'var(--cream,#e8e0d0)'}}>{confirmDocDel.nombre}</strong>?<br/>Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmDocDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'var(--text-muted)',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
            <button onClick={()=>delDoc(confirmDocDel.idx)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

    {confirmPagoDel&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmPagoDel(null)}>
        <div style={{background:'var(--dark-2,#1a2235)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:'28px 32px',maxWidth:380,width:'90vw',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:'1.6rem',marginBottom:10}}>🗑️</div>
          <div style={{fontSize:15,color:'var(--cream,#e8e0d0)',marginBottom:8,fontWeight:500}}>Eliminar cuota</div>
          <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:22,lineHeight:1.6}}>¿Seguro que desea eliminar <strong style={{color:'var(--cream,#e8e0d0)'}}>{confirmPagoDel.nombre}</strong>?<br/>Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmPagoDel(null)} style={{padding:'8px 20px',background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',color:'var(--text-muted)',borderRadius:7,cursor:'pointer',fontSize:13}}>Cancelar</button>
            <button onClick={()=>delPago(confirmPagoDel.idx)} style={{padding:'8px 20px',background:'rgba(239,83,80,0.15)',border:'1px solid rgba(239,83,80,0.4)',color:'#ef5350',borderRadius:7,cursor:'pointer',fontSize:13,fontWeight:600}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

    {tab==='pagos'&&(
      <div className="p-section">
        <div className="p-section-title">Estado financiero del caso</div>
        <div className="p-grid3" style={{marginBottom:16}}>
          <div className="fin-card"><div className="fin-label">Total acordado</div><div className="fin-val">{FS.fmtMoney(totalP)}</div></div>
          <div className="fin-card ok"><div className="fin-label">Pagado</div><div className="fin-val">{FS.fmtMoney(pagado)}</div></div>
          <div className={`fin-card ${saldo>0?'warn':'ok'}`}><div className="fin-label">Saldo pendiente</div><div className="fin-val">{FS.fmtMoney(saldo)}</div></div>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:10,marginTop:20,marginBottom:10}}>
          <div className="p-section-title" style={{margin:0}}>Tabla de cuotas</div>
          {(rol==='admin'||rol==='abogada')&&pagos.length>0&&!confirmBorrarCuotas&&(
            <button className="p-btn-danger-sm" onClick={()=>setConfirmBorrarCuotas(true)} style={{fontSize:11,padding:'3px 10px'}}>Eliminar todas</button>
          )}
        </div>
        {confirmBorrarCuotas&&(
          <div style={{marginBottom:12,padding:'12px 16px',background:'rgba(229,115,115,0.08)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:8,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#e57373',flex:1}}>⚠️ ¿Eliminar las {pagos.length} cuota{pagos.length!==1?'s':''} registrada{pagos.length!==1?'s':''}? Esta acción no se puede deshacer.</span>
            <button className="p-btn-secondary" onClick={()=>setConfirmBorrarCuotas(false)} style={{padding:'6px 14px',fontSize:12}}>Cancelar</button>
            <button className="p-btn-danger-sm" onClick={borrarTodasCuotas} style={{padding:'6px 14px',fontSize:12}}>Sí, eliminar todas</button>
          </div>
        )}
        {pagos.length?<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cuota</th><th>Vencimiento</th><th>Valor cuota</th><th>Estado</th><th>Fecha pago</th><th>Forma de pago</th>{edita&&<th></th>}</tr></thead><tbody>
          {pagos.map((p:any,i:number)=>{
            const isAtrasado = p.estado==='Pendiente' && p.vencimiento && (/^\d{4}-\d{2}-\d{2}$/.test(p.vencimiento)?new Date(p.vencimiento+'T00:00:00'):new Date(p.vencimiento)) < new Date();
            const displayEstado = isAtrasado ? 'Atrasado' : (p.estado||'Pendiente');
            return(
              <tr key={i}>
                <td>{p.concepto||`Cuota ${i+1}`}</td>
                <td>{FS.fmtDate(p.vencimiento||p.fecha)||'—'}</td>
                <td>{FS.fmtMoney(p.monto)}</td>
                <td>
                  {edita ? (
                    <select className="p-select" value={displayEstado} onChange={e=>changePagoEstado(i,e.target.value)} style={{fontSize:12,padding:'3px 6px',minWidth:110,background:displayEstado==='Pagado'?'rgba(76,175,80,0.1)':displayEstado==='Atrasado'?'rgba(255,152,0,0.1)':'transparent',borderColor:displayEstado==='Pagado'?'rgba(76,175,80,0.3)':displayEstado==='Atrasado'?'rgba(255,152,0,0.3)':'rgba(255,255,255,0.1)'}}>
                      <option>Pendiente</option><option>Pagado</option><option>Atrasado</option>
                    </select>
                  ) : (
                    <span className={`pago-badge ${displayEstado==='Pagado'?'ok':displayEstado==='Atrasado'?'warn':'neutral'}`}>{displayEstado}</span>
                  )}
                </td>
                <td>
                  {edita && (p.estado==='Pagado'||displayEstado==='Pagado') ? (
                    <input className="p-input" type="date" value={p.fechaPago||''} onChange={e=>changePagoFechaPago(i,e.target.value)} style={{fontSize:12,padding:'3px 6px',width:130}}/>
                  ) : (
                    <span style={{fontSize:12,color:'var(--text-muted)'}}>{FS.fmtDate(p.fechaPago)||'—'}</span>
                  )}
                </td>
                <td style={{color:'var(--text-muted)',fontSize:12}}>{p.formaPago||'—'}</td>
                {edita&&<td><button className="p-btn-danger-sm" onClick={()=>setConfirmPagoDel({idx:i,nombre:p.concepto||`Cuota ${i+1}`})}>✕</button></td>}
              </tr>
            );
          })}
        </tbody></table></div>:<div className="p-empty">Sin pagos registrados.</div>}

        {edita&&<>
          {/* Auto-generate cuotas */}
          <div style={{marginTop:16,background:'rgba(255,255,255,0.03)',borderRadius:8,padding:16,border:'1px solid rgba(255,255,255,0.06)',marginBottom:16}}>
            <div className="p-section-title" style={{marginBottom:8}}>⚡ Generar cuotas automáticamente</div>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
              <div className="p-field" style={{margin:0,flex:'0 0 120px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>N° de cuotas</div>
                <input className="p-input" type="number" min="1" max="60" value={autoCuotas} onChange={e=>setAutoCuotas(e.target.value)} placeholder="Ej: 6"/>
              </div>
              <div className="p-field" style={{margin:0,flex:'0 0 150px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Valor cada cuota ($)</div>
                <input className="p-input" type="text" value={autoMonto} onChange={e=>setAutoMonto(e.target.value.replace(/[^0-9]/g,''))} placeholder="Ej: 200000" inputMode="numeric"/>
              </div>
              <div className="p-field" style={{margin:0,flex:'0 0 160px'}}>
                <div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Vencimiento 1ª cuota</div>
                <input className="p-input" type="date" value={autoVencInicio} onChange={e=>setAutoVencInicio(e.target.value)}/>
              </div>
              <button className="p-btn-primary" onClick={generarCuotas} disabled={!autoCuotas||!autoMonto} style={{padding:'8px 16px',height:38}}>
                Generar {autoCuotas?autoCuotas+' cuotas':''}
              </button>
            </div>
            {autoCuotas && autoMonto && <div style={{fontSize:12,color:'var(--gold)',marginTop:8}}>Total: {FS.fmtMoney(parseInt(autoCuotas)*Number(autoMonto))} en {autoCuotas} cuotas de {FS.fmtMoney(Number(autoMonto))}</div>}
            {confirmGenerar&&(
              <div style={{marginTop:12,padding:'12px 16px',background:'rgba(229,115,115,0.08)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:8,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                <span style={{fontSize:13,color:'#e57373',flex:1}}>⚠️ Ya existen {(c.pagos||[]).length} cuota{(c.pagos||[]).length!==1?'s':''} registrada{(c.pagos||[]).length!==1?'s':''}. ¿Generar {autoCuotas} cuota{parseInt(autoCuotas)!==1?'s':''} adicional{parseInt(autoCuotas)!==1?'es':''}?</span>
                <button className="p-btn-secondary" onClick={()=>setConfirmGenerar(false)} style={{padding:'6px 14px',fontSize:12}}>Cancelar</button>
                <button className="p-btn-primary" onClick={ejecutarGenerarCuotas} style={{padding:'6px 14px',fontSize:12,background:'#e57373',borderColor:'#e57373'}}>Sí, generar igual</button>
              </div>
            )}
          </div>

          <div style={{marginTop:0}}>
          <div className="p-section-title" style={{marginBottom:8}}>Registrar cuota individual</div>
          <div className="p-grid3" style={{marginBottom:12}}>
            <input className="p-input" placeholder="Cuota (ej: Cuota 1)" value={pConc} onChange={e=>setPConc(e.target.value)}/>
            <input className="p-input" type="number" placeholder="Valor cuota ($)" value={pMon} onChange={e=>setPMon(e.target.value)}/>
            <div className="p-field" style={{margin:0}}><div className="p-field-label" style={{fontSize:10,marginBottom:2}}>Vencimiento</div><input className="p-input" type="date" value={pVenc} onChange={e=>setPVenc(e.target.value)}/></div>
            <select className="p-select" value={pEst} onChange={e=>setPEst(e.target.value)}><option>Pendiente</option><option>Pagado</option><option>Atrasado</option></select>
            <select className="p-select" value={pForma} onChange={e=>setPForma(e.target.value)}><option>Transferencia</option><option>Efectivo</option><option>Tarjeta de crédito</option><option>Tarjeta de débito</option><option>Cheque</option><option>Otro</option></select>
            <input className="p-input" placeholder="Nota (opcional)" value={pNot} onChange={e=>setPNot(e.target.value)}/>
          </div>
          <button className="p-btn-primary" onClick={addPago}>＋ Registrar cuota</button>
        </div>
        </>}
        <NotasInternas casoId={c.id} field="notasInternasPagos" value={c.notasInternasPagos} edita={edita} onRefresh={onRefresh}/>
      </div>
    )}

    {confirmDel&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmDel(null)}>
        <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'24px 28px',maxWidth:380,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:28,marginBottom:8}}>🗑️</div>
          <div style={{fontSize:14,color:'var(--cream,#e8e0d0)',marginBottom:6,fontWeight:500}}>¿Eliminar elemento?</div>
          <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:20,lineHeight:1.5}}>Se eliminará {confirmDel.msg}. Esta acción no se puede deshacer.</div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setConfirmDel(null)} style={{padding:'8px 20px',fontSize:13,fontWeight:600,color:'#fff',background:'#2563EB',border:'none',borderRadius:7,cursor:'pointer'}}>Cancelar</button>
            <button onClick={()=>{confirmDel.onConfirm();setConfirmDel(null);}} style={{padding:'8px 18px',fontSize:13,fontWeight:500,color:'#f28b8b',background:'none',border:'1px solid rgba(229,115,115,0.4)',borderRadius:7,cursor:'pointer'}}>Eliminar</button>
          </div>
        </div>
      </div>
    )}

    {checkSinPlazo&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setCheckSinPlazo(null)}>
        <div style={{background:'var(--dark-3,#141d2e)',border:'1px solid rgba(212,175,55,0.35)',borderRadius:10,padding:'24px 28px',maxWidth:400,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:28,marginBottom:8}}>⏰</div>
          <div style={{fontSize:14,color:'var(--cream,#e8e0d0)',marginBottom:10,fontWeight:500}}>Falta fecha límite</div>
          <div style={{fontSize:13,color:'var(--text-body,#8a9bb5)',marginBottom:6,lineHeight:1.6}}>
            La tarea tiene una mención pero no incluye <strong style={{color:'var(--gold,#d4af37)'}}>@plazo</strong> con fecha.<br/>
            Sin el plazo no se enviará notificación.
          </div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:20,fontStyle:'italic'}}>
            Ej: @Jans @plazo 28-05-2026 revisar contrato
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'center'}}>
            <button onClick={()=>setCheckSinPlazo(null)} style={{padding:'8px 20px',fontSize:13,fontWeight:600,color:'#fff',background:'#2563EB',border:'none',borderRadius:7,cursor:'pointer'}}>Agregar @plazo</button>
            <button onClick={()=>{setCheckSinPlazo(null);addCheck(true);}} style={{padding:'8px 18px',fontSize:13,fontWeight:500,color:'var(--text-muted)',background:'none',border:'1px solid rgba(138,155,181,0.3)',borderRadius:7,cursor:'pointer'}}>Guardar sin notificar</button>
          </div>
        </div>
      </div>
    )}
  </>);
};

/* ══════════════════════════════════════════════════════════════════════════
   NUEVO CASO
   ══════════════════════════════════════════════════════════════════════════ */
const NuevoCaso: React.FC<{rol:string;user:any;userData:any;onBack:()=>void}> = ({rol,user,userData,onBack}) => {
  const DRAFT_KEY = 'nuevoCaso_draft_' + user.uid;
  const DEFAULT_FORM = {tema:'Derecho Laboral',abogadaId:'',abogadaNombre:'',clienteWeb:false,familiaMaterias:[],laboralMotivos:[],nacionalidad:'Chilena',telDigits:'',evaluacionInterna:''};
  const [f,setF]=useState<any>(()=>{try{const d=localStorage.getItem(DRAFT_KEY);return d?JSON.parse(d):DEFAULT_FORM;}catch{return DEFAULT_FORM;}});
  const [draftRestored,setDraftRestored]=useState(()=>{try{return !!localStorage.getItem(DRAFT_KEY);}catch{return false;}});
  const [abogadas,setAbogadas]=useState<any[]>([]);
  const [captadoras,setCaptadoras]=useState<any[]>([]);
  const [msg,setMsg]=useState('');
  const [errors,setErrors]=useState<Record<string,string>>({});
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    FS.loadAbogadas().then(setAbogadas).catch(()=>{});
    if(rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora') FS.loadCaptadoras().then(setCaptadoras).catch(()=>{});
  },[]);

  useEffect(()=>{
    const timer=setTimeout(()=>{
      try{
        const strip=(s:string)=>(s||'').replace(/<[^>]*>/g,'').trim();
        const hasContent=!!(f.nombre||f.rut||f.correo||f.dir||f.contra||strip(f.evaluacion)||strip(f.nota)||f.procedimiento||f.rolCaso||f.empleador||strip(f.evaluacionInterna));
        if(hasContent) localStorage.setItem(DRAFT_KEY,JSON.stringify(f));
        else localStorage.removeItem(DRAFT_KEY);
      }catch{}
    },800);
    return ()=>clearTimeout(timer);
  },[f]);

  const [transformando,setTransformando]=useState(false);
  const [expandEvalInterna,setExpandEvalInterna]=useState(false);
  const transformarConIA=async()=>{
    const raw=(f.evaluacionInterna||'').replace(/<[^>]*>/g,'').trim();
    if(!raw) return;
    setTransformando(true);setMsg('');
    try{
      const fn=httpsCallable(functions,'transformarEvaluacion');
      const res:any=await fn({texto:raw});
      const resultado=DOMPurify.sanitize((res.data.resultado||'').replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>'));
      setF((prev:any)=>({...prev,evaluacion:resultado}));
    }catch(e:any){setMsg('Error IA: '+(e.message||'Intente nuevamente'));}
    finally{setTransformando(false);}
  };

  const toggleChecklistItem=(list:string,item:string)=>{
    const current=f[list]||[];
    const updated=current.includes(item)?current.filter((x:string)=>x!==item):[...current,item];
    setF({...f,[list]:updated});
  };

  const guardar=async()=>{
    if(saving) return;
    const errs:Record<string,string>={};
    if(!f.nombre) errs.nombre='Campo obligatorio';
    if(!f.rut) errs.rut='Campo obligatorio';
    if(!f.telDigits) errs.tel='Campo obligatorio';
    else if(!validatePhone(f.telDigits)) errs.tel='Debe ingresar exactamente 8 dígitos';
    if(debeSeleccionarCaptadora&&!f.captadoraSelId) errs.captadora='Seleccione una captadora';
    if(Object.keys(errs).length){setErrors(errs);setMsg('');setTimeout(()=>document.querySelector<HTMLElement>('.input-error')?.scrollIntoView({behavior:'smooth',block:'center'}),50);return;}
    setErrors({});setMsg('Guardando...');setSaving(true);
    try{
      let captadora:string, captadoraId:string;
      if(f.clienteWeb){captadora='WEB';captadoraId='web';}
      else if((rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora')&&f.captadoraSelId){captadora=f.captadoraSelNombre;captadoraId=f.captadoraSelId;}
      else{captadora=userData?.nombre||user.email;captadoraId=user.uid;}
      const {codigo,id:casoId}=await FS.crearCaso({
        nombreCliente:f.nombre,rut:formatRut(f.rut),telefono:'+569'+f.telDigits.replace(/\D/g,''),correo:f.correo||'',direccion:f.dir||'',
        estadoCivil:f.estadoCivil||'',nacionalidad:f.nacionalidad||'Chilena',ocupacion:f.ocup||'',tema:f.tema,
        abogada:f.abogadaNombre,abogadaId:f.abogadaId,
        contraparte:f.contra||'',evaluacion:f.evaluacion||'',notaInterna:f.nota||'',evaluacionInterna:f.evaluacionInterna||'',
        procedimiento:f.procedimiento||'',rolCaso:f.rolCaso||'',
        captadora,captadoraId,clienteWeb:f.clienteWeb,
        familiaMaterias:f.tema==='Derecho de Familia'?f.familiaMaterias:[],
        laboralMotivos:f.tema==='Derecho Laboral'?f.laboralMotivos:[],
        empleador:f.tema==='Derecho Laboral'?(f.empleador||''):'',
        rutEmpleador:f.tema==='Derecho Laboral'?(f.rutEmpleador?formatRut(f.rutEmpleador):''):'',
        representanteLegal:f.tema==='Derecho Laboral'?(f.representanteLegal||''):'',
        domicilioEmpleador:f.tema==='Derecho Laboral'?(f.domicilioEmpleador||''):'',
        fechaDespido:f.tema==='Derecho Laboral'?(f.fechaDespido||''):'',
      });
      try{localStorage.removeItem(DRAFT_KEY);}catch{}
      setMsg('✓ Caso creado. Código: '+codigo);
      setTimeout(onBack,1800);
    }catch(e:any){setMsg('Error: '+e.message);setSaving(false);}
  };

  const phoneDigits = (f.telDigits||'').replace(/\D/g,'');
  const phoneLen = phoneDigits.length;
  const debeSeleccionarCaptadora = (rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora')&&!f.clienteWeb;

  return(
    <div className="p-section">
      <div className="p-section-title">Ingresar nuevo caso</div>
      {draftRestored&&(
        <div style={{background:'rgba(212,175,55,0.1)',border:'1px solid rgba(212,175,55,0.35)',borderRadius:8,padding:'10px 16px',marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,fontSize:13,color:'var(--white)'}}>
          <span>📋 Se recuperó un borrador no guardado. Revisa los datos antes de guardar.</span>
          <button style={{background:'transparent',border:'1px solid rgba(212,175,55,0.5)',color:'var(--gold)',borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>{if(!window.confirm('¿Descartar este borrador? Se perderán los datos no guardados y esto no se puede deshacer.'))return;setF(DEFAULT_FORM);setDraftRestored(false);try{localStorage.removeItem(DRAFT_KEY);}catch{}}}>Descartar borrador</button>
        </div>
      )}
      <div className="p-grid2">
        <div>
          <div className="p-sub-title" style={{marginBottom:10}}>Datos del cliente</div>

          <div className="p-field"><div className="p-field-label">Nombre completo *</div>
            <input className={`p-input ${errors.nombre?'input-error':''}`} value={f.nombre||''} onChange={e=>{if(errors.nombre)setErrors(prev=>({...prev,nombre:''}));setF({...f,nombre:e.target.value});}} placeholder="Nombre completo"/>
            {errors.nombre&&<div className="field-error">{errors.nombre}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">RUT * <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:400}}>Se formatea automáticamente</span></div>
            <input className={`p-input ${errors.rut?'input-error':''}`} value={f.rut||''} onChange={e=>{if(errors.rut)setErrors(prev=>({...prev,rut:''}));const c=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);setF({...f,rut:c.length>=2?formatRut(c):c});}} placeholder="Ej: 12.345.678-9"/>
            {errors.rut&&<div className="field-error">{errors.rut}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">Teléfono / WhatsApp *</div>
            <div style={{display:'flex',alignItems:'center',gap:0}}>
              <div style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRight:'none',borderRadius:'6px 0 0 6px',padding:'8px 10px',fontSize:14,color:'var(--white)',fontWeight:600,letterSpacing:0.5,whiteSpace:'nowrap'}}>+569</div>
              <input className={`p-input ${errors.tel?'input-error':''}`} style={{borderRadius:'0 6px 6px 0',flex:1}} value={f.telDigits||''} onChange={e=>{if(errors.tel)setErrors(prev=>({...prev,tel:''}));const v=e.target.value.replace(/\D/g,'').slice(0,8);setF({...f,telDigits:v});}} placeholder="8 dígitos" maxLength={8} inputMode="numeric"/>
            </div>
            {phoneLen>0 && phoneLen!==8 && <div className="field-error">{phoneLen<8?`Faltan ${8-phoneLen} dígitos`:'Máximo 8 dígitos'}</div>}
            {errors.tel&&phoneLen===0&&<div className="field-error">{errors.tel}</div>}
          </div>

          <div className="p-field"><div className="p-field-label">Correo electrónico</div><input className="p-input" type="email" value={f.correo||''} onChange={e=>setF({...f,correo:e.target.value})}/></div>
          <div className="p-field"><div className="p-field-label">Dirección</div><input className="p-input" value={f.dir||''} onChange={e=>setF({...f,dir:e.target.value})}/></div>

          <div className="p-field"><div className="p-field-label">Estado civil</div>
            <select className="p-select" value={f.estadoCivil||''} onChange={e=>setF({...f,estadoCivil:e.target.value})}>
              <option value="">Seleccionar...</option><option>Soltero/a</option><option>Casado/a</option><option>Divorciado/a</option><option>Viudo/a</option><option>Conviviente civil</option><option>Separado/a</option>
            </select>
          </div>

          <div className="p-field"><div className="p-field-label">Nacionalidad</div>
            <select className="p-select" value={f.nacionalidad||'Chilena'} onChange={e=>setF({...f,nacionalidad:e.target.value})}>
              <option>Chilena</option><option>Argentina</option><option>Boliviana</option><option>Brasileña</option><option>Colombiana</option><option>Ecuatoriana</option><option>Haitiana</option><option>Peruana</option><option>Venezolana</option><option>Dominicana</option><option>Cubana</option><option>Mexicana</option><option>Española</option><option>Otra</option>
            </select>
            {f.nacionalidad==='Otra'&&<input className="p-input" style={{marginTop:6}} placeholder="Especificar nacionalidad" onChange={e=>setF({...f,nacionalidad:e.target.value})}/>}
          </div>

          <div className="p-field"><div className="p-field-label">Ocupación</div><input className="p-input" value={f.ocup||''} onChange={e=>setF({...f,ocup:e.target.value})}/></div>
        </div>

        <div>
          <div className="p-sub-title" style={{marginBottom:10}}>Datos del caso</div>

          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,marginBottom:14,border:'1px solid rgba(255,255,255,0.06)'}}>
            <input type="checkbox" checked={f.clienteWeb||false} onChange={e=>setF({...f,clienteWeb:e.target.checked})} id="clienteWeb" style={{width:18,height:18}}/>
            <label htmlFor="clienteWeb" style={{cursor:'pointer',fontSize:13,color:'var(--white)'}}>🌐 Cliente llegó por página web</label>
          </div>

          <div className="p-field"><div className="p-field-label">Tema del caso *</div>
            <select className="p-select" value={f.tema} onChange={e=>setF({...f,tema:e.target.value,familiaMaterias:[],laboralMotivos:[]})}>
              <option>Derecho Laboral</option><option>Derecho de Familia</option><option>Derecho Tributario</option><option>Derecho Migratorio</option><option>Derecho Civil</option><option>Derecho Penal</option><option>Derecho Administrativo</option><option>Otro</option>
            </select>
          </div>

          {f.tema==='Derecho de Familia' && (
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.06)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Materia</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px'}}>
                {FAMILIA_MATERIAS.map(m=>(
                  <label key={m} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'5px 0'}}>
                    <input type="checkbox" checked={(f.familiaMaterias||[]).includes(m)} onChange={()=>toggleChecklistItem('familiaMaterias',m)} style={{width:16,height:16,flexShrink:0}}/>
                    <span style={{fontSize:13,color:'var(--white)'}}>{m}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {f.tema==='Derecho Laboral' && (
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.06)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Motivo principal consulta <span style={{fontWeight:400,fontSize:11,color:'var(--text-muted)'}}>(puede seleccionar varios)</span></div>
              <div style={{display:'flex',flexDirection:'column',gap:0}}>
                {LABORAL_MOTIVOS.map((m,i)=>(
                  <label key={m} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'8px 0',borderBottom:i<LABORAL_MOTIVOS.length-1?'1px solid rgba(255,255,255,0.04)':'none'}}>
                    <input type="checkbox" checked={(f.laboralMotivos||[]).includes(m)} onChange={()=>toggleChecklistItem('laboralMotivos',m)} style={{width:16,height:16,flexShrink:0}}/>
                    <span style={{fontSize:13,color:'var(--white)',lineHeight:1.4}}>{m}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {f.tema==='Derecho Laboral' && (
            <div style={{background:'rgba(255,255,255,0.02)',borderRadius:8,padding:'14px 16px',border:'1px solid rgba(255,255,255,0.05)',marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:10}}>Datos laborales</div>
              <div className="p-field"><div className="p-field-label">Empleador</div><input className="p-input" value={f.empleador||''} onChange={e=>setF({...f,empleador:e.target.value})} placeholder="Nombre empresa o empleador"/></div>
              <div className="p-field"><div className="p-field-label">RUT Empleador</div><input className="p-input" value={f.rutEmpleador||''} onChange={e=>{const c=e.target.value.replace(/[^0-9kK]/g,'').toUpperCase().slice(0,9);setF({...f,rutEmpleador:c.length>=2?formatRut(c):c});}} placeholder="Ej: 12.345.678-9"/></div>
              <div className="p-field"><div className="p-field-label">Representante legal</div><input className="p-input" value={f.representanteLegal||''} onChange={e=>setF({...f,representanteLegal:e.target.value})} placeholder="Nombre del representante legal"/></div>
              <div className="p-field"><div className="p-field-label">Domicilio empleador</div><input className="p-input" value={f.domicilioEmpleador||''} onChange={e=>setF({...f,domicilioEmpleador:e.target.value})} placeholder="Dirección de la empresa"/></div>
              <div className="p-field"><div className="p-field-label">Fecha de despido</div><input className="p-input" type="date" value={f.fechaDespido||''} onChange={e=>setF({...f,fechaDespido:e.target.value})}/></div>
            </div>
          )}

          <div className="p-field"><div className="p-field-label">Abogada asignada</div><select className="p-select" value={f.abogadaId||''} onChange={e=>{const idx=e.target.selectedIndex;setF({...f,abogadaId:e.target.value,abogadaNombre:e.target.options[idx]?.text||''});}}><option value="">Sin asignar</option>{abogadas.map((a:any)=><option key={a.id} value={a.id}>{a.nombre||a.email}</option>)}</select></div>

          <div className="p-field"><div className="p-field-label">Captadora{debeSeleccionarCaptadora?' *':''}</div>
            {debeSeleccionarCaptadora?(
              <>
                <select className={`p-select ${errors.captadora?'input-error':''}`} value={f.captadoraSelId||''} onChange={e=>{if(errors.captadora)setErrors(prev=>({...prev,captadora:''}));const opt=e.target.options[e.target.selectedIndex];setF({...f,captadoraSelId:e.target.value,captadoraSelNombre:opt?.text||''});}}>
                  <option value="">— Seleccionar captadora —</option>
                  {captadoras.map((c:any)=><option key={c.id} value={c.id}>{c.nombre||c.email}</option>)}
                </select>
                {errors.captadora&&<div className="field-error">{errors.captadora}</div>}
              </>
            ):(
              <input className="p-input" value={f.clienteWeb?'WEB':(userData?.nombre||user.email)} readOnly style={{opacity:0.7}}/>
            )}
          </div>

          <div className="p-field"><div className="p-field-label">Procedimiento</div><input className="p-input" value={f.procedimiento||''} onChange={e=>setF({...f,procedimiento:e.target.value})} placeholder="Ej: Demanda, Mediación, etc."/></div>
          <div className="p-field"><div className="p-field-label">Rol</div><input className="p-input" value={f.rolCaso||''} onChange={e=>setF({...f,rolCaso:e.target.value})} placeholder="Ej: Demandante, Demandado"/></div>

          <div className="p-field"><div className="p-field-label">Contraparte</div><textarea className="p-input" rows={3} value={f.contra||''} onChange={e=>setF({...f,contra:e.target.value})} style={{resize:'vertical'}} placeholder="Puede agregar varias líneas con Enter"/></div>
          {(rol==='admin'||rol==='abogada'||rol==='abogada1'||rol==='procuradora')&&(
            <div className="p-field">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                <div className="p-field-label" style={{marginBottom:0}}>Evaluación interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Borrador rápido · Solo equipo</span></div>
                <button type="button" onClick={()=>setExpandEvalInterna(true)} style={{background:'none',border:'1px solid rgba(255,255,255,0.2)',color:'var(--text-muted)',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:12,whiteSpace:'nowrap'}}>⛶ Expandir</button>
              </div>
              <RichText value={f.evaluacionInterna||''} onChange={v=>setF({...f,evaluacionInterna:v})} placeholder="Anote aquí de forma libre y rápida los antecedentes del caso durante la consulta. Puede usar lenguaje informal, abreviaturas, etc." minHeight={130}/>
              <button type="button" onClick={transformarConIA} disabled={transformando||!((f.evaluacionInterna||'').replace(/<[^>]*>/g,'').trim())} style={{marginTop:8,padding:'8px 0',background:transformando?'rgba(255,255,255,0.04)':'rgba(212,175,55,0.12)',border:'1px solid rgba(212,175,55,0.4)',color:'var(--gold)',borderRadius:6,cursor:transformando?'wait':'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:6,width:'100%',opacity:transformando||!((f.evaluacionInterna||'').replace(/<[^>]*>/g,'').trim())?0.5:1,transition:'all 0.2s'}}>
                {transformando?'⏳ Procesando con IA…':'✨ Transformar con IA → Evaluación para cliente'}
              </button>
            </div>
          )}
          {/* Modal expandir evaluación interna */}
          {expandEvalInterna&&(
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={()=>setExpandEvalInterna(false)}>
              <div style={{background:'var(--panel-bg,#1a1a2e)',borderRadius:12,width:'100%',maxWidth:760,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 8px 40px rgba(0,0,0,0.6)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                  <span style={{fontWeight:600,color:'var(--white)',fontSize:15}}>Evaluación interna <span style={{fontSize:12,color:'var(--text-muted)',fontWeight:400}}>— Solo equipo</span></span>
                  <button type="button" onClick={()=>setExpandEvalInterna(false)} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:20,cursor:'pointer',lineHeight:1}}>✕</button>
                </div>
                <div style={{flex:1,overflow:'auto',padding:20}}>
                  <RichText value={f.evaluacionInterna||''} onChange={v=>setF((prev:any)=>({...prev,evaluacionInterna:v}))} placeholder="Anote aquí de forma libre y rápida los antecedentes del caso durante la consulta." minHeight={400}/>
                </div>
                <div style={{padding:'12px 20px',borderTop:'1px solid rgba(255,255,255,0.08)',display:'flex',justifyContent:'flex-end'}}>
                  <button type="button" onClick={()=>setExpandEvalInterna(false)} className="p-btn-primary" style={{padding:'8px 24px'}}>Listo</button>
                </div>
              </div>
            </div>
          )}
          <div className="p-field">
            <div style={{border:'1px solid rgba(220,50,50,0.55)',borderRadius:10,padding:'12px 14px',background:'rgba(220,50,50,0.07)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                <span style={{fontSize:16,lineHeight:1}}>⚠️</span>
                <span style={{fontSize:12,color:'#ff8a80',lineHeight:1.4}}><b>Visible para el cliente</b> — Revisa antes de guardar.</span>
              </div>
              <div className="p-field-label" style={{marginBottom:6}}>Evaluación <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>— Visible para el cliente</span></div>
              <RichText value={f.evaluacion||''} onChange={v=>setF({...f,evaluacion:v})} placeholder="Evaluación profesional del caso (se mostrará al cliente)" minHeight={120}/>
            </div>
          </div>
          <div className="p-field">
            <div className="p-field-label">Nota interna <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:400}}>Solo equipo</span></div>
            <RichText value={f.nota||''} onChange={v=>setF({...f,nota:v})} placeholder="Notas internas del equipo" minHeight={80}/>
          </div>
        </div>
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:16,gap:10}}>
        <button className="p-btn-secondary" onClick={onBack}>Cancelar</button>
        <button className="p-btn-primary" onClick={guardar} disabled={saving} style={{padding:'10px 24px',opacity:saving?0.7:1,cursor:saving?'not-allowed':'pointer'}}>
          {saving?'Guardando…':'Guardar caso →'}
        </button>
      </div>
      {msg&&<div style={{marginTop:10,fontSize:13,color:'var(--gold)',textAlign:'right'}}>{msg}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   USUARIOS
   ══════════════════════════════════════════════════════════════════════════ */
// No se incluye 'admin': un administrador solo puede crearse desde la consola
// de Firebase, nunca desde el panel.
const ROLES_USUARIO = [
  {value:'captadora',   label:'Captadora'},
  {value:'procuradora', label:'Procuradora'},
  {value:'abogada1',    label:'Abogada asociada'},
  {value:'abogada',     label:'Abogada'},
];

const Usuarios:React.FC<{rol?:string}>=({rol})=>{
  const [users,setUsers]=useState<any[]>([]);
  const [showForm,setShowForm]=useState(false);
  const [f,setF]=useState({nombre:'',email:'',password:'',rol:'captadora'});
  const [creating,setCreating]=useState(false);
  const [msg,setMsg]=useState('');
  const [err,setErr]=useState('');
  const [credModal,setCredModal]=useState<{uid:string,nombre:string,email:string}|null>(null);
  const [sendingReset,setSendingReset]=useState(false);
  const [resetMsg,setResetMsg]=useState('');
  const [resetErr,setResetErr]=useState('');

  const refresh=()=>FS.loadUsuarios().then(setUsers).catch(()=>{});
  useEffect(()=>{refresh();},[]);

  const crearUsuarioFn=httpsCallable(functions,'crearUsuario');
  const enviarRecuperacionFn=httpsCallable(functions,'enviarRecuperacionPassword');
  const desactivarFn=httpsCallable(functions,'desactivarUsuario');
  const reactivarFn=httpsCallable(functions,'reactivarUsuario');
  const [togglingUid,setTogglingUid]=useState<string|null>(null);

  const handleCreate=async(e:React.FormEvent)=>{
    e.preventDefault();
    if(!f.nombre.trim()||!f.email.trim()||!f.password){setErr('Completa todos los campos.');return;}
    setCreating(true);setErr('');setMsg('');
    try{
      await crearUsuarioFn(f);
      setMsg(`Usuario "${f.nombre}" creado correctamente.`);
      setShowForm(false);
      setF({nombre:'',email:'',password:'',rol:'captadora'});
      refresh();
    }catch(e:any){
      setErr(e.message||'Error al crear usuario.');
    }finally{setCreating(false);}
  };

  const abrirCredencial=(uid:string,nombre:string,email:string)=>{
    setCredModal({uid,nombre,email});
    setResetMsg('');
    setResetErr('');
  };

  const handleEnviarRecuperacion=async()=>{
    if(!credModal)return;
    setSendingReset(true);setResetMsg('');setResetErr('');
    try{
      await enviarRecuperacionFn({uid:credModal.uid});
      setResetMsg('Enlace de recuperación enviado al correo del usuario.');
    }catch(e:any){
      setResetErr(e.message||'Error al enviar el enlace de recuperación.');
    }finally{setSendingReset(false);}
  };

  const handleDesvincular=async(uid:string,nombre:string)=>{
    if(!window.confirm(`¿Desvincular a "${nombre}"? No podrá acceder al sistema hasta que sea reactivado.`))return;
    setTogglingUid(uid);
    try{ await desactivarFn({uid}); refresh(); }
    catch(e:any){ alert('Error: '+(e.message||'No se pudo desvincular.')); }
    finally{ setTogglingUid(null); }
  };

  const handleReactivar=async(uid:string)=>{
    setTogglingUid(uid);
    try{ await reactivarFn({uid}); refresh(); }
    catch(e:any){ alert('Error: '+(e.message||'No se pudo reactivar.')); }
    finally{ setTogglingUid(null); }
  };

  const copiar=(texto:string)=>navigator.clipboard.writeText(texto).catch(()=>{});

  return(
    <div className="p-section">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <div className="p-section-title" style={{marginBottom:0}}>Equipo del estudio</div>
        {rol==='admin'&&(
          <button className="p-btn-primary" onClick={()=>{setShowForm(v=>!v);setMsg('');setErr('');}}>
            {showForm?'× Cancelar':'+ Nuevo usuario'}
          </button>
        )}
      </div>

      {msg&&<div style={{color:'#81C784',fontSize:13,marginBottom:12,padding:'8px 12px',background:'rgba(129,199,132,0.08)',borderRadius:6}}>✓ {msg}</div>}

      {showForm&&rol==='admin'&&(
        <form onSubmit={handleCreate} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.09)',borderRadius:8,padding:20,marginBottom:20}}>
          <div className="p-section-title" style={{marginBottom:14,fontSize:13}}>Crear nuevo usuario</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="p-field">
              <div className="p-field-label">Nombre completo</div>
              <input className="p-input" value={f.nombre} onChange={e=>setF({...f,nombre:e.target.value})} placeholder="Ej: María González" required/>
            </div>
            <div className="p-field">
              <div className="p-field-label">Correo electrónico</div>
              <input className="p-input" type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} placeholder="correo@ejemplo.cl" required/>
            </div>
            <div className="p-field">
              <div className="p-field-label">Contraseña inicial</div>
              <input className="p-input" type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} placeholder="Mínimo 12 caracteres" required minLength={12}/>
            </div>
            <div className="p-field">
              <div className="p-field-label">Rol</div>
              <select className="p-select" value={f.rol} onChange={e=>setF({...f,rol:e.target.value})}>
                {ROLES_USUARIO.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:10}}>
            El rol de administrador no puede asignarse desde el panel. Para crear otro administrador, hazlo desde la consola de Firebase.
          </div>
          {err&&<div style={{color:'#ef5350',fontSize:13,marginTop:10}}>{err}</div>}
          <div style={{marginTop:14}}>
            <button type="submit" className="p-btn-primary" disabled={creating} style={{opacity:creating?0.7:1,cursor:creating?'not-allowed':'pointer'}}>
              {creating?'Creando…':'Crear usuario →'}
            </button>
          </div>
        </form>
      )}

      <table className="p-table">
        <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Estado</th>{rol==='admin'&&<th>Acciones</th>}</tr></thead>
        <tbody>
          {users.map(u=>{
            const activo=u.activo!==false;
            return(
              <tr key={u.id} style={{opacity:activo?1:0.6}}>
                <td>{u.nombre||'—'}</td>
                <td style={{color:'var(--text-muted)'}}>{u.email||'—'}</td>
                <td><span className="doc-tipo-badge">{u.rol||'—'}</span></td>
                <td><span className={`pago-badge ${activo?'ok':'atrasado'}`}>{activo?'Activo':'Inactivo'}</span></td>
                {rol==='admin'&&(
                  <td style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    <button
                      onClick={()=>abrirCredencial(u.id,u.nombre||u.email,u.email||'')}
                      style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer'}}
                    >Ver acceso</button>
                    {activo?(
                      <button
                        onClick={()=>handleDesvincular(u.id,u.nombre||u.email)}
                        disabled={togglingUid===u.id}
                        style={{background:'rgba(239,83,80,0.12)',border:'1px solid rgba(239,83,80,0.3)',color:'#ef5350',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer',opacity:togglingUid===u.id?0.6:1}}
                      >{togglingUid===u.id?'…':'Desvincular'}</button>
                    ):(
                      <button
                        onClick={()=>handleReactivar(u.id)}
                        disabled={togglingUid===u.id}
                        style={{background:'rgba(129,199,132,0.12)',border:'1px solid rgba(129,199,132,0.3)',color:'#81C784',borderRadius:5,padding:'4px 10px',fontSize:12,cursor:'pointer',opacity:togglingUid===u.id?0.6:1}}
                      >{togglingUid===u.id?'…':'Reactivar'}</button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {credModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={()=>setCredModal(null)}>
          <div style={{background:'#1a2235',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,padding:28,width:380,maxWidth:'90vw'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <div style={{fontWeight:600,fontSize:14,color:'var(--text-primary)'}}>Credenciales de acceso</div>
              <button onClick={()=>setCredModal(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:16}}>{credModal.nombre}</div>

            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Usuario (correo)</div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  <span style={{fontSize:13,color:'var(--text-primary)',wordBreak:'break-all'}}>{credModal.email}</span>
                  <button onClick={()=>copiar(credModal.email)} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.12)',color:'var(--text-muted)',borderRadius:5,padding:'3px 8px',fontSize:11,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>Copiar</button>
                </div>
              </div>

              <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:14,marginTop:2}}>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>
                  Contraseña
                </div>
                <button
                  onClick={handleEnviarRecuperacion}
                  disabled={sendingReset}
                  className="p-btn-primary"
                  style={{width:'100%',opacity:sendingReset?0.6:1,cursor:sendingReset?'not-allowed':'pointer',fontSize:13}}
                >
                  {sendingReset?'Enviando…':'Enviar enlace de recuperación por correo'}
                </button>
                {resetErr&&<div style={{color:'#ef5350',fontSize:12,marginTop:6}}>{resetErr}</div>}
                {resetMsg&&<div style={{color:'#81C784',fontSize:12,marginTop:6}}>✓ {resetMsg}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   INGRESO WEB
   ══════════════════════════════════════════════════════════════════════════ */
const IngresoWeb:React.FC=()=>{
  const [ingresos,setIngresos]=useState<any[]>([]);
  const [filtroEstadoWeb,setFiltroEstadoWeb]=useState('');
  useEffect(()=>{
    const loadAll=async()=>{
      const [casosData,contactosData]=await Promise.all([
        FS.loadReportesData().catch(()=>[] as any[]),
        FS.loadContactosWeb().catch(()=>[] as any[]),
      ]);
      const casos=casosData.filter((c:any)=>c.clienteWeb||c.captadora==='WEB').map((c:any)=>({...c,_coleccion:'casos'}));
      const todos=[...casos,...contactosData].sort((a:any,b:any)=>{
        const da=a.createdAt?.toDate?a.createdAt.toDate():new Date(a.createdAt||0);
        const db=b.createdAt?.toDate?b.createdAt.toDate():new Date(b.createdAt||0);
        return db.getTime()-da.getTime();
      });
      setIngresos(todos);
    };
    loadAll();
  },[]);
  const filtered=ingresos.filter(c=>!filtroEstadoWeb||c.estadoWeb===filtroEstadoWeb);
  const updateEstadoWeb=async(item:any,estado:string)=>{
    if(item._coleccion==='contactos_web'){await FS.updateContactoWeb(item.id,'estadoWeb',estado);}
    else{await FS.updateCampo(item.id,'estadoWeb',estado);}
    setIngresos(prev=>prev.map(c=>c.id===item.id?{...c,estadoWeb:estado}:c));
  };
  return(
    <div className="p-section">
      <div className="p-section-title">🌐 Ingresos desde página web</div>
      <div className="p-info-box" style={{marginBottom:16}}>Formularios y contactos desde la página web. Actualice el estado de seguimiento.</div>
      <div style={{display:'flex',gap:10,marginBottom:16}}>
        <select className="p-select" value={filtroEstadoWeb} onChange={e=>setFiltroEstadoWeb(e.target.value)} style={{width:200}}>
          <option value="">Todos los estados</option>
          {INGRESO_WEB_ESTADOS.map(s=><option key={s}>{s}</option>)}
        </select>
      </div>
      {filtered.length?<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Correo</th><th>Teléfono</th><th>Área</th><th>Mensaje</th><th>Fecha</th><th>Estado</th></tr></thead><tbody>
        {filtered.map(c=><tr key={c.id}>
          <td>{c.nombreCliente||'—'}</td>
          <td style={{fontSize:12}}>{c.correo||'—'}</td>
          <td style={{fontSize:12}}>{c.telefono||'—'}</td>
          <td>{c.tema||'—'}</td>
          <td style={{fontSize:12,maxWidth:200,color:'var(--text-muted)'}}>{(((c.mensaje||c.notaInterna||'') as string).replace(/<[^>]*>/g,'').replace(/&nbsp;/g,' ').replace(/&amp;/g,'&').trim()||'—').slice(0,80)}{((c.mensaje||c.notaInterna||'') as string).replace(/<[^>]*>/g,'').trim().length>80?'…':''}</td>
          <td>{FS.fmtDate(c.createdAt)}</td>
          <td><select className="p-select" value={c.estadoWeb||'Contacto'} onChange={e=>updateEstadoWeb(c,e.target.value)} style={{fontSize:12,padding:'4px 8px'}}>{INGRESO_WEB_ESTADOS.map(s=><option key={s}>{s}</option>)}</select></td>
        </tr>)}
      </tbody></table></div>:<div className="p-empty">No hay ingresos web registrados.</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CALENDARIO
   ══════════════════════════════════════════════════════════════════════════ */
const CALENDARIO_ID='c_dda2d724a2ca45c3e64ecfa5915c8517cd0fa2b2b89801dea7372e5c4779d18a@group.calendar.google.com';
const CalendarioView:React.FC<{rol:string;nombre:string;uid:string}>=({rol,nombre,uid})=>{
  const [vista,setVista]=useState<'calendario'|'tasks'>('calendario');
  const [migrando,setMigrando]=useState(false);
  const [migracionResultado,setMigracionResultado]=useState<string|null>(null);
  const src=`https://calendar.google.com/calendar/embed?src=${encodeURIComponent(CALENDARIO_ID)}&ctz=${encodeURIComponent('America/Santiago')}&mode=MONTH&showTitle=0&showPrint=0&showTabs=0&showCalendars=0`;
  const migrarChecklist=async()=>{
    if(migrando)return;
    if(!window.confirm('Esto va a crear en Google Tasks (de admin, abogadas y procuradora) una tarea por cada item del checklist de TODOS los casos que aún no tenga. Puede tardar varios minutos, y puede que necesites correrlo más de una vez si hay muchos casos. ¿Continuar?'))return;
    setMigrando(true);setMigracionResultado(null);
    try{
      const res:any=await httpsCallable(functions,'migrarChecklistATasks',{timeout:1800000})();
      setMigracionResultado(`Listo: ${res?.data?.itemsMigrados||0} items migrados en ${res?.data?.casosConCambios||0} casos, para ${(res?.data?.emails||[]).length} cuentas. Si dice 0, ya terminó todo.`);
    }catch(e:any){
      console.error('Error en migración:',e);
      setMigracionResultado('Error: '+(e?.message||'no se pudo migrar.'));
    }finally{
      setMigrando(false);
    }
  };
  return(
    <div className="p-section">
      <div className="p-section-title">Calendario</div>
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        {([{id:'calendario',label:'📅 Calendario'},{id:'tasks',label:'✅ Tasks'}] as const).map(v=>
          <button key={v.id} className={vista===v.id?'p-btn-primary':'p-btn-secondary'} onClick={()=>setVista(v.id)} style={{padding:'6px 14px',fontSize:13}}>{v.label}</button>
        )}
      </div>
      {rol==='admin'&&(
        <div style={{marginBottom:16,padding:'10px 14px',background:'rgba(212,175,55,0.08)',border:'1px solid rgba(212,175,55,0.3)',borderRadius:8}}>
          <button className="p-btn-secondary" onClick={migrarChecklist} disabled={migrando} style={{fontSize:12}}>{migrando?'Migrando…':'🔄 Migrar checklist existente a Google Tasks (una vez)'}</button>
          {migracionResultado&&<div style={{fontSize:12,marginTop:8,color:'var(--text-muted)'}}>{migracionResultado}</div>}
        </div>
      )}
      {vista==='calendario'&&(<>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12}}>Para ver los eventos, tu navegador debe tener iniciada la sesión de Google de tu correo @svabogadas.cl.</div>
        <div style={{overflow:'hidden',borderRadius:10,border:'1px solid rgba(255,255,255,0.08)'}}>
          <iframe title="Calendario de audiencias" src={src} style={{border:0}} width="100%" height="650" scrolling="no"/>
        </div>
      </>)}
      {vista==='tasks'&&<TasksListView nombre={nombre} rol={rol} uid={uid}/>}
    </div>
  );
};

const DIAS_SEMANA=['LUN','MAR','MIÉ','JUE','VIE','SÁB','DOM'];
const GCAL_BLUE='#1a73e8';
const TasksListView:React.FC<{nombre:string;rol:string;uid:string}>=({nombre,rol,uid})=>{
  const [tasks,setTasks]=useState<any[]|null>(null);
  const [error,setError]=useState('');
  const [mesRef,setMesRef]=useState(()=>{const d=new Date();return new Date(d.getFullYear(),d.getMonth(),1);});
  const [diaSel,setDiaSel]=useState<string|null>(null);
  const [tareaSel,setTareaSel]=useState<any|null>(null);
  const [savingId,setSavingId]=useState<string|null>(null);
  useEffect(()=>{
    (async()=>{
      try{
        const res:any=await httpsCallable(functions,'listarTasksChecklist')();
        setTasks(res?.data?.tasks||[]);
      }catch(e:any){
        console.error('Error listando tasks:',e);
        setError('No se pudieron cargar tus tareas. Verifica que tu cuenta tenga una lista "Casos - Panel Souza & Villa" en Google Tasks.');
      }
    })();
  },[]);
  const toggleTarea=async(t:any,done:boolean)=>{
    if(!t.casoId||t.itemIndex==null||savingId)return;
    setSavingId(t.id);
    try{
      await FS.toggleCheckItem(t.casoId,t.itemIndex,done);
      if(t.texto) await FS.actualizarRealizadaPorTexto(t.casoId,t.texto,done,uid);
      if(t.gtasksIds){
        try{await httpsCallable(functions,'actualizarTaskChecklist')({ids:t.gtasksIds,done});}
        catch(e){console.error('Error actualizando tarea en Google Tasks:',e);}
      }
      const nuevoStatus=done?'completed':'needsAction';
      setTasks(prev=>prev?prev.map(x=>x.id===t.id?{...x,status:nuevoStatus}:x):prev);
      setTareaSel((prev:any)=>prev&&prev.id===t.id?{...prev,status:nuevoStatus}:prev);
    }catch(e){console.error('Error marcando tarea:',e);}
    finally{setSavingId(null);}
  };
  if(error) return <div className="p-empty">{error}</div>;
  if(tasks===null) return <div className="p-empty">Cargando tareas…</div>;

  const porDia:Record<string,any[]>={};
  const sinFecha:any[]=[];
  tasks.forEach(t=>{
    if(t.due){ const dia=t.due.slice(0,10); (porDia[dia]=porDia[dia]||[]).push(t); }
    else sinFecha.push(t);
  });

  const year=mesRef.getFullYear(), month=mesRef.getMonth();
  const startWeekday=(new Date(year,month,1).getDay()+6)%7; // lunes=0
  const daysInMonth=new Date(year,month+1,0).getDate();
  const cells:(number|null)[]=[];
  for(let i=0;i<startWeekday;i++) cells.push(null);
  for(let d=1;d<=daysInMonth;d++) cells.push(d);
  while(cells.length%7!==0) cells.push(null);
  const hoy=new Date().toISOString().slice(0,10);
  const keyOf=(d:number)=>`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  return(
    <div style={{background:'#fff',borderRadius:10,border:'1px solid #dadce0',overflow:'hidden',fontFamily:'Arial,Roboto,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'12px 16px',borderBottom:'1px solid #dadce0'}}>
        <div>
          <div style={{fontSize:14,fontWeight:500,color:'#3c4043'}}>👤 {nombre}</div>
          <div style={{fontSize:11,color:'#70757a',textTransform:'capitalize'}}>{rol}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:14}}>
          <button onClick={()=>{setMesRef(new Date(year,month-1,1));setDiaSel(null);}} style={{border:'none',background:'none',cursor:'pointer',fontSize:18,color:'#5f6368',padding:'2px 8px',borderRadius:'50%'}}>‹</button>
          <div style={{fontSize:15,fontWeight:500,color:'#3c4043',minWidth:130,textAlign:'center'}}>{MESES_NOMBRES[month]} {year}</div>
          <button onClick={()=>{setMesRef(new Date(year,month+1,1));setDiaSel(null);}} style={{border:'none',background:'none',cursor:'pointer',fontSize:18,color:'#5f6368',padding:'2px 8px',borderRadius:'50%'}}>›</button>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:'1px solid #dadce0'}}>
        {DIAS_SEMANA.map(d=><div key={d} style={{fontSize:11,fontWeight:500,color:'#70757a',textAlign:'center',padding:'8px 0'}}>{d}</div>)}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
        {cells.map((d,i)=>{
          if(d===null) return <div key={i} style={{minWidth:0,minHeight:78,borderRight:'1px solid #eee',borderBottom:'1px solid #eee'}}/>;
          const key=keyOf(d);
          const items=porDia[key]||[];
          const esHoy=key===hoy;
          const esSel=key===diaSel;
          return(
            <div key={i} onClick={()=>items.length&&setDiaSel(esSel?null:key)}
              style={{minWidth:0,minHeight:78,padding:'4px 5px',cursor:items.length?'pointer':'default',overflow:'hidden',
                borderRight:'1px solid #eee',borderBottom:'1px solid #eee',
                background:esSel?'#e8f0fe':'#fff'}}>
              <div style={{display:'flex',justifyContent:'center',marginBottom:3}}>
                <span style={{fontSize:12,width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'50%',
                  background:esHoy?GCAL_BLUE:'transparent',color:esHoy?'#fff':'#3c4043',fontWeight:esHoy?600:400,flexShrink:0}}>{d}</span>
              </div>
              {items.slice(0,3).map(t=>(
                <div key={t.id} title={t.title} onClick={e=>{e.stopPropagation();setTareaSel(t);}} style={{fontSize:10.5,padding:'1.5px 5px',marginBottom:2,borderRadius:4,minWidth:0,
                  background:t.status==='completed'?'#f1f3f4':GCAL_BLUE,
                  color:t.status==='completed'?'#70757a':'#fff',
                  textDecoration:t.status==='completed'?'line-through':'none',
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:500}}>{t.title}</div>
              ))}
              {items.length>3&&<div onClick={e=>{e.stopPropagation();setTareaSel(null);setDiaSel(key);}} style={{fontSize:9,color:GCAL_BLUE,cursor:'pointer',fontWeight:600}}>+{items.length-3} más</div>}
            </div>
          );
        })}
      </div>
      {tareaSel&&(
        <div style={{padding:'14px 16px',borderTop:'1px solid #dadce0',background:'#e8f0fe'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
            <div>
              <div style={{fontSize:13,fontWeight:500,color:'#3c4043',textDecoration:tareaSel.status==='completed'?'line-through':'none'}}>{tareaSel.title}</div>
              {tareaSel.notes&&<div style={{fontSize:12,color:'#70757a',marginTop:2}}>{tareaSel.notes}</div>}
              {tareaSel.due&&<div style={{fontSize:12,color:GCAL_BLUE,marginTop:4}}>📅 {FS.fmtDate(tareaSel.due.slice(0,10))}</div>}
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#70757a',marginTop:6,cursor:tareaSel.casoId?'pointer':'not-allowed'}}>
                <input type="checkbox" checked={tareaSel.status==='completed'} disabled={!tareaSel.casoId||savingId===tareaSel.id} onChange={e=>toggleTarea(tareaSel,e.target.checked)}/>
                {tareaSel.status==='completed'?'Completada':'Pendiente'}{!tareaSel.casoId&&' (sin vínculo al caso)'}
              </label>
            </div>
            <button onClick={()=>setTareaSel(null)} style={{border:'none',background:'none',cursor:'pointer',fontSize:16,color:'#5f6368'}}>✕</button>
          </div>
        </div>
      )}
      {!tareaSel&&diaSel&&(porDia[diaSel]||[]).length>0&&(
        <div style={{padding:'14px 16px',borderTop:'1px solid #dadce0',background:'#f8f9fa'}}>
          <div style={{fontSize:13,fontWeight:500,color:'#3c4043',marginBottom:8}}>{FS.fmtDate(diaSel)}</div>
          {porDia[diaSel].map(t=>(
            <div key={t.id} style={{display:'flex',alignItems:'flex-start',gap:8,padding:'6px 0',borderBottom:'1px solid #e8eaed'}}>
              <input type="checkbox" checked={t.status==='completed'} disabled={!t.casoId||savingId===t.id} onClick={e=>e.stopPropagation()} onChange={e=>toggleTarea(t,e.target.checked)} style={{marginTop:4,flexShrink:0,cursor:t.casoId?'pointer':'not-allowed'}}/>
              <div onClick={()=>setTareaSel(t)} style={{flex:1,minWidth:0,cursor:'pointer'}}>
                <div style={{fontSize:13,color:'#3c4043',textDecoration:t.status==='completed'?'line-through':'none'}}>{t.title}</div>
                {t.notes&&<div style={{fontSize:11,color:'#70757a'}}>{t.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {sinFecha.length>0&&(
        <div style={{padding:'14px 16px',borderTop:'1px solid #dadce0'}}>
          <div style={{fontSize:12,fontWeight:500,color:'#70757a',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.02em'}}>Sin fecha límite</div>
          {sinFecha.map(t=>(
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',borderBottom:'1px solid #e8eaed'}}>
              <input type="checkbox" checked={t.status==='completed'} disabled={!t.casoId||savingId===t.id} onClick={e=>e.stopPropagation()} onChange={e=>toggleTarea(t,e.target.checked)} style={{flexShrink:0,cursor:t.casoId?'pointer':'not-allowed'}}/>
              <span onClick={()=>setTareaSel(t)} style={{fontSize:13,color:'#3c4043',textDecoration:t.status==='completed'?'line-through':'none',cursor:'pointer'}}>{t.title}</span>
            </div>
          ))}
        </div>
      )}
      {tasks.length===0&&<div style={{padding:'24px 16px',textAlign:'center',color:'#70757a',fontSize:13}}>No tienes tareas registradas.</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   REPORTES
   ══════════════════════════════════════════════════════════════════════════ */
const Reportes:React.FC=()=>{
  const [data,setData]=useState<any[]>([]);
  const [vistaReporte,setVistaReporte]=useState<'resumen'|'totalCasos'|'totalCobrado'|'saldoPorCobrar'>('resumen');
  const [filterMode,setFilterMode]=useState<'mes'|'ytd'|'anio'>('mes');
  const [filterMes,setFilterMes]=useState<number>(new Date().getMonth());
  const [filterAnio,setFilterAnio]=useState<number>(new Date().getFullYear());
  useEffect(()=>{FS.loadReportesData().then(setData).catch(()=>{});},[]);

  const aniosOpts=Array.from({length:5},(_,i)=>new Date().getFullYear()-2+i);

  const getRange=():{from:Date|null;to:Date|null}=>{
    const now=new Date();
    if(filterMode==='mes') return{from:new Date(filterAnio,filterMes,1),to:new Date(filterAnio,filterMes+1,0,23,59,59)};
    if(filterMode==='ytd') return{from:new Date(now.getFullYear(),0,1),to:now};
    // anio: año completo (ene–dic)
    return{from:new Date(filterAnio,0,1),to:new Date(filterAnio,11,31,23,59,59)};
  };
  const{from,to}=getRange();

  const inRange=(val:string|null|undefined):boolean=>{
    if(!from&&!to) return true;
    if(!val) return false;
    const d=/^\d{4}-\d{2}-\d{2}$/.test(val)?new Date(val+'T00:00:00'):new Date(val);if(isNaN(d.getTime())) return false;
    if(from&&d<from) return false;
    if(to&&d>to) return false;
    return true;
  };
  const inRangeTS=(ts:any):boolean=>{
    if(!from&&!to) return true;
    if(!ts) return false;
    const d=ts?.toDate?ts.toDate():new Date(ts);if(isNaN(d.getTime())) return false;
    if(from&&d<from) return false;
    if(to&&d>to) return false;
    return true;
  };

  // Total casos: filtrado por createdAt
  const casosFiltrados=data.filter(c=>inRangeTS(c.createdAt));
  const total=casosFiltrados.length;
  const porEstado:Record<string,number>={};
  Object.keys(FS.statusColors).forEach(k=>porEstado[k]=0);
  casosFiltrados.forEach((c:any)=>{if(c.estado)porEstado[c.estado]=(porEstado[c.estado]||0)+1;});

  // Total cobrado: por fechaPago de pagos en todo el historial
  const totalPagado=data.reduce((sum,c)=>sum+(c.pagos||[])
    .filter((p:any)=>p.estado==='Pagado'&&inRange(p.fechaPago))
    .reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);

  // Saldo por cobrar: por vencimiento de cuotas pendientes
  const totalPend=data.reduce((sum,c)=>sum+(c.pagos||[])
    .filter((p:any)=>p.estado!=='Pagado'&&inRange(p.vencimiento||p.fecha))
    .reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);

  // Detalle total casos
  const casosDetalle=casosFiltrados.map((c:any)=>{
    const pg=c.pagos||[];
    return{...c,
      cobrado:pg.filter((p:any)=>p.estado==='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0),
      saldo:pg.filter((p:any)=>p.estado!=='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0),
    };
  });

  // Detalle total cobrado (por fechaPago en rango) — una fila por pago
  const cobradoDetalle=data.flatMap((c:any)=>{
    const pg=c.pagos||[];
    const pend=pg.filter((p:any)=>p.estado!=='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    return pg.filter((p:any)=>p.estado==='Pagado'&&inRange(p.fechaPago))
      .map((p:any)=>({...c,cobrado:Number(p.monto||0),saldo:pend,fechaPago:p.fechaPago}));
  }).sort((a,b)=>(b.fechaPago||'').localeCompare(a.fechaPago||''));

  // Detalle saldo por cobrar (por vencimiento en rango)
  const saldoDetalle=data.map((c:any)=>{
    const pg=c.pagos||[];
    const cob=pg.filter((p:any)=>p.estado==='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    const pendPagos=pg.filter((p:any)=>p.estado!=='Pagado'&&inRange(p.vencimiento||p.fecha));
    const pend=pendPagos.reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    const proximoVencimiento=pendPagos.map((p:any)=>p.vencimiento||p.fecha).filter(Boolean).sort()[0]||'';
    return{...c,cobrado:cob,saldo:pend,proximoVencimiento};
  }).filter(c=>c.saldo>0);
  const totalDeuda=saldoDetalle.reduce((s,c)=>s+c.saldo,0);

  const periodoLabel=filterMode==='mes'?`${MESES_NOMBRES[filterMes]} ${filterAnio}`
    :filterMode==='ytd'?`Enero – ${MESES_NOMBRES[new Date().getMonth()]} ${new Date().getFullYear()}`
    :`Año completo ${filterAnio}`;

  return(
    <div className="p-section">
      <div className="p-section-title">Reportes</div>

      {/* Filtro de período */}
      <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'14px 16px',marginBottom:16}}>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:10}}>Período</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {([{id:'mes',label:'Mes específico'},{id:'ytd',label:'Lo que va del año'},{id:'anio',label:'Año completo'}] as const).map(opt=>
            <button key={opt.id} className={filterMode===opt.id?'p-btn-primary':'p-btn-secondary'}
              onClick={()=>setFilterMode(opt.id)} style={{padding:'5px 13px',fontSize:12}}>{opt.label}</button>
          )}
        </div>
        {(filterMode==='mes'||filterMode==='anio')&&(
          <div style={{display:'flex',gap:8,marginTop:12,alignItems:'center'}}>
            {filterMode==='mes'&&<select className="p-select" value={filterMes} onChange={e=>setFilterMes(Number(e.target.value))} style={{fontSize:13,padding:'5px 8px',width:150}}>
              {MESES_NOMBRES.map((m,i)=><option key={i} value={i}>{m}</option>)}
            </select>}
            <select className="p-select" value={filterAnio} onChange={e=>setFilterAnio(Number(e.target.value))} style={{fontSize:13,padding:'5px 8px',width:90}}>
              {aniosOpts.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>Mostrando: <b style={{color:'var(--white)'}}>{periodoLabel}</b></div>
      </div>

      {/* Tabs de vista */}
      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
        {([{id:'resumen',label:'📊 Resumen'},{id:'totalCasos',label:'📋 Total Casos'},{id:'totalCobrado',label:'💰 Total Cobrado'},{id:'saldoPorCobrar',label:'⚠️ Saldo por cobrar'}] as const).map(v=>
          <button key={v.id} className={vistaReporte===v.id?'p-btn-primary':'p-btn-secondary'} onClick={()=>setVistaReporte(v.id)} style={{padding:'8px 16px',fontSize:13}}>{v.label}</button>
        )}
      </div>

      {/* KPI cards */}
      <div className="p-grid3" style={{marginBottom:20}}>
        <div className="fin-card" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('totalCasos')}>
          <div className="fin-label">Total casos</div>
          <div className="fin-val" style={{fontSize:'2rem'}}>{total}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>por fecha de ingreso</div>
        </div>
        <div className="fin-card ok" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('totalCobrado')}>
          <div className="fin-label">Total cobrado</div>
          <div className="fin-val">{FS.fmtMoney(totalPagado)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>por fecha de pago</div>
        </div>
        <div className="fin-card warn" style={{cursor:'pointer'}} onClick={()=>setVistaReporte('saldoPorCobrar')}>
          <div className="fin-label">Saldo por cobrar</div>
          <div className="fin-val">{FS.fmtMoney(totalPend)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>por vencimiento</div>
        </div>
      </div>

      {/* Resumen */}
      {vistaReporte==='resumen'&&<>
        {total===0
          ?<div className="p-empty">Sin casos en este período.</div>
          :Object.entries(porEstado).filter(([,v])=>v>0).map(([k,v])=>(
          <div key={k} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <div style={{width:120,fontSize:12,color:'var(--text-muted)'}}>{k}</div>
            <div style={{flex:1,height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.round(v/total*100)}%`,background:FS.statusColors[k],borderRadius:4}}/></div>
            <div style={{width:30,textAlign:'right',fontSize:13,color:'var(--white)'}}>{v}</div>
          </div>
        ))}
      </>}

      {/* Total casos */}
      {vistaReporte==='totalCasos'&&<div>
        <div className="p-section-title">Total Casos — {periodoLabel} ({casosDetalle.length})</div>
        {casosDetalle.length===0?<div className="p-empty">Sin casos en este período.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Área</th><th>Captadora</th><th>Abogada</th><th>Cobrado total</th><th>Pendiente total</th></tr></thead><tbody>
          {casosDetalle.map(c=><tr key={c.id}><td>{c.nombreCliente||'—'}</td><td>{c.tema||'—'}</td><td>{c.captadora||'—'}</td><td>{c.abogada||'Sin asignar'}</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(c.cobrado)}</td><td style={{color:c.saldo>0?'#e57373':'var(--text-muted)'}}>{FS.fmtMoney(c.saldo)}</td></tr>)}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}><td colSpan={4} style={{textAlign:'right'}}>TOTAL</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(casosDetalle.reduce((s,c)=>s+c.cobrado,0))}</td><td style={{color:'#e57373'}}>{FS.fmtMoney(casosDetalle.reduce((s,c)=>s+c.saldo,0))}</td></tr>
        </tbody></table></div>}
      </div>}

      {/* Total cobrado */}
      {vistaReporte==='totalCobrado'&&<div>
        <div className="p-section-title">Total Cobrado — {periodoLabel}</div>
        {cobradoDetalle.length===0?<div className="p-empty">Sin pagos recibidos en este período.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Área</th><th>Captadora</th><th>Abogada</th><th>Fecha de pago</th><th>Cobrado en período</th><th>Saldo pendiente</th></tr></thead><tbody>
          {cobradoDetalle.map((c,i)=><tr key={c.id+'-'+i}><td>{c.nombreCliente||'—'}</td><td>{c.tema||'—'}</td><td>{c.captadora||'—'}</td><td>{c.abogada||'Sin asignar'}</td><td>{FS.fmtDate(c.fechaPago)||'—'}</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(c.cobrado)}</td><td style={{color:c.saldo>0?'#e57373':'var(--text-muted)'}}>{FS.fmtMoney(c.saldo)}</td></tr>)}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}><td colSpan={5} style={{textAlign:'right'}}>TOTAL COBRADO</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(totalPagado)}</td><td></td></tr>
        </tbody></table></div>}
      </div>}

      {/* Saldo por cobrar */}
      {vistaReporte==='saldoPorCobrar'&&<div>
        <div className="p-section-title">Saldo por Cobrar — {periodoLabel}</div>
        {saldoDetalle.length===0?<div className="p-empty">Sin cuotas pendientes en este período.</div>
          :<div style={{overflowX:'auto'}}><table className="p-table"><thead><tr><th>Cliente</th><th>Área</th><th>Captadora</th><th>Abogada</th><th>Ya cobrado</th><th>Pendiente en período</th><th>Fecha de vencimiento</th></tr></thead><tbody>
          {saldoDetalle.map(c=>{
            const vencido=c.proximoVencimiento&&(/^\d{4}-\d{2}-\d{2}$/.test(c.proximoVencimiento)?new Date(c.proximoVencimiento+'T00:00:00'):new Date(c.proximoVencimiento))<new Date();
            return<tr key={c.id}><td>{c.nombreCliente||'—'}</td><td>{c.tema||'—'}</td><td>{c.captadora||'—'}</td><td>{c.abogada||'Sin asignar'}</td><td style={{color:'var(--gold)'}}>{FS.fmtMoney(c.cobrado)}</td><td style={{color:'#e57373',fontWeight:600}}>{FS.fmtMoney(c.saldo)}</td><td style={{color:vencido?'#e57373':'var(--text-muted)',fontWeight:vencido?600:400}}>{FS.fmtDate(c.proximoVencimiento)||'—'}</td></tr>;
          })}
          <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.15)'}}><td colSpan={5} style={{textAlign:'right'}}>TOTAL DEUDA</td><td style={{color:'#e57373'}}>{FS.fmtMoney(totalDeuda)}</td><td></td></tr>
        </tbody></table></div>}
      </div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   FLUJO DE CAJA
   50% abogadas (dividido equitativamente entre abogadas registradas)
   15% admin (del total)
   15% captadoras (15% de cada caso captado, no del total)
   20% oficina (de ahí se pagan gastos, lo que sobre es utilidad)
   ══════════════════════════════════════════════════════════════════════════ */
const FlujoCaja:React.FC<{soloLectura?:boolean}>=({soloLectura=false})=>{
  const [data,setData]=useState<any[]>([]);
  const [usuarios,setUsuarios]=useState<any[]>([]);
  const [periodoMeses,setPeriodoMeses]=useState(12);
  const [mesEspecifico,setMesEspecifico]=useState<{month:number;year:number}|null>({month:new Date().getMonth(),year:new Date().getFullYear()});
  const [gastos,setGastos]=useState<{fijos:Record<string,number>;custom:Array<{nombre:string;monto:number;fecha?:string}>;inversion:number;meses:Record<string,{fijos:Record<string,number>;custom:Array<{nombre:string;monto:number;fecha?:string}>}>}>({fijos:{arriendo:0,credito:0,luz:0,agua:0,papeleria:0},custom:[],inversion:0,meses:{}});
  const [gastosLoaded,setGastosLoaded]=useState(false);
  const [nuevoGastoNombre,setNuevoGastoNombre]=useState('');
  const [saveStatus,setSaveStatus]=useState<'idle'|'saving'|'saved'|'error'>('idle');
  const [editingNameIdx,setEditingNameIdx]=useState<number|null>(null);
  const [editingNameVal,setEditingNameVal]=useState('');
  const [sheetUrl,setSheetUrl]=useState<string>('');
  const [syncStatus,setSyncStatus]=useState<'idle'|'syncing'|'ok'|'error'>('idle');
  const saveTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);
  const syncTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);
  const [showTooltipCobrado,setShowTooltipCobrado]=useState(false);
  const tooltipTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);

  const SYNC_SHEET_URL='https://us-central1-souza-villa.cloudfunctions.net/syncFlujoCajaSheet';

  const doSheetSync=async(data:typeof gastos)=>{
    setSyncStatus('syncing');
    try{
      const token=await auth.currentUser?.getIdToken();
      if(!token){setSyncStatus('error');return;}
      const resp=await fetch(SYNC_SHEET_URL,{
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},
        body:JSON.stringify({gastos:data}),
      });
      const result=await resp.json();
      if(result.sheetUrl){setSheetUrl(result.sheetUrl);setSyncStatus('ok');setTimeout(()=>setSyncStatus('idle'),4000);}
      else{console.error('Sheet sync error:',result);setSyncStatus('error');}
    }catch(e){console.error('Sheet sync failed:',e);setSyncStatus('error');}
  };

  const scheduleSheetSync=(data:typeof gastos)=>{
    if(syncTimerRef.current)clearTimeout(syncTimerRef.current);
    syncTimerRef.current=setTimeout(()=>doSheetSync(data),3*60*1000); // 3 minutos
  };

  useEffect(()=>{
    FS.loadReportesData().then(setData).catch(()=>{});
    FS.loadUsuarios().then(setUsuarios).catch(()=>{});
    FS.getFlujoCajaConfig().then((cfg:any)=>{
      if(cfg){
        let normalizado:typeof gastos;
        if(!cfg.fijos&&!cfg.custom){
          const{inversion,...rest}=cfg;
          const fijos={arriendo:rest.arriendo||0,credito:rest.credito||0,luz:rest.luz||0,agua:rest.agua||0,papeleria:rest.papeleria||0};
          const custom=rest.otrosGastos?[{nombre:'Otros gastos',monto:rest.otrosGastos}]:[];
          normalizado={fijos,custom,inversion:inversion||0,meses:{}};
        }else{
          normalizado={...cfg,fijos:cfg.fijos||{arriendo:0,credito:0,luz:0,agua:0,papeleria:0},custom:cfg.custom||[],inversion:cfg.inversion||0,meses:cfg.meses||{}};
        }
        // Migrar gastos globales al mes actual si aún no se hizo
        const now=new Date();
        const curKey=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
        if((normalizado.custom||[]).length>0){
          const curMes=normalizado.meses[curKey]||{fijos:{arriendo:0,credito:0,luz:0,agua:0,papeleria:0},custom:[]};
          const nombres=new Set(curMes.custom.map((g:any)=>g.nombre));
          const nuevos=normalizado.custom.filter((g:any)=>!nombres.has(g.nombre));
          const mesActualizado={...curMes,custom:[...curMes.custom,...nuevos]};
          normalizado={...normalizado,meses:{...normalizado.meses,[curKey]:mesActualizado},custom:[]};
          FS.saveFlujoCajaConfig(normalizado);
        }
        setGastos(normalizado);
        // Restaurar link a planilla si ya fue creada antes
        if(cfg.sheetId){
          setSheetUrl(`https://docs.google.com/spreadsheets/d/${cfg.sheetId}`);
        }
      }
      setGastosLoaded(true);
    }).catch(()=>setGastosLoaded(true));
  },[]);

  // Calculate data by month for chart
  const getDataByMonth=()=>{
    const monthlyData:Record<number,{mes:number,año:number,cobrado:number,casos:number,promedioCaso:number}> = {};
    data.forEach((c:any)=>{
      if(!c.createdAt)return;
      const d=c.createdAt?.toDate?c.createdAt.toDate():new Date(c.createdAt);
      const mes=d.getMonth();
      const año=d.getFullYear();
      const key=año*12+mes;
      if(!monthlyData[key])monthlyData[key]={mes,año,cobrado:0,casos:0,promedioCaso:0};
      const cobrado=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0);
      if(cobrado>0){monthlyData[key].cobrado+=cobrado;monthlyData[key].casos+=1;}
    });
    return Object.values(monthlyData).sort((a,b)=>a.año*12+a.mes-b.año*12-b.mes).map(m=>({...m,promedioCaso:m.casos>0?m.cobrado/m.casos:0}));
  };
  
  const allMonthlyData=getDataByMonth();
  const availableYears=(()=>{const years=[...new Set(allMonthlyData.map(m=>m.año))].sort((a,b)=>a-b);const cy=new Date().getFullYear();if(!years.includes(cy))years.push(cy);return years;})();
  const availableMonthOpts=(()=>{const now=new Date();const opts:{year:number;month:number}[]=[];for(let i=0;i<24;i++){const d=new Date(now.getFullYear(),now.getMonth()-i,1);opts.push({year:d.getFullYear(),month:d.getMonth()});}return opts;})();

  // Get filtered data based on selection
  const getFilteredData=()=>{
    if(mesEspecifico!==null){
      const startOfMonth=new Date(mesEspecifico.year,mesEspecifico.month,1);
      const endOfMonth=new Date(mesEspecifico.year,mesEspecifico.month+1,0,23,59,59);
      return data.filter((c:any)=>{if(!c.createdAt)return false;const d=c.createdAt?.toDate?c.createdAt.toDate():new Date(c.createdAt);return d>=startOfMonth&&d<=endOfMonth;});
    } else {
      const cutoffDate=new Date();cutoffDate.setMonth(cutoffDate.getMonth()-periodoMeses);
      return data.filter((c:any)=>{if(!c.createdAt)return true;const d=c.createdAt?.toDate?c.createdAt.toDate():new Date(c.createdAt);return d>=cutoffDate;});
    }
  };
  
  const filteredData=getFilteredData();

  // Filtra pagos por fechaPago dentro del período seleccionado (no por createdAt del caso)
  const inRangeFCaja=(fechaPago:string|undefined|null):boolean=>{
    if(!fechaPago) return false;
    const d=/^\d{4}-\d{2}-\d{2}$/.test(fechaPago)?new Date(fechaPago+'T00:00:00'):new Date(fechaPago);
    if(isNaN(d.getTime())) return false;
    if(mesEspecifico!==null){
      return d>=new Date(mesEspecifico.year,mesEspecifico.month,1)&&d<=new Date(mesEspecifico.year,mesEspecifico.month+1,0,23,59,59);
    }
    const cutoff=new Date();cutoff.setMonth(cutoff.getMonth()-periodoMeses);
    return d>=cutoff;
  };

  const totalCobrado=data.reduce((s:number,c:any)=>s+(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((a:number,p:any)=>a+Number(p.monto||0),0),0);
  const casosConCobro=data.filter((c:any)=>(c.pagos||[]).some((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago))).length;

  const detalleTooltipCobrado=data
    .filter((c:any)=>(c.pagos||[]).some((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)))
    .map((c:any)=>{
      const pagados=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado');
      const pagadoPeriodo=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const totalPagado=pagados.reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const totalHonorarios=(c.pagos||[]).reduce((s:number,p:any)=>s+Number(p.monto||0),0);
      const faltaPorPagar=Math.max(0,totalHonorarios-totalPagado);
      return{cliente:c.nombreCliente||'—',fechaIngreso:FS.fmtDate(c.createdAt),captadora:c.captadora||'—',totalPagado,pagadoPeriodo,faltaPorPagar};
    })
    .sort((a:any,b:any)=>b.pagadoPeriodo-a.pagadoPeriodo);

  const enterCardCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);tooltipTimerRef.current=setTimeout(()=>setShowTooltipCobrado(true),350);};
  const leaveAreaCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);tooltipTimerRef.current=setTimeout(()=>setShowTooltipCobrado(false),150);};
  const enterPopoverCobrado=()=>{if(tooltipTimerRef.current)clearTimeout(tooltipTimerRef.current);};
  const promedioPorCaso=casosConCobro>0?totalCobrado/casosConCobro:0;
  const totalCasosActivos=filteredData.length;

  // Distribution
  const abogadas50=totalCobrado*0.50;
  const admin15=totalCobrado*0.15;
  const oficina20=totalCobrado*0.20;

  const abogadasList=usuarios.filter(u=>u.rol==='abogada');
  const abogadaShare=abogadasList.length>0?abogadas50/abogadasList.length:abogadas50;

  // La abogada asociada (abogada1) no participa en la distribución del flujo de caja.

  // Captadora: 15% de cada caso que captó
  const captadoraMap:Record<string,{nombre:string;total:number;casos:number}>={};
  data.forEach((c:any)=>{
    const cobradoCase=(c.pagos||[]).filter((p:any)=>p.estado==='Pagado'&&inRangeFCaja(p.fechaPago)).reduce((a:number,p:any)=>a+Number(p.monto||0),0);
    if(cobradoCase===0)return;
    const cap=c.captadora||'Sin captadora';
    const comision=cobradoCase*0.15;
    if(!captadoraMap[cap])captadoraMap[cap]={nombre:cap,total:0,casos:0};
    captadoraMap[cap].total+=comision;
    captadoraMap[cap].casos++;
  });
  const totalCaptadoras=Object.values(captadoraMap).reduce((s,v)=>s+v.total,0);

  const mesKey=mesEspecifico?`${mesEspecifico.year}-${String(mesEspecifico.month+1).padStart(2,'0')}`:null;
  // Mes nuevo siempre parte en cero — el usuario ingresa todo manualmente
  const getDefaultMesData=()=>({
    fijos:{arriendo:0,credito:0,luz:0,agua:0,papeleria:0},
    custom:[] as Array<{nombre:string;monto:number}>
  });
  const efectivos=mesKey?(gastos.meses?.[mesKey]??getDefaultMesData()):{fijos:gastos.fijos,custom:gastos.custom};

  const persistGastos=(updated:typeof gastos)=>{
    if(saveTimerRef.current)clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    scheduleSheetSync(updated);
    saveTimerRef.current=setTimeout(()=>{
      const clean=JSON.parse(JSON.stringify(updated));
      FS.saveFlujoCajaConfig(clean).then(()=>{setSaveStatus('saved');setTimeout(()=>setSaveStatus('idle'),2500);}).catch((err:any)=>{console.error('[FlujoCaja] Error al guardar:',err?.code,err?.message,err);setSaveStatus('error');});
    },800);
  };

  // Gastos
  const fijosTotal=Object.values(efectivos.fijos).reduce((s,v)=>s+v,0);
  const customTotal=(efectivos.custom||[]).reduce((s,g)=>s+g.monto,0);
  const totalGastosMensuales=fijosTotal+customTotal;

  // Total real del periodo: solo cuenta meses desde el primer caso hasta hoy
  const primerMes=allMonthlyData.length>0
    ?new Date(allMonthlyData[0].año,allMonthlyData[0].mes,1)
    :new Date(new Date().getFullYear(),new Date().getMonth(),1);
  const calcGastosPeriodo=()=>{
    if(mesEspecifico!==null)return{total:totalGastosMensuales,meses:1};
    const today=new Date();
    const periodoStart=new Date(today.getFullYear(),today.getMonth()-periodoMeses+1,1);
    const effectiveStart=primerMes>periodoStart?primerMes:periodoStart;
    let total=0,meses=0;
    const c=new Date(effectiveStart);
    while(c.getFullYear()*12+c.getMonth()<=today.getFullYear()*12+today.getMonth()){
      const key=`${c.getFullYear()}-${String(c.getMonth()+1).padStart(2,'0')}`;
      const mg=gastos.meses?.[key]??getDefaultMesData();
      total+=Object.values(mg.fijos).reduce((s,v)=>s+(v as number),0);
      total+=(mg.custom||[]).reduce((s:number,g:any)=>s+g.monto,0);
      meses++;
      c.setMonth(c.getMonth()+1);
    }
    return{total,meses};
  };
  const{total:totalGastosPeriodo,meses:mesesEnPeriodo}=calcGastosPeriodo();
  // Total histórico: suma TODOS los meses registrados (para el saldo de inversión)
  const totalGastosHistorico=Object.values(gastos.meses||{}).reduce((sum:number,mes:any)=>{
    const f=Object.values(mes.fijos||{}).reduce((s:number,v:any)=>s+(Number(v)||0),0);
    const c=(mes.custom||[]).reduce((s:number,g:any)=>s+(Number(g.monto)||0),0);
    return sum+f+c;
  },0);
  const utilidadOficina=oficina20-totalGastosPeriodo;
  // Ingresos históricos de oficina (20% de TODO lo cobrado, sin filtro de período)
  const totalCobradoHistorico=data.reduce((s:number,c:any)=>(c.pagos||[]).filter((p:any)=>p.estado==='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0)+s,0);
  const oficina20Historico=totalCobradoHistorico*0.20;
  const saldoInversion=gastos.inversion+oficina20Historico-totalGastosHistorico;
  const totalFondos=gastos.inversion+oficina20Historico;
  const saldoBanco=gastos.inversion+totalCobradoHistorico-totalGastosHistorico;
  const pctConsumido=totalFondos>0?Math.min(100,Math.max(0,Math.round(totalGastosHistorico/totalFondos*100))):0;
  const margenNeto=totalCobrado>0?(utilidadOficina/totalCobrado*100):0;

  const updateFijo=(field:string,val:number)=>{
    if(mesKey){
      const cur=gastos.meses?.[mesKey]??getDefaultMesData();
      const updated={...gastos,meses:{...gastos.meses,[mesKey]:{...cur,fijos:{...cur.fijos,[field]:val}}}};
      setGastos(updated);persistGastos(updated);
    }else{
      const updated={...gastos,fijos:{...gastos.fijos,[field]:val}};
      setGastos(updated);persistGastos(updated);
    }
  };
  const updateCustom=(idx:number,monto:number)=>{
    if(mesKey){
      const cur=gastos.meses?.[mesKey]??getDefaultMesData();
      const updated={...gastos,meses:{...gastos.meses,[mesKey]:{...cur,custom:cur.custom.map((g,i)=>i===idx?{...g,monto}:g)}}};
      setGastos(updated);persistGastos(updated);
    }else{
      const updated={...gastos,custom:gastos.custom.map((g,i)=>i===idx?{...g,monto}:g)};
      setGastos(updated);persistGastos(updated);
    }
  };
  const removeCustom=(idx:number)=>{
    if(mesKey){
      const cur=gastos.meses?.[mesKey]??getDefaultMesData();
      const updated={...gastos,meses:{...gastos.meses,[mesKey]:{...cur,custom:cur.custom.filter((_,i)=>i!==idx)}}};
      setGastos(updated);persistGastos(updated);
    }else{
      const updated={...gastos,custom:gastos.custom.filter((_,i)=>i!==idx)};
      setGastos(updated);persistGastos(updated);
    }
  };
  const addCustomGasto=()=>{
    if(!nuevoGastoNombre.trim())return;
    const fecha=new Date().toISOString().split('T')[0];
    if(mesKey){
      const cur=gastos.meses?.[mesKey]??getDefaultMesData();
      const updated={...gastos,meses:{...gastos.meses,[mesKey]:{...cur,custom:[...cur.custom,{nombre:nuevoGastoNombre.trim(),monto:0,fecha}]}}};
      setGastos(updated);setNuevoGastoNombre('');persistGastos(updated);
    }else{
      const updated={...gastos,custom:[...gastos.custom,{nombre:nuevoGastoNombre.trim(),monto:0,fecha}]};
      setGastos(updated);setNuevoGastoNombre('');persistGastos(updated);
    }
  };
  const updateCustomName=(idx:number,nombre:string)=>{
    if(mesKey){
      const cur=gastos.meses?.[mesKey]??getDefaultMesData();
      const updated={...gastos,meses:{...gastos.meses,[mesKey]:{...cur,custom:cur.custom.map((g,i)=>i===idx?{...g,nombre}:g)}}};
      setGastos(updated);persistGastos(updated);
    }else{
      const updated={...gastos,custom:gastos.custom.map((g,i)=>i===idx?{...g,nombre}:g)};
      setGastos(updated);persistGastos(updated);
    }
  };
  const updateInversion=(val:number)=>{const updated={...gastos,inversion:val};setGastos(updated);persistGastos(updated);};

  const parseMoneyInput=(val:string):number=>{
    const sanitized = val.trim().replace(/\s+/g,'').replace(/\./g,'').replace(/,/g,'.').replace(/[^0-9.-]/g,'');
    const parsed = parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : 0;
  };



  return(
    <div className="p-section">
      <div className="p-section-title">💵 Flujo de Caja</div>

      {/* Selector de periodo */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:8}}>Período:</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          {(()=>{const now=new Date();const isCur=mesEspecifico?.month===now.getMonth()&&mesEspecifico?.year===now.getFullYear();return(<button className={isCur?'p-btn-primary':'p-btn-secondary'} onClick={()=>setMesEspecifico({month:now.getMonth(),year:now.getFullYear()})} style={{padding:'6px 14px',fontSize:12}}>Mes actual</button>);})()}
          {REPORTES_PERIODOS.map(p=>(
            <button key={p.months} className={mesEspecifico===null&&periodoMeses===p.months?'p-btn-primary':'p-btn-secondary'} onClick={()=>{setMesEspecifico(null);setPeriodoMeses(p.months);}} style={{padding:'6px 14px',fontSize:12}}>{p.label}</button>
          ))}
          <select
            value={mesEspecifico?`${mesEspecifico.year}-${mesEspecifico.month}`:''}
            onChange={e=>{if(!e.target.value){setMesEspecifico(null);}else{const[y,m]=e.target.value.split('-').map(Number);setMesEspecifico({year:y,month:m});}}}
            style={{padding:'6px 12px',fontSize:12,background:'var(--bg-card,#1a1a2e)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:6,color:'var(--white,#fff)',cursor:'pointer',outline:'none',height:32}}
          >
            <option value=''>Mes específico…</option>
            {availableMonthOpts.map(o=>(
              <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>{MESES_NOMBRES[o.month]} {o.year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="p-grid3" style={{marginBottom:20}}>
        {/* Total cobrado — hover muestra detalle por cliente */}
        <div style={{position:'relative'}} onMouseEnter={enterCardCobrado} onMouseLeave={leaveAreaCobrado}>
          <div className="fin-card ok" style={{padding:'16px',cursor:'help'}}>
            <div className="fin-label" style={{fontSize:12}}>Total cobrado</div>
            <div className="fin-val" style={{fontSize:'1.6rem'}}>{FS.fmtMoney(totalCobrado)}</div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>{casosConCobro} casos con pago</div>
          </div>
          {showTooltipCobrado&&(
            <div onMouseEnter={enterPopoverCobrado} onMouseLeave={leaveAreaCobrado}
              style={{position:'absolute',top:'calc(100% + 8px)',left:0,zIndex:1000,background:'#0f1120',border:'1px solid rgba(255,255,255,0.13)',borderRadius:10,minWidth:680,maxHeight:340,overflowY:'auto',boxShadow:'0 12px 40px rgba(0,0,0,0.6)'}}>
              <table className="p-table" style={{fontSize:12,margin:0}}>
                <thead><tr><th>Cliente</th><th>Ingreso</th><th>Captadora</th><th style={{textAlign:'right'}}>Total pagado</th><th style={{textAlign:'right'}}>Pagado período</th><th style={{textAlign:'right'}}>Falta por pagar</th></tr></thead>
                <tbody>
                  {detalleTooltipCobrado.map((r:any,i:number)=>(
                    <tr key={i}>
                      <td>{r.cliente}</td>
                      <td style={{whiteSpace:'nowrap'}}>{r.fechaIngreso}</td>
                      <td>{r.captadora}</td>
                      <td style={{textAlign:'right',color:'var(--gold)'}}>{FS.fmtMoney(r.totalPagado)}</td>
                      <td style={{textAlign:'right',color:'#4CAF50',fontWeight:600}}>{FS.fmtMoney(r.pagadoPeriodo)}</td>
                      <td style={{textAlign:'right',color:r.faltaPorPagar>0?'#e57373':'var(--text-muted)'}}>{FS.fmtMoney(r.faltaPorPagar)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.12)'}}>
                    <td colSpan={3} style={{textAlign:'right',color:'var(--text-muted)'}}>TOTAL</td>
                    <td style={{textAlign:'right',color:'var(--gold)'}}>{FS.fmtMoney(detalleTooltipCobrado.reduce((s:number,r:any)=>s+r.totalPagado,0))}</td>
                    <td style={{textAlign:'right',color:'#4CAF50',fontWeight:700}}>{FS.fmtMoney(totalCobrado)}</td>
                    <td style={{textAlign:'right',color:'#e57373'}}>{FS.fmtMoney(detalleTooltipCobrado.reduce((s:number,r:any)=>s+r.faltaPorPagar,0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
        <div className="fin-card" style={{padding:'16px',background:'rgba(33,150,243,0.08)',borderColor:'rgba(33,150,243,0.2)'}}>
          <div className="fin-label" style={{fontSize:12}}>Promedio por caso</div>
          <div className="fin-val" style={{fontSize:'1.6rem',color:'#2196F3'}}>{FS.fmtMoney(promedioPorCaso)}</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>{totalCasosActivos} casos en periodo</div>
        </div>
        <div className="fin-card" style={{padding:'16px',background:'rgba(76,175,80,0.08)',borderColor:'rgba(76,175,80,0.2)'}}>
          <div className="fin-label" style={{fontSize:12}}>Margen neto %</div>
          <div className="fin-val" style={{fontSize:'1.6rem',color:'#4CAF50'}}>{margenNeto.toFixed(1)}%</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>de utilidad</div>
        </div>
      </div>

      {/* Distribución */}
      <div className="p-section-title" style={{marginBottom:14}}>Distribución de ingresos</div>
      <div className="p-grid2" style={{marginBottom:24}}>
        <div>
          {[
            {label:'Abogadas (50%)',amount:abogadas50,color:'#4CAF50'},
            {label:'Admin (15%)',amount:admin15,color:'#2196F3'},
            {label:'Captadoras (15% por caso)',amount:totalCaptadoras,color:'#FF9800'},
            {label:'Oficina (20%)',amount:oficina20,color:'#9C27B0'},
          ].map(r=>(
            <div key={r.label} style={{marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:4}}>
                <span style={{color:'#F0F4FF'}}>{r.label}</span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(r.amount)}</span>
              </div>
              <div style={{height:10,background:'rgba(255,255,255,0.06)',borderRadius:5,overflow:'hidden'}}>
                <div style={{height:'100%',width:totalCobrado>0?`${(r.amount/totalCobrado*100)}%`:'0%',background:r.color,borderRadius:5,transition:'width 0.3s'}}/>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div style={{background:'rgba(76,175,80,0.08)',borderRadius:8,padding:14,marginBottom:12,border:'1px solid rgba(76,175,80,0.2)'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:8}}>Detalle Abogadas — 50% del total</div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Total: {FS.fmtMoney(abogadas50)} dividido equitativamente</div>
            {abogadasList.length>0 ? abogadasList.map(a=>(
              <div key={a.id} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span>{a.nombre||a.email}</span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(abogadaShare)}</span>
              </div>
            )) : <div style={{fontSize:12,color:'var(--text-muted)'}}>No hay abogadas registradas</div>}
          </div>

          <div style={{background:'rgba(255,152,0,0.1)',borderRadius:8,padding:14,border:'1px solid rgba(255,152,0,0.2)'}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:8}}>Detalle Captadoras — 15% de cada caso captado</div>
            {Object.values(captadoraMap).length>0 ? Object.values(captadoraMap).map(cap=>(
              <div key={cap.nombre} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span>{cap.nombre} <span style={{color:'var(--text-muted)',fontSize:11}}>({cap.casos} caso{cap.casos>1?'s':''})</span></span>
                <span style={{color:'var(--gold)',fontWeight:600}}>{FS.fmtMoney(cap.total)}</span>
              </div>
            )) : <div style={{fontSize:12,color:'var(--text-muted)'}}>Sin captaciones en este periodo</div>}
          </div>
        </div>
      </div>

      {/* Gastos de oficina */}
      <div style={{background:'rgba(156,39,176,0.06)',borderRadius:10,padding:22,border:'1px solid rgba(156,39,176,0.15)'}}>
        <div className="p-section-title" style={{marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>🏢 Gastos de oficina y utilidad</span>
          <span style={{fontSize:11,fontWeight:400}}>
            {!soloLectura&&saveStatus==='saving'&&<span style={{color:'var(--text-muted)'}}>Guardando...</span>}
            {!soloLectura&&saveStatus==='saved'&&<span style={{color:'#4CAF50'}}>Guardado ✓</span>}
            {!soloLectura&&saveStatus==='error'&&<span style={{color:'#e57373'}}>Error al guardar</span>}
            {soloLectura&&<span style={{color:'var(--text-muted)',fontSize:10,letterSpacing:'0.1em'}}>SOLO LECTURA</span>}
            {!soloLectura&&<button onClick={()=>doSheetSync(gastos)} disabled={syncStatus==='syncing'} style={{fontSize:11,background:'rgba(66,133,244,0.15)',border:'1px solid rgba(66,133,244,0.3)',borderRadius:5,color:syncStatus==='ok'?'#4CAF50':syncStatus==='error'?'#e57373':'#90CAF9',padding:'2px 8px',cursor:syncStatus==='syncing'?'default':'pointer',marginLeft:8}}>
              {syncStatus==='syncing'?'⏳ Sincronizando...':syncStatus==='ok'?'✓ Planilla actualizada':syncStatus==='error'?'✗ Error al sincronizar':'📊 Sincronizar planilla'}
            </button>}
            {sheetUrl&&<a href={sheetUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'#4CAF50',textDecoration:'none',marginLeft:8}}>↗ Abrir planilla</a>}
          </span>
        </div>
        <div className="p-info-box" style={{marginBottom:16}}>
          Del 20% de oficina ({FS.fmtMoney(oficina20)}) se pagan todos los gastos. Lo que sobra es la utilidad neta.
        </div>

        <div className="p-grid2">
          <div>
            {mesKey?(
              <>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--white)'}}>
                    Gastos de {MESES_NOMBRES[mesEspecifico!.month]} {mesEspecifico!.year}
                  </div>
                  {!soloLectura&&gastos.meses?.[mesKey]&&(
                    <button className="p-btn-danger-sm" style={{fontSize:11}} onClick={()=>{const{[mesKey]:_,...rest}=gastos.meses;const updated={...gastos,meses:rest};setGastos(updated);persistGastos(updated);}}>↩ Limpiar mes</button>
                  )}
                </div>
                {([{label:"Arriendo",field:"arriendo"},{label:"Cuota crédito",field:"credito"},{label:"Luz",field:"luz"},{label:"Agua",field:"agua"},{label:"Papelería",field:"papeleria"}] as {label:string;field:string}[]).map(({label,field})=>(
                  <div key={field} style={{display:'flex',alignItems:'center',gap:20,padding:'7px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                    <span style={{fontSize:13,color:'var(--white)',minWidth:100}}>{label}</span>
                    <div style={{display:'flex',alignItems:'center',gap:8,flex:1}}>
                      <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                      <input className="p-input" type="text" inputMode="numeric" value={efectivos.fijos[field]!==undefined?String(efectivos.fijos[field]):''} onChange={e=>!soloLectura&&updateFijo(field,parseMoneyInput(e.target.value))} readOnly={soloLectura} placeholder="0" style={{fontSize:14,flex:1,minWidth:150}}/>
                    </div>
                  </div>
                ))}
                {(efectivos.custom||[]).map((g,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'7px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                    <div style={{flex:1,minWidth:0}}>
                      {!soloLectura&&editingNameIdx===i?(
                        <div style={{display:'flex',gap:6,alignItems:'center'}}>
                          <input className="p-input" value={editingNameVal} onChange={e=>setEditingNameVal(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){updateCustomName(i,editingNameVal.trim()||g.nombre);setEditingNameIdx(null);}if(e.key==='Escape')setEditingNameIdx(null);}} autoFocus style={{fontSize:12,flex:1}}/>
                          <button className="p-btn-secondary" style={{padding:'2px 8px',fontSize:11}} onClick={()=>{updateCustomName(i,editingNameVal.trim()||g.nombre);setEditingNameIdx(null);}}>✓</button>
                          <button className="p-btn-danger-sm" style={{fontSize:11}} onClick={()=>setEditingNameIdx(null)}>✕</button>
                        </div>
                      ):(
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <span style={{fontSize:13,color:'var(--white)'}}>{g.nombre}</span>
                          {!soloLectura&&<button onClick={()=>{setEditingNameIdx(i);setEditingNameVal(g.nombre);}} title="Editar nombre" style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:11,padding:'0 2px',lineHeight:1}}>✏️</button>}
                        </div>
                      )}
                      {g.fecha&&<div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{g.fecha.split('-').reverse().join('/')}</div>}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                      <input className="p-input" type="text" inputMode="numeric" value={g.monto!==undefined?String(g.monto):''} onChange={e=>!soloLectura&&updateCustom(i,parseMoneyInput(e.target.value))} readOnly={soloLectura} style={{flex:1,minWidth:150,fontSize:13}} placeholder="0"/>
                      {!soloLectura&&<button className="p-btn-danger-sm" onClick={()=>removeCustom(i)} style={{marginLeft:4}}>✕</button>}
                    </div>
                  </div>
                ))}
                {!soloLectura&&<div style={{display:'flex',gap:8,marginTop:10,alignItems:'center'}}>
                  <input className="p-input" value={nuevoGastoNombre} onChange={e=>setNuevoGastoNombre(e.target.value)} placeholder="Nombre del gasto..." style={{flex:1,fontSize:12}} onKeyPress={e=>e.key==='Enter'&&addCustomGasto()}/>
                  <button className="p-btn-secondary" onClick={addCustomGasto} style={{padding:'6px 12px',fontSize:12,whiteSpace:'nowrap'}}>＋ Agregar gasto</button>
                </div>}
                <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0',fontWeight:700,fontSize:14,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:8}}>
                  <span style={{color:'var(--white)'}}>Total {MESES_NOMBRES[mesEspecifico!.month]}</span>
                  <span style={{color:'#e57373'}}>{FS.fmtMoney(totalGastosMensuales)}</span>
                </div>
              </>
            ):(
              <>
                <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:12}}>Gastos del periodo</div>
                <div style={{padding:'16px',background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:6,marginBottom:12}}>
                  <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:14,lineHeight:1.7}}>
                    Selecciona un mes específico para ver y editar sus gastos. Los totales del periodo se calculan sumando todos los meses con datos ingresados.
                  </div>
                  {Object.keys(gastos.meses||{}).length>0&&(
                    <>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Meses con datos:</div>
                      <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                        {Object.keys(gastos.meses).sort().map(key=>{
                          const[y,m]=key.split('-');
                          const t=Object.values((gastos.meses[key].fijos)||{}).reduce((s:number,v:any)=>s+(Number(v)||0),0)+((gastos.meses[key].custom||[]).reduce((s:number,g:any)=>s+(Number(g.monto)||0),0));
                          return(
                            <button key={key} className="p-btn-secondary" style={{padding:'4px 10px',fontSize:11,display:'flex',flexDirection:'column',alignItems:'center',gap:2}} onClick={()=>{setMesEspecifico({month:parseInt(m)-1,year:parseInt(y)});}}>
                              <span>{MESES_NOMBRES[parseInt(m)-1].slice(0,3)} {y}</span>
                              <span style={{color:'#e57373',fontSize:10}}>{FS.fmtMoney(t)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0',fontWeight:700,fontSize:14,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:4}}>
                  <span style={{color:'var(--white)'}}>Total en periodo ({mesesEnPeriodo} {mesesEnPeriodo===1?'mes':'meses'})</span>
                  <span style={{color:'#e57373'}}>{FS.fmtMoney(totalGastosPeriodo)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12,color:'var(--text-muted)'}}>
                  <span>Total histórico acumulado</span>
                  <span style={{color:'#e57373'}}>{FS.fmtMoney(totalGastosHistorico)}</span>
                </div>
              </>
            )}
          </div>

          <div>
            <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:16,marginBottom:14,border:'1px solid rgba(255,255,255,0.06)'}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:4,color:'var(--white)'}}>💰 Inversión inicial (capital inyectado)</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>Capital aportado a la sociedad que cubre los gastos hasta que el negocio sea autosustentable.</div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>$</span>
                <input className="p-input" type="text" inputMode="numeric" value={gastos.inversion !== undefined ? String(gastos.inversion) : ''} onChange={e=>!soloLectura&&updateInversion(parseMoneyInput(e.target.value))} readOnly={soloLectura} placeholder="0" style={{fontSize:14,flex:1}}/>
              </div>
            </div>

            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:8,padding:16,border:'1px solid rgba(255,255,255,0.08)'}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:12,color:'var(--white)'}}>📊 Saldo de inversión</div>

              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>Capital inicial</span>
                <span style={{color:'#2196F3',fontWeight:600}}>{FS.fmtMoney(gastos.inversion)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>+ Ingresos oficina histórico (20%)</span>
                <span style={{color:'#4CAF50',fontWeight:600}}>+ {FS.fmtMoney(oficina20Historico)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                <span style={{color:'var(--text-muted)'}}>− Total gastos histórico</span>
                <span style={{color:'#e57373'}}>− {FS.fmtMoney(totalGastosHistorico)}</span>
              </div>

              {totalFondos>0&&(
                <div style={{margin:'12px 0 4px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:5}}>
                    <span>Gastos vs fondos disponibles</span>
                    <span style={{color:pctConsumido>=100?'#e57373':pctConsumido>=70?'#FF9800':'#4CAF50'}}>{pctConsumido}%</span>
                  </div>
                  <div style={{height:8,background:'rgba(255,255,255,0.06)',borderRadius:4,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pctConsumido}%`,background:pctConsumido>=100?'#e57373':pctConsumido>=70?'#FF9800':'#4CAF50',borderRadius:4,transition:'width 0.4s'}}/>
                  </div>
                </div>
              )}

              <div style={{display:'flex',justifyContent:'space-between',fontSize:17,padding:'12px 0',fontWeight:700,borderTop:'2px solid rgba(255,255,255,0.1)',marginTop:8}}>
                <span style={{color:'var(--white)'}}>Saldo disponible</span>
                <span style={{color:saldoInversion>=0?'#4CAF50':'#e57373'}}>{saldoInversion<0?'− ':''}{FS.fmtMoney(Math.abs(saldoInversion))}</span>
              </div>

              {saldoInversion<0&&<div style={{fontSize:12,color:'#e57373',marginTop:6,padding:'8px 10px',background:'rgba(229,115,115,0.08)',borderRadius:6}}>⚠️ Los gastos superan el capital invertido más los ingresos de oficina.</div>}
              {saldoInversion>=0&&saldoInversion<totalFondos*0.3&&totalFondos>0&&<div style={{fontSize:12,color:'#FF9800',marginTop:6,padding:'8px 10px',background:'rgba(255,152,0,0.08)',borderRadius:6}}>⚠️ Queda menos del 30% de los fondos disponibles.</div>}
              {saldoInversion>=totalFondos*0.3&&totalFondos>0&&<div style={{fontSize:12,color:'#4CAF50',marginTop:6,padding:'8px 10px',background:'rgba(76,175,80,0.08)',borderRadius:6}}>✓ Fondos disponibles.</div>}

              <div style={{marginTop:14,paddingTop:12,borderTop:'1px dashed rgba(255,255,255,0.07)'}}>
                <div style={{fontSize:10,color:'var(--text-muted)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:7}}>Convergencia cuenta banco</div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:2}}>
                  <span>Inversión + ingresos brutos</span>
                  <span>{FS.fmtMoney(gastos.inversion+totalCobradoHistorico)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:8}}>
                  <span>− Gastos históricos</span>
                  <span style={{color:'#e57373'}}>− {FS.fmtMoney(totalGastosHistorico)}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:14,fontWeight:700}}>
                  <span style={{color:'var(--white)'}}>💳 Saldo banco</span>
                  <span style={{color:saldoBanco>=0?'#4CAF50':'#e57373'}}>{saldoBanco<0?'− ':''}{FS.fmtMoney(Math.abs(saldoBanco))}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ANÁLISIS & FORECAST
   ══════════════════════════════════════════════════════════════════════════ */
const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const Analisis: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [flujoCfg, setFlujoCfg] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [gastosBasicos, setGastosBasicos] = useState({ arriendo: 480000, credito: 354247, custom: [] as {nombre:string;monto:number}[] });
  const [newNombre, setNewNombre] = useState('');
  const [newMonto, setNewMonto] = useState('');
  const [cfgSaved, setCfgSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      FS.loadReportesData().catch(() => [] as any[]),
      FS.getFlujoCajaConfig().catch(() => null),
      FS.getForecastConfig().catch(() => null),
    ]).then(([casos, flujo, forecast]) => {
      setData(casos);
      setFlujoCfg(flujo);
      if (forecast) setGastosBasicos({ arriendo: forecast.arriendo ?? 480000, credito: forecast.credito ?? 354247, custom: forecast.custom ?? [] });
      setLoaded(true);
    });
  }, []);

  // Gastos planificados fijos (actualizados mayo 2026)
  const GASTO_PLAN_MAYO = 3_576_364; // mayo 2026: incluye inversión inicial
  const GASTO_PLAN_BASE = 1_979_461; // gasto mensual fijo desde junio 2026

  const monthlyStats = useMemo(() => {
    const map: Record<string, {cobrado:number;oficina:number;gastoFijos:number;gastoReal:number;utilidad:number}> = {};
    data.forEach((c:any) => {
      (c.pagos||[]).forEach((p:any) => {
        if (p.estado !== 'Pagado' || !p.fechaPago) return;
        const key = p.fechaPago.substring(0, 7); // "YYYY-MM" por fecha real de cobro
        if (!map[key]) map[key] = {cobrado:0,oficina:0,gastoFijos:0,gastoReal:0,utilidad:0};
        map[key].cobrado += Number(p.monto||0);
      });
    });
    Object.entries(flujoCfg?.meses||{}).forEach(([key,mes]:any) => {
      if (!map[key]) map[key] = {cobrado:0,oficina:0,gastoFijos:0,gastoReal:0,utilidad:0};
      const fijos = Object.values(mes.fijos||{}).reduce((s:number,v:any)=>s+(Number(v)||0),0);
      const custom = (mes.custom||[]).reduce((s:number,g:any)=>s+(Number(g.monto)||0),0);
      map[key].gastoFijos = fijos as number;
      map[key].gastoReal  = (fijos as number) + custom;
    });
    return Object.entries(map)
      .sort(([a],[b])=>a.localeCompare(b))
      .map(([key,m]) => {
        const [yr,mo] = key.split('-');
        const gastosPlanificados = key === '2026-05' ? GASTO_PLAN_MAYO : GASTO_PLAN_BASE;
        return { ...m, label:`${MESES_CORTOS[+mo-1]} ${yr}`, oficina: m.cobrado, utilidad: m.cobrado - m.gastoReal, gastosPlanificados };
      });
  }, [data, flujoCfg]);

  const totalCobradoHist = data.reduce((s:number,c:any)=>(c.pagos||[]).filter((p:any)=>p.estado==='Pagado').reduce((a:number,p:any)=>a+Number(p.monto||0),0)+s,0);
  const totalGastosHist  = Object.values(flujoCfg?.meses||{}).reduce((sum:number,mes:any)=>{
    const f = Object.values(mes.fijos||{}).reduce((s:number,v:any)=>s+(Number(v)||0),0);
    const c = (mes.custom||[]).reduce((s:number,g:any)=>s+(Number(g.monto)||0),0);
    return sum+f+c;
  },0);
  const saldoDisponible   = (flujoCfg?.inversion||0) + totalCobradoHist - totalGastosHist;
  const gastoBasicoTotal  = gastosBasicos.arriendo + gastosBasicos.credito + gastosBasicos.custom.reduce((s,g)=>s+g.monto,0);
  const runway            = GASTO_PLAN_BASE > 0 ? Math.floor(saldoDisponible / GASTO_PLAN_BASE) : 0;
  const runwayColor       = runway < 3 ? '#e57373' : runway < 6 ? '#FF9800' : '#4CAF50';

  const saveBasicos = (updated: typeof gastosBasicos) => {
    setGastosBasicos(updated);
    FS.saveForecastConfig(updated).catch(()=>{});
    setCfgSaved(true);
    setTimeout(()=>setCfgSaved(false), 2000);
  };

  const tooltipStyle = { contentStyle:{background:'#1a2540',border:'1px solid rgba(255,255,255,0.12)',fontSize:12}, labelStyle:{color:'#F0F4FF'} };
  const axisStyle    = { tick:{fontSize:11,fill:'#9BB0CC'} };
  const gridStyle    = { strokeDasharray:'3 3', stroke:'rgba(255,255,255,0.06)' };

  if (!loaded) return <div className="p-loading">Cargando análisis...</div>;

  return (
    <div>
      <div className="p-section-title" style={{marginBottom:24}}>📈 Análisis y Forecast</div>

      {/* Gráfico 1 — Utilidad neta mensual */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'20px 16px',marginBottom:20,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:3}}>Utilidad neta mensual</div>
        <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:16}}>Ingreso total cobrado vs gasto real del mes · Línea verde = utilidad (ingresos − gastos)</div>
        {monthlyStats.length === 0
          ? <div className="p-empty">Sin datos suficientes aún.</div>
          : <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={monthlyStats} margin={{top:5,right:10,bottom:5,left:10}}>
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey="label" {...axisStyle} />
                <YAxis tickFormatter={v=>`$${(+v/1000).toFixed(0)}k`} {...axisStyle} width={58} />
                <Tooltip {...tooltipStyle} formatter={(v:any,n:any)=>[FS.fmtMoney(+v),String(n??'')]} />
                <Legend wrapperStyle={{fontSize:11,color:'#9BB0CC'}} />
                <Bar dataKey="oficina"   name="Ingreso total cobrado" fill="#9C27B0" radius={[3,3,0,0]} opacity={0.85} />
                <Bar dataKey="gastoReal" name="Gasto real"      fill="#e57373" radius={[3,3,0,0]} opacity={0.85} />
                <Line type="monotone" dataKey="utilidad" name="Utilidad" stroke="#4CAF50" strokeWidth={2} dot={{fill:'#4CAF50',r:3}} />
              </ComposedChart>
            </ResponsiveContainer>
        }
      </div>

      {/* Gráfico 2 — Presupuesto vs Real */}
      <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'20px 16px',marginBottom:20,border:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:3}}>Gastos planificados vs Gasto real</div>
        <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:16}}>Azul = gasto planificado (may $3.576.364, jun+ $1.979.461) · Naranja = gasto real registrado</div>
        {monthlyStats.filter(m=>m.gastoFijos>0||m.gastoReal>0).length === 0
          ? <div className="p-empty">Sin gastos registrados aún.</div>
          : <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyStats} margin={{top:5,right:10,bottom:5,left:10}}>
                <CartesianGrid {...gridStyle} />
                <XAxis dataKey="label" {...axisStyle} />
                <YAxis tickFormatter={v=>`$${(+v/1000).toFixed(0)}k`} {...axisStyle} width={58} />
                <Tooltip {...tooltipStyle} cursor={{fill:'transparent'}} formatter={(v:any,n:any)=>[FS.fmtMoney(+v),String(n??'')]} />
                <Legend wrapperStyle={{fontSize:11,color:'#9BB0CC'}} />
                <Bar dataKey="gastosPlanificados" name="Gasto planificado" fill="#2196F3" radius={[3,3,0,0]} opacity={0.85} />
                <Bar dataKey="gastoReal"          name="Gasto real"       fill="#FF9800" radius={[3,3,0,0]} opacity={0.85} />
              </BarChart>
            </ResponsiveContainer>
        }
      </div>

      {/* Runway + Gastos básicos */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:20,border:'1px solid rgba(255,255,255,0.07)'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:16}}>🕐 Meses de vida estimados</div>
          <div style={{textAlign:'center',padding:'20px 0'}}>
            <div style={{fontSize:60,fontWeight:700,color:runwayColor,lineHeight:1}}>{runway > 0 ? runway : '—'}</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginTop:8}}>meses con fondos actuales</div>
          </div>
          <div style={{fontSize:12,color:'var(--text-muted)',padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:6}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span>Saldo bruto disponible</span>
              <span style={{color:'#F0F4FF',fontWeight:600}}>{FS.fmtMoney(Math.max(0,saldoDisponible))}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{color:'#aaa'}}>Crédito + ingresos cobrados</span>
              <span style={{color:'#aaa'}}>{FS.fmtMoney((flujoCfg?.inversion||0) + totalCobradoHist)}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{color:'#aaa'}}>Gastos históricos</span>
              <span style={{color:'#e57373'}}>−{FS.fmtMoney(totalGastosHist)}</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between'}}>
              <span>Gasto planificado mensual</span>
              <span style={{color:'#F0F4FF',fontWeight:600}}>{FS.fmtMoney(GASTO_PLAN_BASE)}</span>
            </div>
          </div>
          {saldoDisponible > 0 && runway < 3  && <div style={{fontSize:11,color:'#e57373',marginTop:8,padding:'6px 10px',background:'rgba(229,115,115,0.08)',borderRadius:6}}>⚠️ Menos de 3 meses. Revisar urgente.</div>}
          {runway >= 3 && runway < 6           && <div style={{fontSize:11,color:'#FF9800',marginTop:8,padding:'6px 10px',background:'rgba(255,152,0,0.08)',borderRadius:6}}>⚠️ Entre 3 y 6 meses de runway.</div>}
          {runway >= 6                          && <div style={{fontSize:11,color:'#4CAF50',marginTop:8,padding:'6px 10px',background:'rgba(76,175,80,0.08)',borderRadius:6}}>✓ Runway saludable.</div>}
        </div>

        <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:20,border:'1px solid rgba(255,255,255,0.07)'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--white)',marginBottom:16}}>Gastos básicos mensuales</div>
          <div className="p-field">
            <div className="p-field-label">Arriendo</div>
            <input className="p-input" inputMode="numeric" value={gastosBasicos.arriendo||''}
              onChange={e=>{const u={...gastosBasicos,arriendo:Number(e.target.value.replace(/\D/g,''))};saveBasicos(u);}} placeholder="0"/>
          </div>
          <div className="p-field">
            <div className="p-field-label">Crédito</div>
            <input className="p-input" inputMode="numeric" value={gastosBasicos.credito||''}
              onChange={e=>{const u={...gastosBasicos,credito:Number(e.target.value.replace(/\D/g,''))};saveBasicos(u);}} placeholder="0"/>
          </div>
          {gastosBasicos.custom.map((g,i)=>(
            <div key={i} className="p-field" style={{display:'flex',gap:8,alignItems:'flex-end'}}>
              <div style={{flex:1}}>
                <div className="p-field-label">{g.nombre}</div>
                <input className="p-input" inputMode="numeric" value={g.monto||''}
                  onChange={e=>{const custom=gastosBasicos.custom.map((x,j)=>j===i?{...x,monto:Number(e.target.value.replace(/\D/g,''))}:x);saveBasicos({...gastosBasicos,custom});}} placeholder="0"/>
              </div>
              <button className="p-btn-danger-sm" style={{marginBottom:6}} onClick={()=>{const custom=gastosBasicos.custom.filter((_,j)=>j!==i);saveBasicos({...gastosBasicos,custom});}}>✕</button>
            </div>
          ))}
          <div style={{display:'flex',gap:6,marginTop:10}}>
            <input className="p-input" placeholder="Nombre" value={newNombre} onChange={e=>setNewNombre(e.target.value)} style={{flex:2}}/>
            <input className="p-input" placeholder="Monto" inputMode="numeric" value={newMonto} onChange={e=>setNewMonto(e.target.value.replace(/\D/g,''))} style={{flex:1}}/>
            <button className="p-btn-primary" style={{padding:'4px 10px',fontSize:12,flexShrink:0}} onClick={()=>{
              if(!newNombre||!newMonto) return;
              const custom=[...gastosBasicos.custom,{nombre:newNombre,monto:Number(newMonto)}];
              saveBasicos({...gastosBasicos,custom});
              setNewNombre('');setNewMonto('');
            }}>＋</button>
          </div>
          {cfgSaved && <div style={{fontSize:11,color:'#4CAF50',marginTop:8}}>✓ Guardado</div>}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CAPTADORAS — Bonos y Rendimiento
   ══════════════════════════════════════════════════════════════════════════ */
const Captadoras: React.FC = () => {
  const today = new Date();
  const [mes, setMes] = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());
  const [casos, setCasos] = useState<any[]>([]);
  const [captadoras, setCaptadoras] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hoveredBar, setHoveredBar] = useState<{capId: string; hi: number} | null>(null);
  const [rolActual, setRolActual] = useState<string>('');
  const [cerrando, setCerrando] = useState(false);

  const recargar = () => Promise.all([
    FS.loadReportesData().catch(() => [] as any[]),
    FS.loadCaptadoras().catch(() => [] as any[]),
  ]).then(([c, cap]) => { setCasos(c); setCaptadoras(cap); });

  useEffect(() => {
    recargar().then(() => setLoaded(true));
    const uid = auth.currentUser?.uid;
    if (uid) FS.getUserData(uid).then((u: any) => setRolActual(u?.rol || ''));
  }, []);

  const prevMes = () => { if (mes === 0) { setMes(11); setAnio(a => a - 1); } else { setMes(m => m - 1); } };
  const nextMes = () => { if (mes === 11) { setMes(0); setAnio(a => a + 1); } else { setMes(m => m + 1); } };
  const isCurrentMonth = mes === today.getMonth() && anio === today.getFullYear();
  const isFuture = anio > today.getFullYear() || (anio === today.getFullYear() && mes > today.getMonth());

  const calcBonus = (n: number) => n < 11 ? 0 : 100000 + Math.max(0, n - 11) * 10000;

  const ESTADOS_CONTABLES = ['Aceptado', 'En tramitación', 'Resuelto / Cerrado'];
  // Si el caso tiene mesContabilizado, queda congelado en ese mes para siempre:
  // - no cambia aunque el estado varíe, y nunca se doble-contabiliza en otro mes.
  // Sin mesContabilizado, se usa lógica dinámica con fechaUltimoAceptado (actualizable):
  // - si un caso acepta en M1, revierte en M1 y vuelve a aceptar en M2, la fecha se mueve a M2.
  // - mes actual verifica estado en tiempo real; meses pasados no verifican estado.
  const casosEnMes = useCallback((captadoraId: string, m: number, a: number) => {
    const esMesActual = m === today.getMonth() && a === today.getFullYear();
    const mesKey = `${a}-${m}`;
    return casos.filter(c => {
      if (c.captadoraId !== captadoraId) return false;
      if (c.mesContabilizado) return c.mesContabilizado === mesKey;
      const fechaRef = c.fechaUltimoAceptado ?? c.fechaAceptado;
      if (fechaRef) {
        const d = fechaRef?.toDate ? fechaRef.toDate() : new Date(fechaRef);
        if (isNaN(d.getTime()) || d.getMonth() !== m || d.getFullYear() !== a) return false;
        return !esMesActual || ESTADOS_CONTABLES.includes(c.estado);
      }
      // Casos legacy sin ninguna fecha de aceptación: usar createdAt + estado actual
      if (!ESTADOS_CONTABLES.includes(c.estado)) return false;
      const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
      return !isNaN(d.getTime()) && d.getMonth() === m && d.getFullYear() === a;
    });
  }, [casos]);

  const diasEnMes = new Date(anio, mes + 1, 0).getDate();
  const diaActual = isCurrentMonth ? today.getDate() : diasEnMes;

  const rankings = useMemo(() => captadoras
    .map(cap => {
      const casosDelMes = casosEnMes(cap.id, mes, anio);
      const count = casosDelMes.length;
      const pm = mes === 0 ? 11 : mes - 1;
      const pa = mes === 0 ? anio - 1 : anio;
      const prevCount = casosEnMes(cap.id, pm, pa).length;
      const bonus = calcBonus(count);
      const proyeccion = isCurrentMonth && diaActual > 0 ? Math.round((count / diaActual) * diasEnMes) : null;
      return { ...cap, count, prevCount, bonus, proyeccion, bonusProyectado: proyeccion !== null ? calcBonus(proyeccion) : null };
    })
    .sort((a, b) => b.count - a.count || a.nombre?.localeCompare(b.nombre || '') || 0),
  [captadoras, casos, mes, anio]);

  const META = 11;
  const totalBonos = rankings.reduce((s, r) => s + r.bonus, 0);
  const bonusProyectadoTotal = isCurrentMonth ? rankings.reduce((s, r) => s + (r.bonusProyectado || 0), 0) : null;

  const mesKey = `${anio}-${mes}`;
  // El mes está cerrado si algún caso ya tiene mesContabilizado para este mes
  const mesCerrado = casos.some(c => c.mesContabilizado === mesKey);

  const handleCerrarMes = async () => {
    // Recoger todos los casos del mes que aún no tienen mesContabilizado asignado
    const esMesActual = mes === today.getMonth() && anio === today.getFullYear();
    const idsPorCerrar = casos.filter(c => {
      if (c.mesContabilizado) return false;
      const fechaRef = c.fechaUltimoAceptado ?? c.fechaAceptado;
      if (fechaRef) {
        const d = fechaRef?.toDate ? fechaRef.toDate() : new Date(fechaRef);
        if (isNaN(d.getTime()) || d.getMonth() !== mes || d.getFullYear() !== anio) return false;
        return !esMesActual || ESTADOS_CONTABLES.includes(c.estado);
      }
      if (!ESTADOS_CONTABLES.includes(c.estado)) return false;
      const d = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
      return !isNaN(d.getTime()) && d.getMonth() === mes && d.getFullYear() === anio;
    }).map(c => c.id);

    if (idsPorCerrar.length === 0) {
      alert(mesCerrado ? 'Todos los casos de este mes ya están cerrados.' : 'No hay casos para cerrar en este mes.');
      return;
    }
    const msg = mesCerrado
      ? `El mes tiene casos ya cerrados. ¿Cerrar también los ${idsPorCerrar.length} caso(s) nuevos contabilizados desde entonces? Esta acción es irreversible.`
      : `¿Confirmar cierre de ${MESES_NOMBRES[mes]} ${anio} con ${idsPorCerrar.length} caso(s)?\n\nEl conteo quedará congelado: estos casos siempre se contarán en este mes, sin importar cambios futuros de estado.`;
    if (!window.confirm(msg)) return;
    setCerrando(true);
    try {
      await FS.cerrarMesCaptadoras(idsPorCerrar, mesKey);
      await recargar();
    } finally {
      setCerrando(false);
    }
  };

  // Historial últimos 6 meses para sparklines
  const historial = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(anio, mes - 5 + i, 1);
      const m2 = d.getMonth(), a2 = d.getFullYear();
      return { label: MESES_CORTOS[m2], mes: m2, anio: a2 };
    });
  }, [mes, anio]);

  if (!loaded) return <div className="p-loading">Cargando datos de captadoras...</div>;

  return (
    <div>
      <div className="p-section-title" style={{ marginBottom: 20 }}>🏆 Rendimiento Captadoras</div>

      {/* Navegador de mes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 20px', border: `1px solid ${mesCerrado ? 'rgba(76,175,80,0.35)' : 'rgba(255,255,255,0.07)'}` }}>
        <button className="p-btn-secondary" onClick={prevMes} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--white)' }}>{MESES_NOMBRES[mes]} {anio}</span>
            {mesCerrado && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(76,175,80,0.2)', color: '#4CAF50', border: '1px solid rgba(76,175,80,0.4)', borderRadius: 4, padding: '2px 6px', letterSpacing: '0.5px' }}>CERRADO</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {isCurrentMonth ? `Día ${diaActual} de ${diasEnMes} — mes en curso` : mesCerrado ? 'Conteo congelado' : 'Mes completo'}
          </div>
        </div>
        <button className="p-btn-secondary" onClick={nextMes} disabled={isFuture} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1, opacity: isFuture ? 0.4 : 1 }}>›</button>
      </div>

      {/* Botón cerrar mes — solo admin, solo meses no futuros */}
      {rolActual === 'admin' && !isFuture && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button
            onClick={handleCerrarMes}
            disabled={cerrando}
            style={{
              padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: cerrando ? 'wait' : 'pointer',
              background: mesCerrado ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${mesCerrado ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.15)'}`,
              color: mesCerrado ? '#4CAF50' : 'var(--text-muted)',
              opacity: cerrando ? 0.6 : 1,
            }}
          >
            {cerrando ? 'Cerrando…' : mesCerrado ? '🔒 Cerrar casos nuevos' : '🔒 Cerrar mes'}
          </button>
        </div>
      )}

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 20 }}>
        <div className="fin-card">
          <div className="fin-label">Total bonos del mes</div>
          <div className="fin-val" style={{ fontSize: '1.2rem', color: totalBonos > 0 ? '#BFAF72' : 'var(--cream)' }}>{FS.fmtMoney(totalBonos)}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label">Con bono</div>
          <div className="fin-val" style={{ fontSize: '1.2rem', color: '#4CAF50' }}>{rankings.filter(r => r.count >= META).length} / {rankings.length}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label">Total casos captados</div>
          <div className="fin-val" style={{ fontSize: '1.2rem' }}>{rankings.reduce((s, r) => s + r.count, 0)}</div>
        </div>
        {isCurrentMonth && bonusProyectadoTotal !== null && (
          <div className="fin-card" style={{ borderColor: 'rgba(100,181,246,0.25)' }}>
            <div className="fin-label">Bonos proyectados fin de mes</div>
            <div className="fin-val" style={{ fontSize: '1.2rem', color: '#64B5F6' }}>{FS.fmtMoney(bonusProyectadoTotal)}</div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {rankings.length === 0
        ? <div className="p-empty">No hay captadoras registradas.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rankings.map((cap, i) => {
              const pct = Math.min(100, Math.round((cap.count / META) * 100));
              const tieneBonus = cap.count >= META;
              const trend = cap.count - cap.prevCount;
              const faltanMeta = Math.max(0, META - cap.count);
              const extraCasos = Math.max(0, cap.count - META);

              return (
                <div key={cap.id} className={tieneBonus ? 'cap-card-bonus' : 'cap-card'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    {/* Posición */}
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      background: i === 0 ? 'linear-gradient(135deg,#B8965A,#E0C878)' : i === 1 ? 'rgba(160,174,192,0.25)' : i === 2 ? 'rgba(160,120,80,0.25)' : 'rgba(255,255,255,0.07)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: i === 0 ? '#2a1800' : i < 3 ? '#F0F4FF' : '#9BB0CC',
                    }}>{i + 1}</div>

                    {/* Nombre */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cap.nombre || cap.email || cap.id}
                      </div>
                      {cap.email && cap.nombre && <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cap.email}</div>}
                    </div>

                    {/* Tendencia */}
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: trend > 0 ? '#4CAF50' : trend < 0 ? '#e57373' : '#9BB0CC' }}>
                        {trend > 0 ? `▲ +${trend}` : trend < 0 ? `▼ ${trend}` : '─'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>vs mes ant.</div>
                    </div>

                    {/* Casos */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: tieneBonus ? 'var(--gold)' : 'var(--white)' }}>{cap.count}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>casos</div>
                    </div>
                  </div>

                  {/* Barra de progreso */}
                  <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 4, height: 6, marginBottom: 10, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: tieneBonus ? 'linear-gradient(90deg,#B8965A,#E0C878)' : 'linear-gradient(90deg,#4A7FA5,#64B5F6)', transition: 'width 0.5s ease' }} />
                    {/* Marca de la meta */}
                    <div style={{ position: 'absolute', top: 0, left: '100%', width: 2, height: '100%', background: 'rgba(255,255,255,0.3)' }} />
                  </div>

                  {/* Info fila */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {tieneBonus ? (
                      <div style={{ fontSize: 13, color: '#BFAF72', fontWeight: 600 }}>
                        💰 Bono: {FS.fmtMoney(cap.bonus)}
                        {extraCasos > 0 && <span style={{ fontSize: 11, color: '#9BB0CC', fontWeight: 400 }}> ({extraCasos} extra × $10.000)</span>}
                      </div>
                    ) : cap.count === 0 ? (
                      <div style={{ fontSize: 12, color: '#9BB0CC' }}>Sin casos este mes</div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#9BB0CC' }}>
                        🎯 Faltan <strong style={{ color: 'var(--white)' }}>{faltanMeta}</strong> para el bono
                      </div>
                    )}

                    {/* Proyección (solo mes actual) */}
                    {isCurrentMonth && cap.proyeccion !== null && cap.count > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', textAlign: 'right' }}>
                        Proyección: <strong style={{ color: cap.bonusProyectado > 0 ? '#4CAF50' : 'var(--white)' }}>{cap.proyeccion} casos</strong>
                        {cap.bonusProyectado > 0 && <span style={{ color: '#BFAF72' }}> → {FS.fmtMoney(cap.bonusProyectado)}</span>}
                      </div>
                    )}
                  </div>

                  {/* Historial sparkline (barras mini últimos 6 meses) */}
                  {cap.count > 0 && (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', height: 44 }}>
                      {historial.map((h, hi) => {
                        const clientesDelMes = casosEnMes(cap.id, h.mes, h.anio);
                        const n = clientesDelMes.length;
                        const maxN = Math.max(...historial.map(hh => casosEnMes(cap.id, hh.mes, hh.anio).length), 1);
                        const barH = n === 0 ? 3 : Math.max(16, Math.round((n / maxN) * 28) + 4);
                        const isSelected = h.mes === mes && h.anio === anio;
                        const barBg = isSelected ? '#64B5F6' : n >= META ? 'rgba(184,150,90,0.5)' : 'rgba(255,255,255,0.1)';
                        const numColor = isSelected ? '#0B1732' : n >= META ? '#2a1500' : '#F0F4FF';
                        const isHovered = hoveredBar?.capId === cap.id && hoveredBar?.hi === hi;
                        return (
                          <div key={hi} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}>
                            <div
                              style={{ width: '100%', height: barH, background: barBg, borderRadius: 2, transition: 'height 0.3s', position: 'relative', cursor: n > 0 ? 'pointer' : 'default' }}
                              onMouseEnter={() => n > 0 && setHoveredBar({ capId: cap.id, hi })}
                              onMouseLeave={() => setHoveredBar(null)}
                            >
                              {n > 0 && (
                                <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, textAlign: 'center', fontSize: 8, fontWeight: 700, color: numColor, lineHeight: 1 }}>{n}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 8, color: isSelected ? '#64B5F6' : '#6a82a0' }}>{h.label}</div>

                            {/* Tooltip con clientes del mes */}
                            {isHovered && n > 0 && (
                              <div style={{
                                position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                                background: '#0f1929', border: '1px solid rgba(100,181,246,0.3)', borderRadius: 8,
                                padding: '8px 12px', zIndex: 200, minWidth: 210, maxWidth: 300,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.5)', pointerEvents: 'none',
                              }}>
                                <div style={{ fontSize: 10, color: '#64B5F6', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                  {h.label} {h.anio} · {n} caso{n !== 1 ? 's' : ''}
                                </div>
                                {[...clientesDelMes].sort((a: any, b: any) => {
                                  const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                                  const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                                  return da - db2;
                                }).map((caso: any, ci: number) => {
                                  const llegada = caso.createdAt?.toDate ? caso.createdAt.toDate() : new Date(caso.createdAt);
                                  const fechaLlegada = !isNaN(llegada.getTime())
                                    ? llegada.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                    : '—';
                                  return (
                                    <div key={ci} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0', borderTop: ci > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                                      <span style={{ fontSize: 12, color: 'var(--white)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {caso.nombreCliente || '—'}
                                      </span>
                                      <span style={{ fontSize: 10, color: '#6a82a0', flexShrink: 0 }}>
                                        {fechaLlegada}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
      }

      {/* Regla de bonos */}
      <div style={{ marginTop: 24, padding: '14px 18px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)', fontSize: 12, color: '#9BB0CC', lineHeight: 1.9 }}>
        <div style={{ fontWeight: 600, color: 'var(--white)', marginBottom: 4 }}>Regla de bonos vigente</div>
        <div>• <strong style={{ color: 'var(--gold)' }}>11 casos</strong> en el mes → Bono base de <strong style={{ color: 'var(--gold)' }}>$100.000</strong></div>
        <div>• <strong style={{ color: 'var(--gold)' }}>12+ casos</strong> → $100.000 + <strong style={{ color: 'var(--gold)' }}>$10.000 por cada caso desde el 12°</strong></div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   ASISTENCIA — Control de asistencia de captadoras
   ══════════════════════════════════════════════════════════════════════════ */
const Asistencia: React.FC<{ rol: string; userId: string; userNombre: string }> = ({ rol, userId, userNombre }) => {
  const today = new Date();
  const [mes, setMes] = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());
  const [captadoras, setCaptadoras] = useState<any[]>([]);
  // Map<key, valor>  donde valor = 1 (día completo) | 0.5 (medio día)
  const [marcados, setMarcados] = useState<Map<string, number>>(new Map());
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  const puedeEditar = ['admin', 'abogada', 'abogada1'].includes(rol);
  const diasEnMes = new Date(anio, mes + 1, 0).getDate();
  const dias = Array.from({ length: diasEnMes }, (_, i) => i + 1);
  const DIAS_SEMANA = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

  const mkKey = (capId: string, dia: number) =>
    `${capId}_${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

  const fetchData = useCallback(async () => {
    setLoaded(false);
    const [caps, asist] = await Promise.all([
      FS.loadCaptadoras().catch(() => [] as any[]),
      FS.loadAsistenciaMes(mes, anio).catch(() => [] as any[]),
    ]);
    setCaptadoras(
      caps
        .filter((u: any) => u.rol === 'captadora')
        .sort((a: any, b: any) => (a.nombre || '').localeCompare(b.nombre || ''))
    );
    const map = new Map<string, number>();
    asist.forEach((a: any) => map.set(a.id, a.valor ?? 1));
    setMarcados(map);
    setLoaded(true);
  }, [mes, anio]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMes = () => { if (mes === 0) { setMes(11); setAnio(a => a - 1); } else setMes(m => m - 1); };
  const nextMes = () => { if (mes === 11) { setMes(0); setAnio(a => a + 1); } else setMes(m => m + 1); };
  const isFuture = anio > today.getFullYear() || (anio === today.getFullYear() && mes > today.getMonth());

  // Ciclo: ausente (0) → día completo (1) → medio día (0.5) → ausente (0)
  const toggle = async (cap: any, dia: number) => {
    if (!puedeEditar) return;
    const fecha = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const k = mkKey(cap.id, dia);
    if (savingKeys.has(k)) return;

    const valorActual = marcados.get(k) ?? 0;
    const nuevoValor = valorActual === 0 ? 1 : valorActual === 1 ? 0.5 : 0;

    setSavingKeys(s => { const n = new Set(s); n.add(k); return n; });
    setMarcados(s => { const n = new Map(s); nuevoValor === 0 ? n.delete(k) : n.set(k, nuevoValor); return n; });

    try {
      if (nuevoValor === 0) {
        await FS.desmarcarAsistencia(cap.id, fecha);
      } else {
        await FS.marcarAsistencia(cap.id, cap.nombre || cap.email, fecha, userId, userNombre, nuevoValor);
      }
    } catch {
      setMarcados(s => { const n = new Map(s); valorActual === 0 ? n.delete(k) : n.set(k, valorActual); return n; });
    } finally {
      setSavingKeys(s => { const n = new Set(s); n.delete(k); return n; });
    }
  };

  const totalDias = (capId: string) => {
    let total = 0;
    dias.forEach(d => { total += marcados.get(mkKey(capId, d)) ?? 0; });
    return total;
  };

  if (!loaded) return <div className="p-loading">Cargando asistencia...</div>;

  return (
    <div>
      <div className="p-section-title" style={{ marginBottom: 20 }}>📅 Asistencia Captadoras</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 20px', border: '1px solid rgba(255,255,255,0.07)' }}>
        <button className="p-btn-secondary" onClick={prevMes} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--white)' }}>{MESES_NOMBRES[mes]} {anio}</div>
        </div>
        <button className="p-btn-secondary" onClick={nextMes} disabled={isFuture} style={{ padding: '6px 16px', fontSize: 18, lineHeight: 1, opacity: isFuture ? 0.4 : 1 }}>›</button>
      </div>

      {puedeEditar && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 11, color: 'var(--text-muted)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(76,175,80,0.25)', border: '1.5px solid rgba(76,175,80,0.6)', display: 'inline-block' }} />
            Día completo
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(245,158,11,0.25)', border: '1.5px solid rgba(245,158,11,0.6)', display: 'inline-block' }} />
            Medio día (0.5)
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>· Clic para cambiar estado</span>
        </div>
      )}

      {captadoras.length === 0 ? (
        <div className="p-empty">No hay captadoras registradas.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 150, position: 'sticky', left: 0, zIndex: 2, background: '#0f1929' }}>
                  Captadora
                </th>
                {dias.map(d => {
                  const diaSemana = new Date(anio, mes, d).getDay();
                  const esHoy = d === today.getDate() && mes === today.getMonth() && anio === today.getFullYear();
                  const esFinde = diaSemana === 0 || diaSemana === 6;
                  return (
                    <th key={d} style={{ textAlign: 'center', padding: '4px 1px', color: esHoy ? '#64B5F6' : esFinde ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)', fontWeight: esHoy ? 700 : 400, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 28 }}>
                      <div style={{ fontSize: 9, marginBottom: 1 }}>{DIAS_SEMANA[diaSemana]}</div>
                      <div style={{ fontSize: 11 }}>{d}</div>
                    </th>
                  );
                })}
                <th style={{ textAlign: 'center', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.1)', minWidth: 52 }}>
                  Días
                </th>
              </tr>
            </thead>
            <tbody>
              {captadoras.map((cap, ci) => {
                const total = totalDias(cap.id);
                const bgSticky = ci % 2 === 0 ? '#0c1829' : '#0f1929';
                return (
                  <tr key={cap.id} style={{ background: ci % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                    <td style={{ padding: '6px 12px', color: 'var(--white)', borderBottom: '1px solid rgba(255,255,255,0.04)', position: 'sticky', left: 0, zIndex: 1, background: bgSticky, fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13 }}>
                      {cap.nombre || cap.email}
                    </td>
                    {dias.map(d => {
                      const diaSemana = new Date(anio, mes, d).getDay();
                      const esFinde = diaSemana === 0 || diaSemana === 6;
                      const esHoy = d === today.getDate() && mes === today.getMonth() && anio === today.getFullYear();
                      const k = mkKey(cap.id, d);
                      const valor = marcados.get(k) ?? 0;
                      const esCompleto = valor === 1;
                      const esMedio = valor === 0.5;
                      const saving = savingKeys.has(k);
                      const title = puedeEditar
                        ? esCompleto ? '1 clic → medio día | 2 clics → quitar'
                        : esMedio ? '1 clic → quitar'
                        : '1 clic → día completo'
                        : undefined;
                      return (
                        <td key={d} style={{ textAlign: 'center', padding: '3px 1px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: esFinde ? 'rgba(255,255,255,0.015)' : undefined }}>
                          <button
                            onClick={() => toggle(cap, d)}
                            disabled={!puedeEditar || saving}
                            title={title}
                            style={{
                              width: 22, height: 22, borderRadius: 4, margin: 'auto',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: esCompleto
                                ? '1.5px solid rgba(76,175,80,0.6)'
                                : esMedio
                                  ? '1.5px solid rgba(245,158,11,0.6)'
                                  : esHoy ? '1.5px solid rgba(100,181,246,0.35)'
                                  : '1.5px solid rgba(255,255,255,0.08)',
                              background: esCompleto
                                ? 'rgba(76,175,80,0.2)'
                                : esMedio ? 'rgba(245,158,11,0.2)' : 'transparent',
                              cursor: puedeEditar ? 'pointer' : 'default',
                              fontSize: esMedio ? 9 : 11,
                              fontWeight: 700,
                              color: esCompleto ? '#4CAF50' : esMedio ? '#F59E0B' : 'transparent',
                              opacity: saving ? 0.5 : 1,
                              transition: 'all 0.12s',
                            }}
                          >
                            {saving ? '·' : esCompleto ? '✓' : esMedio ? '½' : ''}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'center', padding: '6px 10px', borderBottom: '1px solid rgba(255,255,255,0.04)', fontWeight: 700, color: total > 0 ? '#BFAF72' : 'var(--text-muted)', fontSize: 14 }}>
                      {total > 0 ? total : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   NOTIFICACIONES VIEW
   ══════════════════════════════════════════════════════════════════════════ */
const NotificacionesView: React.FC<{notifs:any[];uid:string;rol:string;onAbrirCaso:(casoId:string)=>void}> = ({notifs,uid,rol,onAbrirCaso}) => {
  const [expanded,setExpanded]=useState(true);
  const [working,setWorking]=useState<Set<string>>(new Set());
  const [confirmEliminar,setConfirmEliminar]=useState<string|null>(null);
  const puedeEliminar=['admin','abogada','abogada1','captadora','procuradora'].includes(rol);

  const MS_DAY=1000*60*60*24;
  const now=Date.now();

  const getDaysLeft=(n:any):number=>{
    const d=n.createdAt?.toDate?n.createdAt.toDate():new Date(n.createdAt||0);
    return Math.max(0,20-Math.floor((now-d.getTime())/MS_DAY));
  };

  const isPlazoUrgente=(plazo:string):boolean=>{
    const d=parsePlazoDate(plazo);
    if(!d) return false;
    const diff=d.getTime()-Date.now();
    return diff>=-MS_DAY&&diff<=3*MS_DAY; // dentro de 3 días (incluye hoy/vencido)
  };

  const isPlazoVencido=(plazo:string):boolean=>{
    const d=parsePlazoDate(plazo);
    return !!d&&d.getTime()<Date.now()-MS_DAY;
  };

  const sorted=[...notifs].sort((a,b)=>{
    if(!!a.realizado!==!!b.realizado) return a.realizado?1:-1;
    const aU=a.plazo&&isPlazoUrgente(a.plazo);
    const bU=b.plazo&&isPlazoUrgente(b.plazo);
    if(aU!==bU) return aU?-1:1;
    if(a.plazo&&b.plazo){
      const dA=parsePlazoDate(a.plazo),dB=parsePlazoDate(b.plazo);
      if(dA&&dB) return dA.getTime()-dB.getTime();
    }
    return getDaysLeft(a)-getDaysLeft(b);
  });

  const diasHastaPlazo=(plazo:string):number=>{
    const d=parsePlazoDate(plazo);
    if(!d) return 0;
    return Math.ceil((d.getTime()-Date.now())/MS_DAY);
  };

  const handleRealizado=async(n:any,done:boolean)=>{
    setWorking(w=>{const s=new Set(w);s.add(n.id);return s;});
    try{ await FS.marcarRealizada(n.id,n.casoId,n.mensaje,done); }
    finally{ setWorking(w=>{const s=new Set(w);s.delete(n.id);return s;}); }
  };

  const handleEliminar=async(notifId:string)=>{
    setWorking(w=>{const s=new Set(w);s.add(notifId);return s;});
    try{ await FS.eliminarNotificacion(notifId); }
    finally{ setWorking(w=>{const s=new Set(w);s.delete(notifId);return s;}); }
  };

  const alertas=notifs.filter(n=>{
    if(n.realizado) return false;
    if(getDaysLeft(n)<=3) return true;
    if(n.plazo&&isPlazoUrgente(n.plazo)) return true;
    return false;
  });
  const pendientes=notifs.filter(n=>!n.realizado).length;

  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <div className="p-section-title" style={{marginBottom:0}}>Notificaciones</div>
        <span style={{fontSize:12,color:'#9BB0CC'}}>{notifs.length} notificación{notifs.length!==1?'es':''} · últimos 20 días</span>
      </div>

      {alertas.length>0&&(
        <div style={{background:'rgba(229,57,53,0.08)',border:'1px solid rgba(229,57,53,0.3)',borderRadius:8,padding:'10px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:18}}>⚠️</span>
          <span style={{fontSize:13,color:'#EF9A9A',lineHeight:1.5}}>
            Tienes <strong style={{color:'#EF5350'}}>{alertas.length} tarea{alertas.length>1?'s':''}</strong> sin completar que vence{alertas.length>1?'n':''} en los próximos 3 días
          </span>
        </div>
      )}

      <div className="notif-view-header" onClick={()=>setExpanded(e=>!e)}>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:11}}>{expanded?'▲':'▼'}</span>
          Todas las notificaciones ({notifs.length})
        </span>
        {pendientes>0&&<span className="notif-badge" style={{position:'static',fontSize:'0.65rem',padding:'2px 7px'}}>{pendientes} pendiente{pendientes!==1?'s':''}</span>}
      </div>

      {expanded&&(
        notifs.length===0?(
          <div className="p-empty" style={{padding:'32px 0',textAlign:'center'}}>Sin notificaciones en los últimos 20 días.<br/><span style={{fontSize:11,opacity:0.6}}>Las menciones con @ en el checklist de Proceso aparecerán aquí.</span></div>
        ):(
          <div style={{overflowX:'auto',marginTop:2}}>
            <table className="p-table" style={{minWidth:560}}>
              <thead>
                <tr>
                  <th>De</th>
                  <th>Caso</th>
                  <th>Tarea asignada</th>
                  <th>Plazo</th>
                  <th style={{textAlign:'center'}}>Días restantes</th>
                  <th style={{textAlign:'center'}}>Realizado</th>
                  {puedeEliminar&&<th></th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(n=>{
                  const esMio=n.de===uid;
                  const plazoUrgente=n.plazo&&isPlazoUrgente(n.plazo)&&!n.realizado;
                  const plazoVencido=n.plazo&&isPlazoVencido(n.plazo)&&!n.realizado;
                  const dias=n.plazo?diasHastaPlazo(n.plazo):null;
                  return(
                    <tr key={n.id} style={{opacity:n.realizado?0.45:1,transition:'opacity 0.3s'}}>
                      <td style={{whiteSpace:'nowrap'}}>
                        <span style={{fontWeight:600,color:esMio?'#BFAF72':'#D6E4F4',fontSize:13}}>
                          {esMio?'👤 Yo':n.deNombre}
                        </span>
                      </td>
                      <td>
                        <button onClick={()=>onAbrirCaso(n.casoId)} style={{background:'none',border:'none',cursor:'pointer',color:'#BFAF72',fontSize:12,padding:0,textDecoration:'underline',textAlign:'left'}}>
                          {n.casoNombre}
                        </button>
                        <div style={{fontSize:10,color:'#9BB0CC',marginTop:1}}>{n.codigoCaso}</div>
                      </td>
                      <td style={{maxWidth:240,wordBreak:'break-word',fontSize:12,lineHeight:1.5}}>
                        {renderMenciones(n.mensaje)}
                      </td>
                      <td style={{whiteSpace:'nowrap',minWidth:110}}>
                        {n.plazo?(
                          <span style={{color:plazoVencido?'#EF5350':plazoUrgente?'#FF7043':'#BFAF72',fontWeight:plazoUrgente||plazoVencido?700:400,fontSize:12}}>
                            {plazoVencido?'🔴 ':plazoUrgente?'⚠️ ':''}{n.plazo}
                          </span>
                        ):<span style={{color:'#6a82a0',fontSize:11}}>—</span>}
                      </td>
                      <td style={{textAlign:'center',whiteSpace:'nowrap'}}>
                        {dias!==null?(
                          dias<0
                            ?<span style={{color:'#EF5350',fontWeight:700,fontSize:12}}>Vencido</span>
                            :dias===0
                              ?<span style={{color:'#EF5350',fontWeight:700,fontSize:12}}>Hoy</span>
                              :<span style={{color:dias<=3?'#EF5350':dias<=7?'#FFA726':'#66BB6A',fontWeight:dias<=3?700:400,fontSize:12}}>{dias} día{dias!==1?'s':''}</span>
                        ):<span style={{color:'#6a82a0',fontSize:11}}>—</span>}
                      </td>
                      <td style={{textAlign:'center'}}>
                        <input
                          type="checkbox"
                          checked={!!n.realizado}
                          disabled={working.has(n.id)}
                          onChange={e=>handleRealizado(n,e.target.checked)}
                          style={{accentColor:'var(--gold)',width:15,height:15,cursor:'pointer'}}
                          title={n.realizado?'Marcar como pendiente':'Marcar como realizado'}
                        />
                      </td>
                      {puedeEliminar&&(
                        <td style={{textAlign:'center'}}>
                          <button
                            onClick={()=>setConfirmEliminar(n.id)}
                            disabled={working.has(n.id)}
                            style={{background:'none',border:'none',cursor:'pointer',color:'rgba(239,83,80,0.5)',fontSize:13,padding:'0 4px',lineHeight:1,transition:'color 0.2s'}}
                            onMouseEnter={e=>(e.currentTarget.style.color='#EF5350')}
                            onMouseLeave={e=>(e.currentTarget.style.color='rgba(239,83,80,0.5)')}
                            title="Eliminar notificación"
                          >✕</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {confirmEliminar&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setConfirmEliminar(null)}>
          <div style={{background:'#141d2e',border:'1px solid rgba(229,115,115,0.35)',borderRadius:10,padding:'24px 28px',maxWidth:380,width:'90%',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:28,marginBottom:8}}>🗑️</div>
            <div style={{fontSize:14,color:'#e8e0d0',marginBottom:6,fontWeight:500}}>¿Eliminar notificación?</div>
            <div style={{fontSize:13,color:'#8a9bb5',marginBottom:20,lineHeight:1.5}}>Esta acción no se puede deshacer.</div>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>setConfirmEliminar(null)} style={{padding:'8px 20px',fontSize:13,fontWeight:600,color:'#fff',background:'#2563EB',border:'none',borderRadius:7,cursor:'pointer'}}>Cancelar</button>
              <button onClick={()=>{handleEliminar(confirmEliminar);setConfirmEliminar(null);}} style={{padding:'8px 18px',fontSize:13,fontWeight:500,color:'#f28b8b',background:'none',border:'1px solid rgba(229,115,115,0.4)',borderRadius:7,cursor:'pointer'}}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelApp;
